var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Component, NotifyPropertyChanges, ChildProperty, L10n, Collection, Complex, isBlazor, Browser } from '@syncfusion/ej2-base';
import { isNullOrUndefined, Property, Event, SanitizeHtmlHelper } from '@syncfusion/ej2-base';
import { PdfViewerBase, PdfiumRunner } from './index';
import { PdfRenderer } from './index';
import { FontStyle, AnnotationResizerLocation, CursorType, ContextMenuItem, DynamicStampItem, SignStampItem, StandardBusinessStampItem, AnnotationDataFormat, DisplayMode, FormFieldDataFormat, ExtractTextOption } from './base/types';
import { FormFields } from './index';
import { PdfAnnotationBase, PdfFormFieldBase } from './drawing/pdf-annotation';
import { Drawing } from './drawing/drawing';
import { Selector } from './drawing/selector';
import { processPathData, splitArrayCollection } from '@syncfusion/ej2-drawings';
import { renderAdornerLayer } from './drawing/dom-util';
import { PdfViewerUtils, PdfiumTaskScheduler, TaskPriorityLevel } from './base/pdfviewer-utlis';
/**
 * The `ToolbarSettings` module is used to provide the toolbar settings of PDF viewer.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the tool bar settings.
 *  viewer.toolbarSettings = {
 *      showTooltip: false,
 *      toolbarItems: [
 *          "OpenOption",
 *          "UndoRedoTool",
 *          "PageNavigationTool",
 *          "MagnificationTool",
 *          "PanTool",
 *          "SelectionTool",
 *          "CommentTool",
 *          "SubmitForm",
 *          "AnnotationEditTool",
 *          "FormDesignerEditTool",
 *          "FreeTextAnnotationOption",
 *          "InkAnnotationOption",
 *          "ShapeAnnotationOption",
 *          "StampAnnotation",
 *          "SignatureOption",
 *          "SearchOption",
 *          "PrintOption",
 *          "DownloadOption"
 *      ],
 *      annotationToolbarItems: [
 *          "HighlightTool",
 *          "UnderlineTool",
 *          "StrikethroughTool",
 *          "SquigglyTool",
 *          "ColorEditTool",
 *          "OpacityEditTool",
 *          "AnnotationDeleteTool",
 *          "StampAnnotationTool",
 *          "HandWrittenSignatureTool",
 *          "InkAnnotationTool",
 *          "ShapeTool",
 *          "CalibrateTool",
 *          "StrokeColorEditTool",
 *          "ThicknessEditTool",
 *          "FreeTextAnnotationTool",
 *          "FontFamilyAnnotationTool",
 *          "FontSizeAnnotationTool",
 *          "FontStylesAnnotationTool",
 *          "FontAlignAnnotationTool",
 *          "FontColorAnnotationTool",
 *          "CommentPanelTool"
 *      ],
 *      formDesignerToolbarItems: [
 *          "TextboxTool",
 *          "PasswordTool",
 *          "CheckBoxTool",
 *          "RadioButtonTool",
 *          "DropdownTool",
 *          "ListboxTool",
 *          "DrawSignatureTool",
 *          "DeleteTool"
 *      ]
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class ToolbarSettings extends ChildProperty {
}
__decorate([
    Property(true)
], ToolbarSettings.prototype, "showTooltip", void 0);
__decorate([
    Property()
], ToolbarSettings.prototype, "toolbarItems", void 0);
__decorate([
    Property()
], ToolbarSettings.prototype, "annotationToolbarItems", void 0);
__decorate([
    Property()
], ToolbarSettings.prototype, "formDesignerToolbarItems", void 0);
/**
 * Defines customized toolbar items.
 */
export class CustomToolbarItem extends ChildProperty {
}
__decorate([
    Property('')
], CustomToolbarItem.prototype, "prefixIcon", void 0);
__decorate([
    Property('')
], CustomToolbarItem.prototype, "tooltipText", void 0);
__decorate([
    Property('')
], CustomToolbarItem.prototype, "id", void 0);
__decorate([
    Property('')
], CustomToolbarItem.prototype, "text", void 0);
__decorate([
    Property('')
], CustomToolbarItem.prototype, "cssClass", void 0);
__decorate([
    Property('left')
], CustomToolbarItem.prototype, "align", void 0);
__decorate([
    Property('')
], CustomToolbarItem.prototype, "template", void 0);
__decorate([
    Property('Button')
], CustomToolbarItem.prototype, "type", void 0);
/**
 * The `AjaxRequestSettings` module is used to set the ajax Request Headers of PDF viewer.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // HTTP header "X-Custom-Header": "Custom header value" // Custom HTTP header
 *  viewer.ajaxRequestSettings = {
 *      ajaxHeaders: [
 *          {
 *              headerName : "Authorization",
 *              headerValue : "Bearer"
 *          }
 *      ],
 *      withCredentials: false
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class AjaxRequestSettings extends ChildProperty {
}
__decorate([
    Property()
], AjaxRequestSettings.prototype, "ajaxHeaders", void 0);
__decorate([
    Property(false)
], AjaxRequestSettings.prototype, "withCredentials", void 0);
/**
 * The `CustomStamp` module is used to provide the custom stamp added in stamp menu of the PDF Viewer toolbar.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Add your custom stamp image source as base64 image.
 *  viewer.customStamp = [
 *       {
 *          customStampName: 'Sample',
 *          customStampImageSource: "data:image/png;base64, Syncfusion pdf viewer"
 *      }
 *  ];
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class CustomStamp extends ChildProperty {
}
__decorate([
    Property('')
], CustomStamp.prototype, "customStampName", void 0);
__decorate([
    Property('')
], CustomStamp.prototype, "customStampImageSource", void 0);
/**
 * The `AnnotationToolbarSettings` module is used to provide the annotation toolbar settings of the PDF viewer.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the annotation tool bar settings.
 *  viewer.toolbarSettings = {
 *      showTooltip: false,
 *      annotationToolbarItems: [
 *          "HighlightTool",
 *          "UnderlineTool",
 *          "StrikethroughTool",
 *          "SquigglyTool",
 *          "ColorEditTool",
 *          "OpacityEditTool",
 *          "AnnotationDeleteTool",
 *          "StampAnnotationTool",
 *          "HandWrittenSignatureTool",
 *          "InkAnnotationTool",
 *          "ShapeTool",
 *          "CalibrateTool",
 *          "StrokeColorEditTool",
 *          "ThicknessEditTool",
 *          "FreeTextAnnotationTool",
 *          "FontFamilyAnnotationTool",
 *          "FontSizeAnnotationTool",
 *          "FontStylesAnnotationTool",
 *          "FontAlignAnnotationTool",
 *          "FontColorAnnotationTool",
 *          "CommentPanelTool"
 *      ],
 *  };
 * viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class AnnotationToolbarSettings extends ChildProperty {
}
__decorate([
    Property(true)
], AnnotationToolbarSettings.prototype, "showTooltip", void 0);
__decorate([
    Property()
], AnnotationToolbarSettings.prototype, "annotationToolbarItem", void 0);
/**
 * The `FormDesignerToolbarSettings` module is used to provide the Form designer toolbar settings of the PDF viewer.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the form field tool bar settings.
 *  viewer.toolbarSettings = {
 *      showTooltip: false,
 *      formDesignerToolbarItems: [
 *          "TextboxTool",
 *          "PasswordTool",
 *          "CheckBoxTool",
 *          "RadioButtonTool",
 *          "DropdownTool",
 *          "ListboxTool",
 *          "DrawSignatureTool",
 *          "DeleteTool"
 *      ]
 *  };
 * viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class FormDesignerToolbarSettings extends ChildProperty {
}
__decorate([
    Property(true)
], FormDesignerToolbarSettings.prototype, "showTooltip", void 0);
__decorate([
    Property()
], FormDesignerToolbarSettings.prototype, "formDesignerToolbarItem", void 0);
/**
 * The `SignatureFieldSettings` module is used to set the properties of signature field in PDF Viewer
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the signature field settings.
 *  viewer.signatureFieldSettings = {
 *      bounds: {x:0, y:0, width:0, height:0},
 *      name: "",
 *      isReadOnly: true,
 *      visibility: "visible",
 *      isRequired: true,
 *      isPrint: false,
 *      tooltip: "",
 *      thickness: 1,
 *      pageNumber: 0,
 *      signatureIndicatorSettings: {
 *          opacity: 1,
 *          backgroundColor: "orange",
 *          width: 19,
 *          height: 10,
 *          fontSize: 10,
 *          text: null,
 *          color: "black"
 *      },
 *      signatureDialogSettings: {
 *          displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload,
 *          hideSaveSignature: false
 *      },
 *      customData: null,
 *      typeSignatureFonts: ['arial']
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class SignatureFieldSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0, width: 0, height: 0 })
], SignatureFieldSettings.prototype, "bounds", void 0);
__decorate([
    Property('')
], SignatureFieldSettings.prototype, "name", void 0);
__decorate([
    Property(false)
], SignatureFieldSettings.prototype, "isReadOnly", void 0);
__decorate([
    Property('visible')
], SignatureFieldSettings.prototype, "visibility", void 0);
__decorate([
    Property(false)
], SignatureFieldSettings.prototype, "isRequired", void 0);
__decorate([
    Property(false)
], SignatureFieldSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], SignatureFieldSettings.prototype, "tooltip", void 0);
__decorate([
    Property(1)
], SignatureFieldSettings.prototype, "thickness", void 0);
__decorate([
    Property(0)
], SignatureFieldSettings.prototype, "pageNumber", void 0);
__decorate([
    Property()
], SignatureFieldSettings.prototype, "signatureDialogSettings", void 0);
__decorate([
    Property()
], SignatureFieldSettings.prototype, "signatureIndicatorSettings", void 0);
__decorate([
    Property(null)
], SignatureFieldSettings.prototype, "customData", void 0);
__decorate([
    Property()
], SignatureFieldSettings.prototype, "typeSignatureFonts", void 0);
/**
 * The `InitialFieldSettings` module is used to set the properties of initial field in PDF Viewer
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Changes the initial field settings.
 *  viewer.initialFieldSettings = {
 *      bounds: {x:0, y:0, width:0, height:0},
 *      name: "",
 *      isReadOnly: true,
 *      visibility: "visible",
 *      isRequired: true,
 *      isPrint: true,
 *      tooltip: "",
 *      thickness: 1,
 *      pageNumber: 0,
 *      isInitialField: false,
 *      initialIndicatorSettings: {
 *          opacity: 1,
 *          backgroundColor: "orange",
 *          width: 19,
 *          height: 10,
 *          fontSize: 10,
 *          text: null,
 *          color: "black"
 *      },
 *      initialDialogSettings: {
 *         displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload,
 *          hideSaveSignature: false
 *      },
 *      customData: null,
 *      typeInitialFonts: ['arial']
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class InitialFieldSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0, width: 0, height: 0 })
], InitialFieldSettings.prototype, "bounds", void 0);
__decorate([
    Property('')
], InitialFieldSettings.prototype, "name", void 0);
__decorate([
    Property(false)
], InitialFieldSettings.prototype, "isReadOnly", void 0);
__decorate([
    Property('visible')
], InitialFieldSettings.prototype, "visibility", void 0);
__decorate([
    Property(false)
], InitialFieldSettings.prototype, "isRequired", void 0);
__decorate([
    Property(false)
], InitialFieldSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], InitialFieldSettings.prototype, "tooltip", void 0);
__decorate([
    Property(1)
], InitialFieldSettings.prototype, "thickness", void 0);
__decorate([
    Property(0)
], InitialFieldSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(false)
], InitialFieldSettings.prototype, "isInitialField", void 0);
__decorate([
    Property()
], InitialFieldSettings.prototype, "initialDialogSettings", void 0);
__decorate([
    Property()
], InitialFieldSettings.prototype, "initialIndicatorSettings", void 0);
__decorate([
    Property(null)
], InitialFieldSettings.prototype, "customData", void 0);
__decorate([
    Property()
], InitialFieldSettings.prototype, "typeInitialFonts", void 0);
/**
 * The `SignatureIndicatorSettings` module is used to provide the properties of signature Indicator in the signature field.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the signature indicator settings.
 *  viewer.signatureFieldSettings = {
 *      signatureIndicatorSettings: {
 *          opacity: 1,
 *          backgroundColor: 'orange',
 *          width: 19,
 *          height: 10,
 *          fontSize: 10,
 *          text: null,
 *          color: 'black'
 *      }
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class SignatureIndicatorSettings extends ChildProperty {
}
__decorate([
    Property(1)
], SignatureIndicatorSettings.prototype, "opacity", void 0);
__decorate([
    Property('orange')
], SignatureIndicatorSettings.prototype, "backgroundColor", void 0);
__decorate([
    Property(19)
], SignatureIndicatorSettings.prototype, "width", void 0);
__decorate([
    Property(10)
], SignatureIndicatorSettings.prototype, "height", void 0);
__decorate([
    Property(10)
], SignatureIndicatorSettings.prototype, "fontSize", void 0);
__decorate([
    Property(null)
], SignatureIndicatorSettings.prototype, "text", void 0);
__decorate([
    Property('black')
], SignatureIndicatorSettings.prototype, "color", void 0);
/**
 * The `SignatureDialogSettings` module is used to customize the signature dialog box.
 *
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the signature dialog settings.
 *  viewer.signatureDialogSettings = {
 *      displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload,
 *      hideSaveSignature: true
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class SignatureDialogSettings extends ChildProperty {
}
__decorate([
    Property(DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload)
], SignatureDialogSettings.prototype, "displayMode", void 0);
__decorate([
    Property(false)
], SignatureDialogSettings.prototype, "hideSaveSignature", void 0);
/**
 * The `ServerActionSettings` module is used to provide the server action methods of PDF viewer.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the server action settings.
 *  viewer.serverActionSettings = {
 *      load: "Load",
 *      renderPages: "RenderPdfPages",
 *      unload: "Unload",
 *      download: "Download",
 *      renderThumbnail: "RenderThumbnailImages",
 *      print: "PrintImages",
 *      renderComments: "RenderAnnotationComments",
 *      importAnnotations: "ImportAnnotations",
 *      exportAnnotations: "ExportAnnotations",
 *      importFormFields: "ImportFormFields",
 *      exportFormFields: "ExportFormFields",
 *      renderTexts: "RenderPdfTexts",
 *      validatePassword: "ValidatePassword"
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class ServerActionSettings extends ChildProperty {
}
__decorate([
    Property('Load')
], ServerActionSettings.prototype, "load", void 0);
__decorate([
    Property('Unload')
], ServerActionSettings.prototype, "unload", void 0);
__decorate([
    Property('RenderPdfPages')
], ServerActionSettings.prototype, "renderPages", void 0);
__decorate([
    Property('Print')
], ServerActionSettings.prototype, "print", void 0);
__decorate([
    Property('Download')
], ServerActionSettings.prototype, "download", void 0);
__decorate([
    Property('RenderThumbnailImages')
], ServerActionSettings.prototype, "renderThumbnail", void 0);
__decorate([
    Property('RenderAnnotationComments')
], ServerActionSettings.prototype, "renderComments", void 0);
__decorate([
    Property('ImportAnnotations')
], ServerActionSettings.prototype, "importAnnotations", void 0);
__decorate([
    Property('ExportAnnotations')
], ServerActionSettings.prototype, "exportAnnotations", void 0);
__decorate([
    Property('ImportFormFields')
], ServerActionSettings.prototype, "importFormFields", void 0);
__decorate([
    Property('ExportFormFields')
], ServerActionSettings.prototype, "exportFormFields", void 0);
__decorate([
    Property('RenderPdfTexts')
], ServerActionSettings.prototype, "renderTexts", void 0);
__decorate([
    Property('ValidatePassword')
], ServerActionSettings.prototype, "validatePassword", void 0);
/**
 * The `StrikethroughSettings` module is used to provide the properties to Strikethrough annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the strike through annotation settings.
 *  viewer.strikethroughSettings = {
 *      pageNumber: 1,
 *      bounds: [],
 *      opacity: 1,
 *      color: '#ff0000',
 *      author: 'Guest',
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Square',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges
 *      },
 *      customData: null,
 *      isLock: false,
 *      enableMultiPageAnnotation: false,
 *      enableTextMarkupResizer: false,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class StrikethroughSettings extends ChildProperty {
}
__decorate([
    Property(1)
], StrikethroughSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], StrikethroughSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ff0000')
], StrikethroughSettings.prototype, "color", void 0);
__decorate([
    Property('Guest')
], StrikethroughSettings.prototype, "author", void 0);
__decorate([
    Property('')
], StrikethroughSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(null)
], StrikethroughSettings.prototype, "customData", void 0);
__decorate([
    Property(false)
], StrikethroughSettings.prototype, "isLock", void 0);
__decorate([
    Property(false)
], StrikethroughSettings.prototype, "enableMultiPageAnnotation", void 0);
__decorate([
    Property(false)
], StrikethroughSettings.prototype, "enableTextMarkupResizer", void 0);
__decorate([
    Property(['None'])
], StrikethroughSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], StrikethroughSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], StrikethroughSettings.prototype, "subject", void 0);
/**
 * The `SquigglySettings` module is used to provide the properties to Squiggly annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the squiggly annotation settings.
 *  viewer.squigglySettings = {
 *      pageNumber: 1,
 *      bounds: [],
 *      opacity: 1,
 *      color: '#ff0000',
 *      author: 'Guest',
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Square',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges
 *      },
 *      customData: null,
 *      isLock: false,
 *      enableMultiPageAnnotation: false,
 *      enableTextMarkupResizer: false,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class SquigglySettings extends ChildProperty {
}
__decorate([
    Property(1)
], SquigglySettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], SquigglySettings.prototype, "opacity", void 0);
__decorate([
    Property('#ff0000')
], SquigglySettings.prototype, "color", void 0);
__decorate([
    Property('Guest')
], SquigglySettings.prototype, "author", void 0);
__decorate([
    Property('')
], SquigglySettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(null)
], SquigglySettings.prototype, "customData", void 0);
__decorate([
    Property(false)
], SquigglySettings.prototype, "isLock", void 0);
__decorate([
    Property(false)
], SquigglySettings.prototype, "enableMultiPageAnnotation", void 0);
__decorate([
    Property(false)
], SquigglySettings.prototype, "enableTextMarkupResizer", void 0);
__decorate([
    Property(['None'])
], SquigglySettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], SquigglySettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], SquigglySettings.prototype, "subject", void 0);
/**
 * The `UnderlineSettings` module is used to provide the properties to Underline annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the underline annotation settings.
 *  viewer.underlineSettings = {
 *      pageNumber: 1,
 *      bounds: [],
 *      opacity: 1,
 *      color: '#9c2592',
 *      author: 'Guest',
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Square',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges
 *      },
 *      customData: null,
 *      isLock: false,
 *      enableMultiPageAnnotation: false,
 *      enableTextMarkupResizer: false,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class UnderlineSettings extends ChildProperty {
}
__decorate([
    Property(1)
], UnderlineSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], UnderlineSettings.prototype, "opacity", void 0);
__decorate([
    Property('#00ff00')
], UnderlineSettings.prototype, "color", void 0);
__decorate([
    Property('Guest')
], UnderlineSettings.prototype, "author", void 0);
__decorate([
    Property('')
], UnderlineSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(null)
], UnderlineSettings.prototype, "customData", void 0);
__decorate([
    Property(false)
], UnderlineSettings.prototype, "isLock", void 0);
__decorate([
    Property(false)
], UnderlineSettings.prototype, "enableMultiPageAnnotation", void 0);
__decorate([
    Property(false)
], UnderlineSettings.prototype, "enableTextMarkupResizer", void 0);
__decorate([
    Property(['None'])
], UnderlineSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], UnderlineSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], UnderlineSettings.prototype, "subject", void 0);
/**
 * The `HighlightSettings` module is used to provide the properties to Highlight annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the highlight annotation settings.
 *  viewer.highlightSettings = {
 *      pageNumber: 1,
 *      bounds: [],
 *      opacity: 1,
 *      color: '#ff0000',
 *      author: 'Guest',
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Square',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges
 *      },
 *      customData: null,
 *      isLock: false,
 *      enableMultiPageAnnotation: false,
 *      enableTextMarkupResizer: false,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class HighlightSettings extends ChildProperty {
}
__decorate([
    Property(1)
], HighlightSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], HighlightSettings.prototype, "opacity", void 0);
__decorate([
    Property('#FFDF56')
], HighlightSettings.prototype, "color", void 0);
__decorate([
    Property('Guest')
], HighlightSettings.prototype, "author", void 0);
__decorate([
    Property('')
], HighlightSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(null)
], HighlightSettings.prototype, "customData", void 0);
__decorate([
    Property(false)
], HighlightSettings.prototype, "isLock", void 0);
__decorate([
    Property(false)
], HighlightSettings.prototype, "enableMultiPageAnnotation", void 0);
__decorate([
    Property(false)
], HighlightSettings.prototype, "enableTextMarkupResizer", void 0);
__decorate([
    Property(['None'])
], HighlightSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], HighlightSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], HighlightSettings.prototype, "subject", void 0);
/**
 * The `LineSettings` module is used to provide the properties to line annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the line annotation settings.
 *  viewer.lineSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      vertexPoints: [],
 *      opacity: 1,
 *      fillColor: 'ffffff00',
 *      strokeColor: '#ff0000',
 *      author: 'Guest',
 *      thickness: 1,
 *      lineHeadStartStyle: 'None',
 *      lineHeadEndStyle: 'None',
 *      borderDashArray: 0,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Square',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges
 *      },
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxHeight: 0,
 *      maxWidth: 0,
 *      isLock: false,
 *      customData: null,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class LineSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], LineSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], LineSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], LineSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], LineSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#ff0000')
], LineSettings.prototype, "strokeColor", void 0);
__decorate([
    Property('Guest')
], LineSettings.prototype, "author", void 0);
__decorate([
    Property('1')
], LineSettings.prototype, "thickness", void 0);
__decorate([
    Property('None')
], LineSettings.prototype, "lineHeadStartStyle", void 0);
__decorate([
    Property('None')
], LineSettings.prototype, "lineHeadEndStyle", void 0);
__decorate([
    Property(0)
], LineSettings.prototype, "borderDashArray", void 0);
__decorate([
    Property('')
], LineSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(0)
], LineSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], LineSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], LineSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], LineSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], LineSettings.prototype, "isLock", void 0);
__decorate([
    Property(null)
], LineSettings.prototype, "customData", void 0);
__decorate([
    Property(['None'])
], LineSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], LineSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], LineSettings.prototype, "subject", void 0);
/**
 * The `ArrowSettings` module is used to provide the properties to arrow annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the arrow annotation settings.
 *  viewer.arrowSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      vertexPoints: [],
 *      opacity: 1,
 *      fillColor: '#9c2592',
 *      strokeColor: '#ff0000',
 *      author: 'Guest',
 *      thickness: 1,
 *      borderDashArray: 0,
 *      lineHeadStartStyle: 'Closed',
 *      lineHeadEndStyle: 'Closed',
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Square',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      customData: null,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class ArrowSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], ArrowSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], ArrowSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], ArrowSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], ArrowSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#ff0000')
], ArrowSettings.prototype, "strokeColor", void 0);
__decorate([
    Property('Guest')
], ArrowSettings.prototype, "author", void 0);
__decorate([
    Property('1')
], ArrowSettings.prototype, "thickness", void 0);
__decorate([
    Property('None')
], ArrowSettings.prototype, "lineHeadStartStyle", void 0);
__decorate([
    Property('None')
], ArrowSettings.prototype, "lineHeadEndStyle", void 0);
__decorate([
    Property(0)
], ArrowSettings.prototype, "borderDashArray", void 0);
__decorate([
    Property('')
], ArrowSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(0)
], ArrowSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], ArrowSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], ArrowSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], ArrowSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], ArrowSettings.prototype, "isLock", void 0);
__decorate([
    Property(null)
], ArrowSettings.prototype, "customData", void 0);
__decorate([
    Property(['None'])
], ArrowSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], ArrowSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], ArrowSettings.prototype, "subject", void 0);
/**
 * The `RectangleSettings` module is used to provide the properties to rectangle annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the rectangle annotation settings.
 *  viewer.rectangleSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      width: 100,
 *      height: 50,
 *      opacity: 1,
 *      fillColor: '#9c2592',
 *      strokeColor: '#ff0000',
 *      author: 'Guest',
 *      thickness: 1,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Square',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      customData: null,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class RectangleSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], RectangleSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], RectangleSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(100)
], RectangleSettings.prototype, "width", void 0);
__decorate([
    Property(50)
], RectangleSettings.prototype, "height", void 0);
__decorate([
    Property(1)
], RectangleSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], RectangleSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#ff0000')
], RectangleSettings.prototype, "strokeColor", void 0);
__decorate([
    Property('Guest')
], RectangleSettings.prototype, "author", void 0);
__decorate([
    Property('1')
], RectangleSettings.prototype, "thickness", void 0);
__decorate([
    Property('')
], RectangleSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(0)
], RectangleSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], RectangleSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], RectangleSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], RectangleSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], RectangleSettings.prototype, "isLock", void 0);
__decorate([
    Property(null)
], RectangleSettings.prototype, "customData", void 0);
__decorate([
    Property(['None'])
], RectangleSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], RectangleSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], RectangleSettings.prototype, "subject", void 0);
/**
 * The `CircleSettings` module is used to provide the properties to circle annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the circle annotation settings.
 *  viewer.circleSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      width: 100,
 *      height: 100,
 *      opacity: 1,
 *      fillColor: '#9c2592',
 *      strokeColor: '#ff0000',
 *      author: 'Guest',
 *      thickness: 1,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Square',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      customData: null,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class CircleSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], CircleSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], CircleSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(100)
], CircleSettings.prototype, "width", void 0);
__decorate([
    Property(100)
], CircleSettings.prototype, "height", void 0);
__decorate([
    Property(1)
], CircleSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], CircleSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#ff0000')
], CircleSettings.prototype, "strokeColor", void 0);
__decorate([
    Property('Guest')
], CircleSettings.prototype, "author", void 0);
__decorate([
    Property('1')
], CircleSettings.prototype, "thickness", void 0);
__decorate([
    Property('')
], CircleSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(0)
], CircleSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], CircleSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], CircleSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], CircleSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], CircleSettings.prototype, "isLock", void 0);
__decorate([
    Property(null)
], CircleSettings.prototype, "customData", void 0);
__decorate([
    Property(['None'])
], CircleSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], CircleSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], CircleSettings.prototype, "subject", void 0);
/**
 * The `ShapeLabelSettings` module is used to provide the properties to rectangle annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the shape label settings.
 *  viewer.shapeLabelSettings = {
 *      opacity: 1,
 *      fillColor: '#9c2592',
 *      fontColor: '#000',
 *      fontSize: 16,
 *      fontFamily: 'Helvetica'
 *      labelContent: 'Label',
 *      notes: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class ShapeLabelSettings extends ChildProperty {
}
__decorate([
    Property(1)
], ShapeLabelSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], ShapeLabelSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#000')
], ShapeLabelSettings.prototype, "fontColor", void 0);
__decorate([
    Property(16)
], ShapeLabelSettings.prototype, "fontSize", void 0);
__decorate([
    Property('Helvetica')
], ShapeLabelSettings.prototype, "fontFamily", void 0);
__decorate([
    Property('Label')
], ShapeLabelSettings.prototype, "labelContent", void 0);
__decorate([
    Property('')
], ShapeLabelSettings.prototype, "notes", void 0);
/**
 * The `PolygonSettings` module is used to provide the properties to polygon annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the polygon annotation settings.
 *  viewer.polygonSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      vertexPoints: [],
 *      opacity: 1,
 *      fillColor: '#4070FF',
 *      strokeColor: '#ff0000',
 *      author: 'Guest',
 *      thickness: 1,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Square',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      customData: null,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class PolygonSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], PolygonSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], PolygonSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], PolygonSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], PolygonSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#ff0000')
], PolygonSettings.prototype, "strokeColor", void 0);
__decorate([
    Property('Guest')
], PolygonSettings.prototype, "author", void 0);
__decorate([
    Property('1')
], PolygonSettings.prototype, "thickness", void 0);
__decorate([
    Property('')
], PolygonSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(0)
], PolygonSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], PolygonSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], PolygonSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], PolygonSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], PolygonSettings.prototype, "isLock", void 0);
__decorate([
    Property(null)
], PolygonSettings.prototype, "customData", void 0);
__decorate([
    Property(['None'])
], PolygonSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], PolygonSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], PolygonSettings.prototype, "subject", void 0);
/**
 * The `stampSettings` module is used to provide the properties to stamp annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the stamp annotation settings.
 *  viewer.stampSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      width: 150,
 *      height: 50,
 *      opacity: 1,
 *      author: 'Guest',
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'red',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 5,
 *          resizerShape: 'Circle',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      customData: null,
 *      dynamicStamps: [
 *          DynamicStampItem.Revised,
 *          DynamicStampItem.Reviewed,
 *          DynamicStampItem.Received,
 *          DynamicStampItem.Confidential,
 *          DynamicStampItem.Approved,
 *          DynamicStampItem.NotApproved
 *      ],
 *      signStamps: [
 *          SignStampItem.Witness,
 *          SignStampItem.InitialHere,
 *          SignStampItem.SignHere,
 *          SignStampItem.Accepted,
 *          SignStampItem.Rejected
 *      ],
 *      standardBusinessStamps: [
 *          StandardBusinessStampItem.Approved,
 *          StandardBusinessStampItem.NotApproved,
 *          StandardBusinessStampItem.Draft,
 *          StandardBusinessStampItem.Final,
 *          StandardBusinessStampItem.Completed,
 *          StandardBusinessStampItem.Confidential,
 *          StandardBusinessStampItem.ForPublicRelease,
 *          StandardBusinessStampItem.NotForPublicRelease,
 *          StandardBusinessStampItem.ForComment,
 *          StandardBusinessStampItem.Void,
 *          StandardBusinessStampItem.PreliminaryResults,
 *          StandardBusinessStampItem.InformationOnly
 *      ],
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class StampSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], StampSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], StampSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(150)
], StampSettings.prototype, "width", void 0);
__decorate([
    Property(50)
], StampSettings.prototype, "height", void 0);
__decorate([
    Property(1)
], StampSettings.prototype, "opacity", void 0);
__decorate([
    Property('Guest')
], StampSettings.prototype, "author", void 0);
__decorate([
    Property('')
], StampSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(0)
], StampSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], StampSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], StampSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], StampSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], StampSettings.prototype, "isLock", void 0);
__decorate([
    Property(null)
], StampSettings.prototype, "customData", void 0);
__decorate([
    Property([])
], StampSettings.prototype, "dynamicStamps", void 0);
__decorate([
    Property([])
], StampSettings.prototype, "signStamps", void 0);
__decorate([
    Property([])
], StampSettings.prototype, "standardBusinessStamps", void 0);
__decorate([
    Property(['None'])
], StampSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], StampSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], StampSettings.prototype, "subject", void 0);
/**
 * The `CustomStampSettings` module is used to provide the properties to customstamp annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the custom stamp annotation settings.
 *  viewer.customStampSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      opacity: 1,
 *      author: 'Guest',
 *      width: 0,
 *      height: 0,
 *      left: 0,
 *      top: 0,
 *      isAddToMenu: false,
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      customStamps: '',
 *      enableCustomStamp: true,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class CustomStampSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], CustomStampSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], CustomStampSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], CustomStampSettings.prototype, "opacity", void 0);
__decorate([
    Property('Guest')
], CustomStampSettings.prototype, "author", void 0);
__decorate([
    Property(0)
], CustomStampSettings.prototype, "width", void 0);
__decorate([
    Property(0)
], CustomStampSettings.prototype, "height", void 0);
__decorate([
    Property(0)
], CustomStampSettings.prototype, "left", void 0);
__decorate([
    Property(0)
], CustomStampSettings.prototype, "top", void 0);
__decorate([
    Property(false)
], CustomStampSettings.prototype, "isAddToMenu", void 0);
__decorate([
    Property(0)
], CustomStampSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], CustomStampSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], CustomStampSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], CustomStampSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], CustomStampSettings.prototype, "isLock", void 0);
__decorate([
    Property('')
], CustomStampSettings.prototype, "customStamps", void 0);
__decorate([
    Property(true)
], CustomStampSettings.prototype, "enableCustomStamp", void 0);
__decorate([
    Property(['None'])
], CustomStampSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], CustomStampSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], CustomStampSettings.prototype, "subject", void 0);
/**
 * The `DistanceSettings` module is used to provide the properties to distance calibrate annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the distance annotation settings.
 *  viewer.distanceSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      vertexPoints: [],
 *      opacity: 1,
 *      fillColor: '#4070FF',
 *      strokeColor: '#ff0000',
 *      author: 'Guest',
 *      thickness: 1,
 *      borderDashArray: 0,
 *      lineHeadStartStyle: 'Closed',
 *      lineHeadEndStyle: 'Closed',
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Square',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      customData: null,
 *      leaderLength: 40,
 *      resizeCursorType: CursorType.move,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class DistanceSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], DistanceSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], DistanceSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], DistanceSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ff0000')
], DistanceSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#ff0000')
], DistanceSettings.prototype, "strokeColor", void 0);
__decorate([
    Property('Guest')
], DistanceSettings.prototype, "author", void 0);
__decorate([
    Property('1')
], DistanceSettings.prototype, "thickness", void 0);
__decorate([
    Property('None')
], DistanceSettings.prototype, "lineHeadStartStyle", void 0);
__decorate([
    Property('None')
], DistanceSettings.prototype, "lineHeadEndStyle", void 0);
__decorate([
    Property(0)
], DistanceSettings.prototype, "borderDashArray", void 0);
__decorate([
    Property('')
], DistanceSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(0)
], DistanceSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], DistanceSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], DistanceSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], DistanceSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], DistanceSettings.prototype, "isLock", void 0);
__decorate([
    Property(null)
], DistanceSettings.prototype, "customData", void 0);
__decorate([
    Property(40)
], DistanceSettings.prototype, "leaderLength", void 0);
__decorate([
    Property(CursorType.move)
], DistanceSettings.prototype, "resizeCursorType", void 0);
__decorate([
    Property(['None'])
], DistanceSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], DistanceSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], DistanceSettings.prototype, "subject", void 0);
/**
 * The `PerimeterSettings` module is used to provide the properties to perimeter calibrate annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the perimeter annotation settings.
 *  viewer.perimeterSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      vertexPoints: [],
 *      opacity: 1,
 *      fillColor: '#4070FF',
 *      strokeColor: '#ff0000',
 *      author: 'Guest',
 *      thickness: 1,
 *      borderDashArray: 0,
 *      lineHeadStartStyle: 'Open',
 *      lineHeadEndStyle: 'Open',
 *      minHeight: 0, minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#4070FF',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Circle',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class PerimeterSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], PerimeterSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], PerimeterSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], PerimeterSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], PerimeterSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#ff0000')
], PerimeterSettings.prototype, "strokeColor", void 0);
__decorate([
    Property('Guest')
], PerimeterSettings.prototype, "author", void 0);
__decorate([
    Property('1')
], PerimeterSettings.prototype, "thickness", void 0);
__decorate([
    Property('None')
], PerimeterSettings.prototype, "lineHeadStartStyle", void 0);
__decorate([
    Property('None')
], PerimeterSettings.prototype, "lineHeadEndStyle", void 0);
__decorate([
    Property(0)
], PerimeterSettings.prototype, "borderDashArray", void 0);
__decorate([
    Property(0)
], PerimeterSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], PerimeterSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], PerimeterSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], PerimeterSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], PerimeterSettings.prototype, "isLock", void 0);
__decorate([
    Property('')
], PerimeterSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(['None'])
], PerimeterSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], PerimeterSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], PerimeterSettings.prototype, "subject", void 0);
/**
 * The `AreaSettings` module is used to provide the properties to area calibrate annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the area annotation settings.
 *  viewer.areaSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      vertexPoints: [],
 *      opacity: 1,
 *      fillColor: '#4070FF',
 *      strokeColor: '#ff0000',
 *      author: 'Guest',
 *      thickness: 1,
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#4070FF',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Circle',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class AreaSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], AreaSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], AreaSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], AreaSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], AreaSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#ff0000')
], AreaSettings.prototype, "strokeColor", void 0);
__decorate([
    Property('Guest')
], AreaSettings.prototype, "author", void 0);
__decorate([
    Property('1')
], AreaSettings.prototype, "thickness", void 0);
__decorate([
    Property(0)
], AreaSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], AreaSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], AreaSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], AreaSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], AreaSettings.prototype, "isLock", void 0);
__decorate([
    Property('')
], AreaSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(['None'])
], AreaSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], AreaSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], AreaSettings.prototype, "subject", void 0);
/**
 * The `RadiusSettings` module is used to provide the properties to radius calibrate annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the radius annotation settings.
 *  viewer.radiusSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      width: 100,
 *      height: 90,
 *      opacity: 1,
 *      fillColor: '#4070FF',
 *      strokeColor: '#ff0000',
 *      author: 'Guest',
 *      thickness: 1,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'red',
 *          resizerFillColor: '#4070FF',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Circle',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      customData: null,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class RadiusSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], RadiusSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], RadiusSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(100)
], RadiusSettings.prototype, "width", void 0);
__decorate([
    Property(90)
], RadiusSettings.prototype, "height", void 0);
__decorate([
    Property(1)
], RadiusSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], RadiusSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#ff0000')
], RadiusSettings.prototype, "strokeColor", void 0);
__decorate([
    Property('Guest')
], RadiusSettings.prototype, "author", void 0);
__decorate([
    Property('1')
], RadiusSettings.prototype, "thickness", void 0);
__decorate([
    Property('')
], RadiusSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(0)
], RadiusSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], RadiusSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], RadiusSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], RadiusSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], RadiusSettings.prototype, "isLock", void 0);
__decorate([
    Property(null)
], RadiusSettings.prototype, "customData", void 0);
__decorate([
    Property(['None'])
], RadiusSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], RadiusSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], RadiusSettings.prototype, "subject", void 0);
/**
 * The `VolumeSettings` module is used to provide the properties to volume calibrate annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the volume annotation settings.
 *  viewer.volumeSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      vertexPoints: [],
 *      opacity: 1,
 *      fillColor: '#4070FF',
 *      strokeColor: '#ff0000',
 *      author: 'Guest',
 *      thickness: 1,
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#4070FF',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Circle',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class VolumeSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], VolumeSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], VolumeSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], VolumeSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], VolumeSettings.prototype, "fillColor", void 0);
__decorate([
    Property('#ff0000')
], VolumeSettings.prototype, "strokeColor", void 0);
__decorate([
    Property('Guest')
], VolumeSettings.prototype, "author", void 0);
__decorate([
    Property('1')
], VolumeSettings.prototype, "thickness", void 0);
__decorate([
    Property(0)
], VolumeSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], VolumeSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], VolumeSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], VolumeSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], VolumeSettings.prototype, "isLock", void 0);
__decorate([
    Property('')
], VolumeSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(['None'])
], VolumeSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], VolumeSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], VolumeSettings.prototype, "subject", void 0);
/**
 * The `Ink` module is used to provide the properties to Ink annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the ink annotation settings.
 *  viewer.inkAnnotationSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      width: 0,
 *      height: 0,
 *      path: '',
 *      author: 'Guest',
 *      opacity: 1,
 *      strokeColor: '#ff0000',
 *      thickness: 1,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Circle',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      isLock: false,
 *      customData: null
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class InkAnnotationSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], InkAnnotationSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], InkAnnotationSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(0)
], InkAnnotationSettings.prototype, "width", void 0);
__decorate([
    Property(0)
], InkAnnotationSettings.prototype, "height", void 0);
__decorate([
    Property(0)
], InkAnnotationSettings.prototype, "path", void 0);
__decorate([
    Property(1)
], InkAnnotationSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ff0000')
], InkAnnotationSettings.prototype, "strokeColor", void 0);
__decorate([
    Property(1)
], InkAnnotationSettings.prototype, "thickness", void 0);
__decorate([
    Property('')
], InkAnnotationSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(false)
], InkAnnotationSettings.prototype, "isLock", void 0);
__decorate([
    Property('Guest')
], InkAnnotationSettings.prototype, "author", void 0);
__decorate([
    Property(['None'])
], InkAnnotationSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(null)
], InkAnnotationSettings.prototype, "customData", void 0);
__decorate([
    Property(true)
], InkAnnotationSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], InkAnnotationSettings.prototype, "subject", void 0);
/**
 * The `stickyNotesSettings` module is used to provide the properties to sticky notes annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the sticky notes annotation settings.
 *  viewer.stickyNotesSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      author: 'Guest',
 *      opacity: 1,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'red',
 *          resizerFillColor: '#4070FF',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Circle',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      isLock: false,
 *      customData: null,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class StickyNotesSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], StickyNotesSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], StickyNotesSettings.prototype, "pageNumber", void 0);
__decorate([
    Property('Guest')
], StickyNotesSettings.prototype, "author", void 0);
__decorate([
    Property(1)
], StickyNotesSettings.prototype, "opacity", void 0);
__decorate([
    Property('')
], StickyNotesSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(null)
], StickyNotesSettings.prototype, "customData", void 0);
__decorate([
    Property(false)
], StickyNotesSettings.prototype, "isLock", void 0);
__decorate([
    Property(['None'])
], StickyNotesSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], StickyNotesSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], StickyNotesSettings.prototype, "subject", void 0);
/**
 * The `MeasurementSettings` module is used to provide the settings to measurement annotations.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the measurement annotation settings.
 *  viewer.measurementSettings = {
 *      conversionUnit: 'cm',
 *      displayUnit: 'cm',
 *      scaleRatio: 1,
 *      depth: 96
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class MeasurementSettings extends ChildProperty {
}
__decorate([
    Property(1)
], MeasurementSettings.prototype, "scaleRatio", void 0);
__decorate([
    Property('in')
], MeasurementSettings.prototype, "conversionUnit", void 0);
__decorate([
    Property('in')
], MeasurementSettings.prototype, "displayUnit", void 0);
__decorate([
    Property(96)
], MeasurementSettings.prototype, "depth", void 0);
/**
 * The `FreeTextSettings` module is used to provide the properties to free text annotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the free text annotation settings.
 *  viewer.freeTextSettings = {
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      opacity: 1,
 *      fillColor: '#4070FF',
 *      borderColor: '#4070FF',
 *      author: 'Guest',
 *      borderWidth: 1,
 *      borderStyle: 'solid',
 *      width: 151,
 *      fontSize: 16,
 *      height: 24.6,
 *      fontColor: '#000',
 *      fontFamily: 'Courier',
 *      defaultText: 'Type Here',
 *      fontStyle: 'None',
 *      textAlignment: 'Right',
 *      fontStyle: FontStyle.Italic,
 *      allowTextOnly: false,
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Circle',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      customData: null,
 *      allowedInteractions: ['None'],
 *      isPrint: true,
 *      isReadonly: false,
 *      enableAutoFit: false,
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class FreeTextSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0 })
], FreeTextSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], FreeTextSettings.prototype, "pageNumber", void 0);
__decorate([
    Property(1)
], FreeTextSettings.prototype, "opacity", void 0);
__decorate([
    Property('#ffffff00')
], FreeTextSettings.prototype, "borderColor", void 0);
__decorate([
    Property(1)
], FreeTextSettings.prototype, "borderWidth", void 0);
__decorate([
    Property('solid')
], FreeTextSettings.prototype, "borderStyle", void 0);
__decorate([
    Property('Guest')
], FreeTextSettings.prototype, "author", void 0);
__decorate([
    Property('#ffffff00')
], FreeTextSettings.prototype, "fillColor", void 0);
__decorate([
    Property(16)
], FreeTextSettings.prototype, "fontSize", void 0);
__decorate([
    Property(151)
], FreeTextSettings.prototype, "width", void 0);
__decorate([
    Property(24.6)
], FreeTextSettings.prototype, "height", void 0);
__decorate([
    Property('#000')
], FreeTextSettings.prototype, "fontColor", void 0);
__decorate([
    Property('Helvetica')
], FreeTextSettings.prototype, "fontFamily", void 0);
__decorate([
    Property('TypeHere')
], FreeTextSettings.prototype, "defaultText", void 0);
__decorate([
    Property('None')
], FreeTextSettings.prototype, "fontStyle", void 0);
__decorate([
    Property('Left')
], FreeTextSettings.prototype, "textAlignment", void 0);
__decorate([
    Property(false)
], FreeTextSettings.prototype, "allowEditTextOnly", void 0);
__decorate([
    Property('')
], FreeTextSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property(0)
], FreeTextSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], FreeTextSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], FreeTextSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], FreeTextSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], FreeTextSettings.prototype, "isLock", void 0);
__decorate([
    Property(null)
], FreeTextSettings.prototype, "customData", void 0);
__decorate([
    Property(['None'])
], FreeTextSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property(true)
], FreeTextSettings.prototype, "isPrint", void 0);
__decorate([
    Property(false)
], FreeTextSettings.prototype, "isReadonly", void 0);
__decorate([
    Property(false)
], FreeTextSettings.prototype, "enableAutoFit", void 0);
__decorate([
    Property('')
], FreeTextSettings.prototype, "subject", void 0);
/**
 * The `AnnotationSelectorSettings` module is used to provide the properties to annotation selectors.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the annotation selector settings.
 *  viewer.annotationSelectorSettings = {
 *      selectionBorderColor: '',
 *      resizerBorderColor: 'Circle',
 *      resizerFillColor: '#4070FF',
 *      resizerSize: 8,
 *      selectionBorderThickness: 1,
 *      resizerShape: 'Square',
 *      selectorLineDashArray: [],
 *      resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *      resizerCursorType: null
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class AnnotationSelectorSettings extends ChildProperty {
}
__decorate([
    Property('')
], AnnotationSelectorSettings.prototype, "selectionBorderColor", void 0);
__decorate([
    Property('black')
], AnnotationSelectorSettings.prototype, "resizerBorderColor", void 0);
__decorate([
    Property('#FF4081')
], AnnotationSelectorSettings.prototype, "resizerFillColor", void 0);
__decorate([
    Property(8)
], AnnotationSelectorSettings.prototype, "resizerSize", void 0);
__decorate([
    Property(1)
], AnnotationSelectorSettings.prototype, "selectionBorderThickness", void 0);
__decorate([
    Property('Square')
], AnnotationSelectorSettings.prototype, "resizerShape", void 0);
__decorate([
    Property('')
], AnnotationSelectorSettings.prototype, "selectorLineDashArray", void 0);
__decorate([
    Property(AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges)
], AnnotationSelectorSettings.prototype, "resizerLocation", void 0);
__decorate([
    Property(null)
], AnnotationSelectorSettings.prototype, "resizerCursorType", void 0);
/**
 * The `TextSearchColorSettings` module is used to set the settings for the color of the text search highlight.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the text search color settings.
 *  viewer.textSearchColorSettings = {
 *      searchHighlightColor: '#4070FF',
 *      searchColor: '#FF4081'
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class TextSearchColorSettings extends ChildProperty {
}
__decorate([
    Property('#fdd835')
], TextSearchColorSettings.prototype, "searchHighlightColor", void 0);
__decorate([
    Property('#8b4c12')
], TextSearchColorSettings.prototype, "searchColor", void 0);
/**
 * Represents the information of a specific page within the viewer.
 * This class provides essential information such as the page index, dimensions, and rotation.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  viewer.appendTo("#pdfViewer");
 *  let pageInfo = viewer.getPageInfo(pageIndex);
 *  console.log(pageInfo);
 * ```
 *
 */
export class PageInfo extends ChildProperty {
}
__decorate([
    Property(0)
], PageInfo.prototype, "pageIndex", void 0);
__decorate([
    Property(0)
], PageInfo.prototype, "width", void 0);
__decorate([
    Property(0)
], PageInfo.prototype, "height", void 0);
__decorate([
    Property(0)
], PageInfo.prototype, "rotation", void 0);
/**
 * The `HandWrittenSignatureSettings` module is used to provide the properties to handwritten signature.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the hand written signature settings.
 *  viewer.handWrittenSignatureSettings = {
 *      signatureItem: [
 *          'Signature',
 *          'Initial'
 *      ],
 *      saveSignatureLimit: 1,
 *      saveInitialLimit: 1,
 *      opacity: 1,
 *      strokeColor: '#000000',
 *      width: 150,
 *      height: 100,
 *      thickness: 1,
 *      offset: { x:0, y:0 },
 *      pageNumber: 1,
 *      fontFamily: 'Helvetica',
 *      canSave: false,
 *      typeSignatureFonts: ['arial']
 *      annotationSelectorSettings: {
 *          selectionBorderColor: '',
 *          resizerBorderColor: 'black',
 *          resizerFillColor: '#FF4081',
 *          resizerSize: 8,
 *          selectionBorderThickness: 1,
 *          resizerShape: 'Circle',
 *          selectorLineDashArray: [],
 *          resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges,
 *          resizerCursorType: null
 *      },
 *      allowedInteractions: ['None'],
 *      signatureDialogSettings: {
 *          displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload, hideSaveSignature: false
 *      },
 *      initialDialogSettings: {
 *          displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload,
 *      hideSaveSignature: false
 *      }
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class HandWrittenSignatureSettings extends ChildProperty {
}
__decorate([
    Property(1)
], HandWrittenSignatureSettings.prototype, "opacity", void 0);
__decorate([
    Property('#000000')
], HandWrittenSignatureSettings.prototype, "strokeColor", void 0);
__decorate([
    Property(1)
], HandWrittenSignatureSettings.prototype, "thickness", void 0);
__decorate([
    Property(150)
], HandWrittenSignatureSettings.prototype, "width", void 0);
__decorate([
    Property(100)
], HandWrittenSignatureSettings.prototype, "height", void 0);
__decorate([
    Property(1)
], HandWrittenSignatureSettings.prototype, "saveSignatureLimit", void 0);
__decorate([
    Property(1)
], HandWrittenSignatureSettings.prototype, "saveInitialLimit", void 0);
__decorate([
    Property([])
], HandWrittenSignatureSettings.prototype, "signatureItem", void 0);
__decorate([
    Property()
], HandWrittenSignatureSettings.prototype, "typeSignatureFonts", void 0);
__decorate([
    Property('')
], HandWrittenSignatureSettings.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property()
], HandWrittenSignatureSettings.prototype, "signatureDialogSettings", void 0);
__decorate([
    Property()
], HandWrittenSignatureSettings.prototype, "initialDialogSettings", void 0);
__decorate([
    Property({ x: 0, y: 0 })
], HandWrittenSignatureSettings.prototype, "offset", void 0);
__decorate([
    Property(1)
], HandWrittenSignatureSettings.prototype, "pageNumber", void 0);
__decorate([
    Property('')
], HandWrittenSignatureSettings.prototype, "path", void 0);
__decorate([
    Property('Helvetica')
], HandWrittenSignatureSettings.prototype, "fontFamily", void 0);
__decorate([
    Property(false)
], HandWrittenSignatureSettings.prototype, "canSave", void 0);
/**
 * The `AnnotationSettings` module is used to provide the properties to annotations.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the annotation settings.
 *  viewer.annotationSettings = {
 *      author: 'Guest',
 *      minHeight: 0,
 *      minWidth: 0,
 *      maxWidth: 0,
 *      maxHeight: 0,
 *      isLock: false,
 *      skipPrint: false,
 *      skipDownload: false,
 *      customData: null,
 *      allowedInteractions: ['None'],
 *      subject: ''
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class AnnotationSettings extends ChildProperty {
}
__decorate([
    Property('Guest')
], AnnotationSettings.prototype, "author", void 0);
__decorate([
    Property(0)
], AnnotationSettings.prototype, "minHeight", void 0);
__decorate([
    Property(0)
], AnnotationSettings.prototype, "minWidth", void 0);
__decorate([
    Property(0)
], AnnotationSettings.prototype, "maxHeight", void 0);
__decorate([
    Property(0)
], AnnotationSettings.prototype, "maxWidth", void 0);
__decorate([
    Property(false)
], AnnotationSettings.prototype, "isLock", void 0);
__decorate([
    Property(false)
], AnnotationSettings.prototype, "skipPrint", void 0);
__decorate([
    Property(false)
], AnnotationSettings.prototype, "skipDownload", void 0);
__decorate([
    Property(null)
], AnnotationSettings.prototype, "customData", void 0);
__decorate([
    Property(['None'])
], AnnotationSettings.prototype, "allowedInteractions", void 0);
__decorate([
    Property('')
], AnnotationSettings.prototype, "subject", void 0);
/**
 * The `DocumentTextCollectionSettings` module is used to provide the properties to DocumentTextCollection.
 */
export class DocumentTextCollectionSettings extends ChildProperty {
}
__decorate([
    Property()
], DocumentTextCollectionSettings.prototype, "textData", void 0);
__decorate([
    Property()
], DocumentTextCollectionSettings.prototype, "pageText", void 0);
__decorate([
    Property()
], DocumentTextCollectionSettings.prototype, "pageSize", void 0);
/**
 * The `TextDataSettings` module is used to provide the properties of text data.
 */
export class TextDataSettings extends ChildProperty {
}
__decorate([
    Property()
], TextDataSettings.prototype, "bounds", void 0);
__decorate([
    Property()
], TextDataSettings.prototype, "text", void 0);
/**
 * The `RectangleBounds` module is used to provide the properties of rectangle bounds.
 */
export class RectangleBounds extends ChildProperty {
}
__decorate([
    Property()
], RectangleBounds.prototype, "size", void 0);
__decorate([
    Property()
], RectangleBounds.prototype, "x", void 0);
__decorate([
    Property()
], RectangleBounds.prototype, "y", void 0);
__decorate([
    Property()
], RectangleBounds.prototype, "width", void 0);
__decorate([
    Property()
], RectangleBounds.prototype, "height", void 0);
__decorate([
    Property()
], RectangleBounds.prototype, "left", void 0);
__decorate([
    Property()
], RectangleBounds.prototype, "top", void 0);
__decorate([
    Property()
], RectangleBounds.prototype, "right", void 0);
__decorate([
    Property()
], RectangleBounds.prototype, "bottom", void 0);
__decorate([
    Property()
], RectangleBounds.prototype, "isEmpty", void 0);
/**
 * The `TileRenderingSettings` module is used to provide the tile rendering settings of the PDF viewer.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the tile rendering settings.
 *  viewer.tileRenderingSettings = {
 *      enableTileRendering: false,
 *      x: 0,
 *      y: 0
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class TileRenderingSettings extends ChildProperty {
}
__decorate([
    Property(true)
], TileRenderingSettings.prototype, "enableTileRendering", void 0);
__decorate([
    Property(0)
], TileRenderingSettings.prototype, "x", void 0);
__decorate([
    Property(0)
], TileRenderingSettings.prototype, "y", void 0);
/**
 * The `ScrollSettings` module is used to provide the settings of the scroll of the PDF viewer.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the scroll settings.
 *  viewer.scrollSettings = {
 *      delayPageRequestTimeOnScroll: 150
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class ScrollSettings extends ChildProperty {
}
__decorate([
    Property(100)
], ScrollSettings.prototype, "delayPageRequestTimeOnScroll", void 0);
/**
 * The `FormField` is used to store the form fields of PDF document.
 */
export class FormField extends ChildProperty {
}
__decorate([
    Property('')
], FormField.prototype, "name", void 0);
__decorate([
    Property(false)
], FormField.prototype, "isChecked", void 0);
__decorate([
    Property(false)
], FormField.prototype, "isSelected", void 0);
__decorate([
    Property('')
], FormField.prototype, "id", void 0);
__decorate([
    Property('')
], FormField.prototype, "value", void 0);
__decorate([
    Property('')
], FormField.prototype, "type", void 0);
__decorate([
    Property(false)
], FormField.prototype, "isReadOnly", void 0);
__decorate([
    Property([''])
], FormField.prototype, "signatureType", void 0);
__decorate([
    Property('')
], FormField.prototype, "fontName", void 0);
__decorate([
    Property({ x: 0, y: 0, width: 0, height: 0 })
], FormField.prototype, "bounds", void 0);
__decorate([
    Property('Helvetica')
], FormField.prototype, "fontFamily", void 0);
__decorate([
    Property(10)
], FormField.prototype, "fontSize", void 0);
__decorate([
    Property('None')
], FormField.prototype, "fontStyle", void 0);
__decorate([
    Property('black')
], FormField.prototype, "color", void 0);
__decorate([
    Property('white')
], FormField.prototype, "backgroundColor", void 0);
__decorate([
    Property('Left')
], FormField.prototype, "alignment", void 0);
__decorate([
    Property('visible')
], FormField.prototype, "visibility", void 0);
__decorate([
    Property(0)
], FormField.prototype, "maxLength", void 0);
__decorate([
    Property(false)
], FormField.prototype, "isRequired", void 0);
__decorate([
    Property(false)
], FormField.prototype, "isPrint", void 0);
__decorate([
    Property('')
], FormField.prototype, "tooltip", void 0);
__decorate([
    Property('')
], FormField.prototype, "options", void 0);
__decorate([
    Property()
], FormField.prototype, "signatureIndicatorSettings", void 0);
__decorate([
    Property(1)
], FormField.prototype, "thickness", void 0);
__decorate([
    Property('#303030')
], FormField.prototype, "borderColor", void 0);
__decorate([
    Property(false)
], FormField.prototype, "isMultiline", void 0);
__decorate([
    Property(false)
], FormField.prototype, "insertSpaces", void 0);
__decorate([
    Property(-1)
], FormField.prototype, "pageIndex", void 0);
__decorate([
    Property(1)
], FormField.prototype, "pageNumber", void 0);
__decorate([
    Property(false)
], FormField.prototype, "isTransparent", void 0);
__decorate([
    Property(0)
], FormField.prototype, "rotateAngle", void 0);
__decorate([
    Property('')
], FormField.prototype, "selectedIndex", void 0);
__decorate([
    Property(0)
], FormField.prototype, "zIndex", void 0);
__decorate([
    Property(null)
], FormField.prototype, "customData", void 0);
/**
 * The `ContextMenuSettings` is used to show the context menu of PDF document.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the settings of the context menu option.
 *  viewer.contextMenuSettings = {
 *      contextMenuAction: 'RightClick',
 *      contextMenuItems: [
 *          ContextMenuItem.Comment,
 *          ContextMenuItem.Copy,
 *          ContextMenuItem.Cut,
 *          ContextMenuItem.Delete,
 *          ContextMenuItem.Highlight,
 *          ContextMenuItem.Paste,
 *          ContextMenuItem.Properties,
 *          ContextMenuItem.ScaleRatio,
 *          ContextMenuItem.Strikethrough,
 *          ContextMenuItem.Underline
 *         ]
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class ContextMenuSettings extends ChildProperty {
}
__decorate([
    Property('RightClick')
], ContextMenuSettings.prototype, "contextMenuAction", void 0);
__decorate([
    Property([])
], ContextMenuSettings.prototype, "contextMenuItems", void 0);
/**
 * The `TextFieldSettings` is used to to show and customize the appearance of text box HTML element.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the text field settings.
 *  viewer.textFieldSettings = {
 *      bounds: { x:0,y:0, width: 0, height: 0 },
 *      name: '',
 *      value: '',
 *      fontFamily: 'Courier',
 *      fontSize: 10,
 *      pageNumber: 0,
 *      fontStyle: 'None',
 *      color: 'black',
 *      borderColor: 'black',
 *      backgroundColor: 'white',
 *      alignment: 'Right',
 *      isReadOnly: false,
 *      visibility: 'visible',
 *      maxLength: 0,
 *      isRequired: false,
 *      isPrint: true,
 *      tooltip: '',
 *      thickness: 1,
 *      isMultiline: false,
 *      customData: null
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class TextFieldSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0, width: 0, height: 0 })
], TextFieldSettings.prototype, "bounds", void 0);
__decorate([
    Property('')
], TextFieldSettings.prototype, "name", void 0);
__decorate([
    Property('')
], TextFieldSettings.prototype, "value", void 0);
__decorate([
    Property('Helvetica')
], TextFieldSettings.prototype, "fontFamily", void 0);
__decorate([
    Property(10)
], TextFieldSettings.prototype, "fontSize", void 0);
__decorate([
    Property(0)
], TextFieldSettings.prototype, "pageNumber", void 0);
__decorate([
    Property('None')
], TextFieldSettings.prototype, "fontStyle", void 0);
__decorate([
    Property('black')
], TextFieldSettings.prototype, "color", void 0);
__decorate([
    Property('white')
], TextFieldSettings.prototype, "backgroundColor", void 0);
__decorate([
    Property('Left')
], TextFieldSettings.prototype, "alignment", void 0);
__decorate([
    Property(false)
], TextFieldSettings.prototype, "isReadOnly", void 0);
__decorate([
    Property('visible')
], TextFieldSettings.prototype, "visibility", void 0);
__decorate([
    Property(0)
], TextFieldSettings.prototype, "maxLength", void 0);
__decorate([
    Property(false)
], TextFieldSettings.prototype, "isRequired", void 0);
__decorate([
    Property(false)
], TextFieldSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], TextFieldSettings.prototype, "tooltip", void 0);
__decorate([
    Property(1)
], TextFieldSettings.prototype, "thickness", void 0);
__decorate([
    Property('#303030')
], TextFieldSettings.prototype, "borderColor", void 0);
__decorate([
    Property(false)
], TextFieldSettings.prototype, "isMultiline", void 0);
__decorate([
    Property(null)
], TextFieldSettings.prototype, "customData", void 0);
/**
 * The `PasswordFieldSettings` is used to to show and customize the appearance of password input HTML element.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the password field settings.
 *  viewer.passwordFieldSettings = {
 *      bounds: { x:0,y:0, width: 0, height: 0 },
 *      name: '',
 *      value: '',
 *      pageNumber: 0,
 *      fontFamily: 'Courier',
 *      fontSize: 10,
 *      fontStyle: 'None',
 *      color: 'black',
 *      borderColor: 'black',
 *      backgroundColor: 'white',
 *      alignment: 'Right',
 *      isReadOnly: false,
 *      visibility: 'visible',
 *      maxLength: 0,
 *      isRequired: false,
 *      isPrint: true,
 *      tooltip: '',
 *      thickness: 1,
 *      customData: null
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class PasswordFieldSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0, width: 0, height: 0 })
], PasswordFieldSettings.prototype, "bounds", void 0);
__decorate([
    Property('')
], PasswordFieldSettings.prototype, "name", void 0);
__decorate([
    Property('')
], PasswordFieldSettings.prototype, "value", void 0);
__decorate([
    Property(0)
], PasswordFieldSettings.prototype, "pageNumber", void 0);
__decorate([
    Property('Helvetica')
], PasswordFieldSettings.prototype, "fontFamily", void 0);
__decorate([
    Property(10)
], PasswordFieldSettings.prototype, "fontSize", void 0);
__decorate([
    Property('None')
], PasswordFieldSettings.prototype, "fontStyle", void 0);
__decorate([
    Property('black')
], PasswordFieldSettings.prototype, "color", void 0);
__decorate([
    Property('white')
], PasswordFieldSettings.prototype, "backgroundColor", void 0);
__decorate([
    Property('Left')
], PasswordFieldSettings.prototype, "alignment", void 0);
__decorate([
    Property(false)
], PasswordFieldSettings.prototype, "isReadOnly", void 0);
__decorate([
    Property('visible')
], PasswordFieldSettings.prototype, "visibility", void 0);
__decorate([
    Property(0)
], PasswordFieldSettings.prototype, "maxLength", void 0);
__decorate([
    Property(false)
], PasswordFieldSettings.prototype, "isRequired", void 0);
__decorate([
    Property(false)
], PasswordFieldSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], PasswordFieldSettings.prototype, "tooltip", void 0);
__decorate([
    Property(1)
], PasswordFieldSettings.prototype, "thickness", void 0);
__decorate([
    Property('#303030')
], PasswordFieldSettings.prototype, "borderColor", void 0);
__decorate([
    Property(null)
], PasswordFieldSettings.prototype, "customData", void 0);
/**
 * The `CheckBoxFieldSettings` is used to to show and customize the appearance of check box element.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the check box field settings.
 *  viewer.checkBoxFieldSettings = {
 *      bounds: { x:0,y:0, width: 0, height: 0 },
 *      name: '',
 *      value: '',
 *      isChecked: true,
 *      pageNumber: 0,
 *      backgroundColor: 'white',
 *      isReadOnly: false,
 *      visibility: 'visible',
 *      isPrint: true,
 *      tooltip: '',
 *      isRequired: false,
 *      thickness: 5,
 *      borderColor: 'black',
 *      customData: null
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class CheckBoxFieldSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0, width: 0, height: 0 })
], CheckBoxFieldSettings.prototype, "bounds", void 0);
__decorate([
    Property('')
], CheckBoxFieldSettings.prototype, "name", void 0);
__decorate([
    Property('')
], CheckBoxFieldSettings.prototype, "value", void 0);
__decorate([
    Property(false)
], CheckBoxFieldSettings.prototype, "isChecked", void 0);
__decorate([
    Property('white')
], CheckBoxFieldSettings.prototype, "backgroundColor", void 0);
__decorate([
    Property(false)
], CheckBoxFieldSettings.prototype, "isReadOnly", void 0);
__decorate([
    Property('visible')
], CheckBoxFieldSettings.prototype, "visibility", void 0);
__decorate([
    Property(false)
], CheckBoxFieldSettings.prototype, "isPrint", void 0);
__decorate([
    Property(0)
], CheckBoxFieldSettings.prototype, "pageNumber", void 0);
__decorate([
    Property('')
], CheckBoxFieldSettings.prototype, "tooltip", void 0);
__decorate([
    Property(false)
], CheckBoxFieldSettings.prototype, "isRequired", void 0);
__decorate([
    Property(1)
], CheckBoxFieldSettings.prototype, "thickness", void 0);
__decorate([
    Property('#303030')
], CheckBoxFieldSettings.prototype, "borderColor", void 0);
__decorate([
    Property(null)
], CheckBoxFieldSettings.prototype, "customData", void 0);
/**
 * The `RadioButtonFieldSettings` is used to to show and customize the appearance of radio button element.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the radio button field settings.
 *  viewer.radioButtonFieldSettings = {
 *      bounds: { x:0,y:0, width: 0, height: 0 },
 *      name: '',
 *      value: '',
 *      pageNumber: 0,
 *      isSelected: false,
 *      backgroundColor: 'white',
 *      isReadOnly: false,
 *      visibility: 'visible',
 *      isPrint: true,
 *      tooltip: '',
 *      isRequired: false,
 *      thickness: 1,
 *      borderColor: 'black',
 *      customData: null
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class RadioButtonFieldSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0, width: 0, height: 0 })
], RadioButtonFieldSettings.prototype, "bounds", void 0);
__decorate([
    Property('')
], RadioButtonFieldSettings.prototype, "name", void 0);
__decorate([
    Property('')
], RadioButtonFieldSettings.prototype, "value", void 0);
__decorate([
    Property(false)
], RadioButtonFieldSettings.prototype, "isSelected", void 0);
__decorate([
    Property('white')
], RadioButtonFieldSettings.prototype, "backgroundColor", void 0);
__decorate([
    Property(false)
], RadioButtonFieldSettings.prototype, "isReadOnly", void 0);
__decorate([
    Property(false)
], RadioButtonFieldSettings.prototype, "isRequired", void 0);
__decorate([
    Property(0)
], RadioButtonFieldSettings.prototype, "pageNumber", void 0);
__decorate([
    Property('visible')
], RadioButtonFieldSettings.prototype, "visibility", void 0);
__decorate([
    Property(false)
], RadioButtonFieldSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], RadioButtonFieldSettings.prototype, "tooltip", void 0);
__decorate([
    Property(1)
], RadioButtonFieldSettings.prototype, "thickness", void 0);
__decorate([
    Property('#303030')
], RadioButtonFieldSettings.prototype, "borderColor", void 0);
__decorate([
    Property(null)
], RadioButtonFieldSettings.prototype, "customData", void 0);
/**
 * The `DropdownFieldSettings` is used to to show and customize the appearance of drop down element.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the drop down field settings.
 *  viewer.DropdownFieldSettings = {
 *      bounds: { x:0,y:0, width: 0, height: 0 },
 *      name: '',
 *      value: '',
 *      fontFamily: 'Helvetica',
 *      fontSize: 10,
 *      pageNumber: 0,
 *      fontStyle: 'None',
 *      color: 'black',
 *      backgroundColor: 'white',
 *      alignment: 'Left',
 *      isReadOnly: true,
 *      visibility: 'visible',
 *      isPrint: true,
 *      tooltip: '',
 *      isRequired: false,
 *      options: '',
 *      thickness: 5,
 *      borderColor: '303030',
 *      customData: null
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class DropdownFieldSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0, width: 0, height: 0 })
], DropdownFieldSettings.prototype, "bounds", void 0);
__decorate([
    Property('')
], DropdownFieldSettings.prototype, "name", void 0);
__decorate([
    Property('')
], DropdownFieldSettings.prototype, "value", void 0);
__decorate([
    Property('Helvetica')
], DropdownFieldSettings.prototype, "fontFamily", void 0);
__decorate([
    Property(10)
], DropdownFieldSettings.prototype, "fontSize", void 0);
__decorate([
    Property(0)
], DropdownFieldSettings.prototype, "pageNumber", void 0);
__decorate([
    Property('None')
], DropdownFieldSettings.prototype, "fontStyle", void 0);
__decorate([
    Property('black')
], DropdownFieldSettings.prototype, "color", void 0);
__decorate([
    Property('white')
], DropdownFieldSettings.prototype, "backgroundColor", void 0);
__decorate([
    Property('Left')
], DropdownFieldSettings.prototype, "alignment", void 0);
__decorate([
    Property(false)
], DropdownFieldSettings.prototype, "isReadOnly", void 0);
__decorate([
    Property('visible')
], DropdownFieldSettings.prototype, "visibility", void 0);
__decorate([
    Property(false)
], DropdownFieldSettings.prototype, "isRequired", void 0);
__decorate([
    Property(false)
], DropdownFieldSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], DropdownFieldSettings.prototype, "tooltip", void 0);
__decorate([
    Property('')
], DropdownFieldSettings.prototype, "options", void 0);
__decorate([
    Property(1)
], DropdownFieldSettings.prototype, "thickness", void 0);
__decorate([
    Property('#303030')
], DropdownFieldSettings.prototype, "borderColor", void 0);
__decorate([
    Property(null)
], DropdownFieldSettings.prototype, "customData", void 0);
/**
 * The `ListBoxFieldSettings` is used to to show and customize the appearance of list box element.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the list box field settings.
 *  viewer.listBoxFieldSettings = {
 *      bounds: { x:0,y:0, width: 0, height: 0 },
 *      name: '',
 *      value: '',
 *      fontFamily: 'Courier',
 *      fontSize: 5,
 *      pageNumber: 0,
 *      color: 'black',
 *      backgroundColor: 'white',
 *      alignment: 'Right',
 *      isReadOnly: false,
 *      visibility: 'visible',
 *      isRequired: false,
 *      isPrint: false,
 *      tooltip: '',
 *      options: [],
 *      thickness: 1,
 *      borderColor: 'black'
 *  };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class ListBoxFieldSettings extends ChildProperty {
}
__decorate([
    Property({ x: 0, y: 0, width: 0, height: 0 })
], ListBoxFieldSettings.prototype, "bounds", void 0);
__decorate([
    Property('')
], ListBoxFieldSettings.prototype, "name", void 0);
__decorate([
    Property('')
], ListBoxFieldSettings.prototype, "value", void 0);
__decorate([
    Property('Helvetica')
], ListBoxFieldSettings.prototype, "fontFamily", void 0);
__decorate([
    Property(10)
], ListBoxFieldSettings.prototype, "fontSize", void 0);
__decorate([
    Property(0)
], ListBoxFieldSettings.prototype, "pageNumber", void 0);
__decorate([
    Property('None')
], ListBoxFieldSettings.prototype, "fontStyle", void 0);
__decorate([
    Property('black')
], ListBoxFieldSettings.prototype, "color", void 0);
__decorate([
    Property('white')
], ListBoxFieldSettings.prototype, "backgroundColor", void 0);
__decorate([
    Property('Left')
], ListBoxFieldSettings.prototype, "alignment", void 0);
__decorate([
    Property(false)
], ListBoxFieldSettings.prototype, "isReadOnly", void 0);
__decorate([
    Property('visible')
], ListBoxFieldSettings.prototype, "visibility", void 0);
__decorate([
    Property(false)
], ListBoxFieldSettings.prototype, "isRequired", void 0);
__decorate([
    Property(false)
], ListBoxFieldSettings.prototype, "isPrint", void 0);
__decorate([
    Property('')
], ListBoxFieldSettings.prototype, "tooltip", void 0);
__decorate([
    Property([])
], ListBoxFieldSettings.prototype, "options", void 0);
__decorate([
    Property(1)
], ListBoxFieldSettings.prototype, "thickness", void 0);
__decorate([
    Property('#303030')
], ListBoxFieldSettings.prototype, "borderColor", void 0);
export class Item extends ChildProperty {
}
__decorate([
    Property('')
], Item.prototype, "itemName", void 0);
__decorate([
    Property('')
], Item.prototype, "itemValue", void 0);
/**
 * Defines the combination of keys and modifier keys.
 */
export class KeyGesture extends ChildProperty {
}
__decorate([
    Property()
], KeyGesture.prototype, "pdfKeys", void 0);
__decorate([
    Property()
], KeyGesture.prototype, "modifierKeys", void 0);
/**
 * Defines a command and a key gesture to define when the command should be executed.
 */
export class KeyboardCommand extends ChildProperty {
}
__decorate([
    Property('')
], KeyboardCommand.prototype, "name", void 0);
__decorate([
    Complex({}, KeyGesture)
], KeyboardCommand.prototype, "gesture", void 0);
/**
 * Defines the collection of commands and the corresponding key gestures.
 *```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 * let viewer: PdfViewer = new PdfViewer();
 * viewer.commandManager = {
 *      keyboardCommand: [{
 *          name: 'customCopy',
 *          gesture: {
 *              pdfKeys: PdfKeys.G,
 *              modifierKeys: ModifierKeys.Shift | ModifierKeys.Alt
 *          }
 *       },
 *       {
 *          name: 'customPaste',
 *          gesture: {
 *              pdfKeys: PdfKeys.H,
 *              modifierKeys: ModifierKeys.Shift | ModifierKeys.Alt
 *          }
 *      }]
 * };
 * viewer.appendTo("#pdfViewer");
 * ```
 */
export class CommandManager extends ChildProperty {
}
__decorate([
    Collection([], KeyboardCommand)
], CommandManager.prototype, "keyboardCommand", void 0);
/**
 * The `PageOrganizerSettings` is allows pages to be deleted, inserted and rotated in the PDF viewer.
 *
 * ```html
 * <div id="pdfViewer" style="height: 100%;width: 100%;"></div>
 * ```
 * ```ts
 *  let viewer: PdfViewer = new PdfViewer();
 *  // Change the page organizer settings.
 *  viewer.pageOrganizerSettings = {
 *           canDelete: true,
 *           canInsert: true,
 *           canRotate: true,
 *           canCopy: true,
 *           canRearrange: true,
 *           canImport: true,
 *           imageZoom: 1,
 *           showImageZoomingSlider: false,
 *           imageZoomMin: 1,
 *           imageZoomMax: 5
 *   };
 *  viewer.appendTo("#pdfViewer");
 * ```
 *
 */
export class PageOrganizerSettings extends ChildProperty {
}
__decorate([
    Property(true)
], PageOrganizerSettings.prototype, "canDelete", void 0);
__decorate([
    Property(true)
], PageOrganizerSettings.prototype, "canInsert", void 0);
__decorate([
    Property(true)
], PageOrganizerSettings.prototype, "canRotate", void 0);
__decorate([
    Property(true)
], PageOrganizerSettings.prototype, "canCopy", void 0);
__decorate([
    Property(true)
], PageOrganizerSettings.prototype, "canRearrange", void 0);
__decorate([
    Property(true)
], PageOrganizerSettings.prototype, "canImport", void 0);
__decorate([
    Property(false)
], PageOrganizerSettings.prototype, "showImageZoomingSlider", void 0);
__decorate([
    Property(1)
], PageOrganizerSettings.prototype, "imageZoomMin", void 0);
__decorate([
    Property(5)
], PageOrganizerSettings.prototype, "imageZoomMax", void 0);
__decorate([
    Property(1)
], PageOrganizerSettings.prototype, "imageZoom", void 0);
/**
 * The `SearchResult` provides the page index along with an array of bounds that indicate the locations of the search text identified on that page.
 */
export class SearchResult extends ChildProperty {
}
__decorate([
    Property(0)
], SearchResult.prototype, "pageIndex", void 0);
__decorate([
    Property([])
], SearchResult.prototype, "bounds", void 0);
/**
 * Represents the PDF viewer component.
 * ```html
 * <div id="pdfViewer"></div>
 * <script>
 *  var pdfViewerObj = new PdfViewer();
 *  pdfViewerObj.appendTo("#pdfViewer");
 * </script>
 * ```
 */
let PdfViewer = class PdfViewer extends Component {
    constructor(options, element) {
        super(options, element);
        /**
         * Checks if the form fields are loaded for the document in the PdfViewer control.
         *
         * @private
         */
        this.isFormFieldsLoaded = false;
        /**
         * Get the Loaded document signature Collections in the PdfViewer control.
         *
         * {% codeBlock src='pdfviewer/signatureCollection/index.md' %}{% endcodeBlock %}
         *
         */
        this.signatureCollection = [];
        /**
         * Gets or sets the document name loaded in the PdfViewer control.
         *
         * {% codeBlock src='pdfviewer/fileName/index.md' %}{% endcodeBlock %}
         *
         */
        this.fileName = null;
        /**
         * @private
         */
        this.zIndex = -1;
        /**
         * @private
         */
        this.nameTable = {};
        /**   @private  */
        this.clipboardData = {};
        /**
         * @private
         */
        this.zIndexTable = [];
        this.isTextSelectionStarted = false;
        /**
         * @private
         */
        this.touchPadding = 10;
        /**
         * @private
         */
        this.paddingDifferenceValue = 10;
        /** @hidden */
        this.defaultLocale = {
            'PdfViewer': 'PDF Viewer',
            'Cancel': 'Cancel',
            'Download file': 'Download file',
            'Download': 'Download',
            'Enter Password': 'This document is password protected. Please enter a password.',
            'File Corrupted': 'File Corrupted',
            'File Corrupted Content': 'The file is corrupted and cannot be opened.',
            'Fit Page': 'Fit Page',
            'Fit Width': 'Fit Width',
            'Automatic': 'Automatic',
            'Go To First Page': 'Show first page',
            'Invalid Password': 'Incorrect Password. Please try again.',
            'Next Page': 'Show next page',
            'OK': 'OK',
            'Open': 'Open file',
            'Page Number': 'Current page number',
            'Previous Page': 'Show previous page',
            'Go To Last Page': 'Show last page',
            'Zoom': 'Zoom',
            'Zoom In': 'Zoom in',
            'Zoom Out': 'Zoom out',
            'Page Thumbnails': 'Page Thumbnails',
            'Bookmarks': 'Bookmarks',
            'Print': 'Print file',
            'Organize Pages': 'Organize Pages',
            'Insert Right': 'Insert Right',
            'Insert Left': 'Insert Left',
            'Total': 'Total',
            'Pages': 'Pages',
            'Rotate Right': 'Rotate Right',
            'Rotate Left': 'Rotate Left',
            'Delete Page': 'Delete Page',
            'Delete Pages': 'Delete Pages',
            'Copy Page': 'Copy Page',
            'Copy Pages': 'Copy Pages',
            'Save': 'Save',
            'Save As': 'Save As',
            'Select All': 'Select All',
            'Import Document': 'Import Document',
            'Change Page Zoom': 'Change Page Zoom',
            'Increase Page Zoom': 'Increase Page Zoom',
            'Decrease Page Zoom': 'Decrease Page Zoom',
            'Password Protected': 'Password Required',
            'Copy': 'Copy',
            'Text Selection': 'Text selection tool',
            'Panning': 'Pan mode',
            'Text Search': 'Find text',
            'Find in document': 'Find in document',
            'Match case': 'Match case',
            'Match any word': 'Match any word',
            'Apply': 'Apply',
            'GoToPage': 'Go to Page',
            'No Matches': 'PDF Viewer has finished searching the document. No matches were found.',
            'No More Matches': 'PDF Viewer has finished searching the document. No more matches were found.',
            'No Search Matches': 'No matches found',
            'No More Search Matches': 'No more matches found',
            'Exact Matches': 'EXACT MATCHES',
            'Total Matches': 'TOTAL MATCHES',
            'Undo': 'Undo',
            'Redo': 'Redo',
            'Annotation': 'Add or Edit annotations',
            'FormDesigner': 'Add and Edit Form Fields',
            'Highlight': 'Highlight Text',
            'Underline': 'Underline Text',
            'Strikethrough': 'Strikethrough Text',
            'Squiggly': 'Squiggly Text',
            'Delete': 'Delete annotation',
            'Opacity': 'Opacity',
            'Color edit': 'Change Color',
            'Opacity edit': 'Change Opacity',
            'Highlight context': 'Highlight',
            'Underline context': 'Underline',
            'Strikethrough context': 'Strikethrough',
            'Squiggly context': 'Squiggly',
            'Server error': 'Web-service is not listening. PDF Viewer depends on web-service for all it\'s features. Please start the web service to continue.',
            'Client error': 'Client-side error is found. Please check the custom headers provided in the AjaxRequestSettings property and web action methods in the ServerActionSettings property.',
            'Cors policy error': 'Unable to retrieve the document due to an invalid URL or access restrictions. Please check the document URL and try again.',
            'Open text': 'Open',
            'First text': 'First Page',
            'Previous text': 'Previous Page',
            'Next text': 'Next Page',
            'Last text': 'Last Page',
            'Zoom in text': 'Zoom In',
            'Zoom out text': 'Zoom Out',
            'Selection text': 'Selection',
            'Pan text': 'Pan',
            'Print text': 'Print',
            'Search text': 'Search',
            'Annotation Edit text': 'Edit Annotation',
            'FormDesigner Edit text': 'Add and Edit Form Fields',
            'Line Thickness': 'Line Thickness',
            'Line Properties': 'Line Properties',
            'Start Arrow': 'Start Arrow',
            'End Arrow': 'End Arrow',
            'Line Style': 'Line Style',
            'Fill Color': 'Fill Color',
            'Line Color': 'Line Color',
            'None': 'None',
            'Open Arrow': 'Open',
            'Closed Arrow': 'Closed',
            'Round Arrow': 'Round',
            'Square Arrow': 'Square',
            'Diamond Arrow': 'Diamond',
            'Butt': 'Butt',
            'Cut': 'Cut',
            'Paste': 'Paste',
            'Delete Context': 'Delete',
            'Properties': 'Properties',
            'Add Stamp': 'Add Stamp',
            'Add Shapes': 'Add Shapes',
            'Stroke edit': 'Change Stroke Color',
            'Change thickness': 'Change Border Thickness',
            'Add line': 'Add Line',
            'Add arrow': 'Add Arrow',
            'Add rectangle': 'Add Rectangle',
            'Add circle': 'Add Circle',
            'Add polygon': 'Add Polygon',
            'Add Comments': 'Add Comments',
            'Comments': 'Comments',
            'SubmitForm': 'Submit Form',
            'No Comments Yet': 'No Comments Yet',
            'Accepted': 'Accepted',
            'Completed': 'Completed',
            'Cancelled': 'Cancelled',
            'Rejected': 'Rejected',
            'Leader Length': 'Leader Length',
            'Scale Ratio': 'Scale Ratio',
            'Calibrate': 'Calibrate',
            'Calibrate Distance': 'Calibrate Distance',
            'Calibrate Perimeter': 'Calibrate Perimeter',
            'Calibrate Area': 'Calibrate Area',
            'Calibrate Radius': 'Calibrate Radius',
            'Calibrate Volume': 'Calibrate Volume',
            'Depth': 'Depth',
            'Closed': 'Closed',
            'Round': 'Round',
            'Square': 'Square',
            'Diamond': 'Diamond',
            'Edit': 'Edit',
            'Comment': 'Comment',
            'Comment Panel': 'Comment Panel',
            'Set Status': 'Set Status',
            'Post': 'Post',
            'Page': 'Page',
            'Add a comment': 'Add a comment',
            'Add a reply': 'Add a reply',
            'Import Annotations': 'Import annotations from JSON file',
            'Export Annotations': 'Export annotation to JSON file',
            'Export XFDF': 'Export annotation to XFDF file',
            'Import XFDF': 'Import annotations from XFDF file',
            'Add': 'Add',
            'Clear': 'Clear',
            'Bold': 'Bold',
            'Italic': 'Italic',
            'Strikethroughs': 'Strikethrough',
            'Underlines': 'Underline',
            'Superscript': 'Superscript',
            'Subscript': 'Subscript',
            'Align left': 'Align Left',
            'Align right': 'Align Right',
            'Center': 'Center',
            'Justify': 'Justify',
            'Font color': 'Font Color',
            'Text Align': 'Text Align',
            'Text Properties': 'Font Style',
            'SignatureFieldDialogHeaderText': 'Add Signature',
            'HandwrittenSignatureDialogHeaderText': 'Add Signature',
            'InitialFieldDialogHeaderText': 'Add Initial',
            'HandwrittenInitialDialogHeaderText': 'Add Initial',
            'Draw Ink': 'Draw Ink',
            'Create': 'Create',
            'Font family': 'Font Family',
            'Font size': 'Font Size',
            'Free Text': 'Free Text',
            'Import Failed': 'Invalid JSON file type or file name; please select a valid JSON file',
            'Import PDF Failed': 'Invalid PDF file type or PDF file not found. Please select a valid PDF file',
            'File not found': 'Imported JSON file is not found in the desired location',
            'Export Failed': 'Export annotations action has failed; please ensure annotations are added properly',
            'of': 'of ',
            'Dynamic': 'Dynamic',
            'Standard Business': 'Standard Business',
            'Sign Here': 'Sign Here',
            'Custom Stamp': 'Custom Stamp',
            'Enter Signature as Name': 'Enter your name',
            'Draw-hand Signature': 'DRAW',
            'Type Signature': 'TYPE',
            'Upload Signature': 'UPLOAD',
            'Browse Signature Image': 'BROWSE',
            'Save Signature': 'Save Signature',
            'Save Initial': 'Save Initial',
            'Textbox': 'Textbox',
            'Password': 'Password',
            'Check Box': 'Checkbox',
            'Radio Button': 'Radio Button',
            'Dropdown': 'Drop Down',
            'List Box': 'List Box',
            'Signature': 'Signature',
            'Delete FormField': 'Delete Form Field',
            'Textbox Properties': 'Textbox Properties',
            'Name': 'Name',
            'Tooltip': 'Tooltip',
            'Value': 'Value',
            'Form Field Visibility': 'Form Field Visibility',
            'Read Only': 'Read Only',
            'Required': 'Required',
            'Checked': 'Checked',
            'Show Printing': 'Show Printing',
            'Formatting': 'Format',
            'Fill': 'Fill',
            'Border': 'Border',
            'Border Color': 'Border Color',
            'Thickness': 'Thickness',
            'Max Length': 'Max Length',
            'List Item': 'Item Name',
            'Export Value': 'Item Value',
            'Dropdown Item List': 'Dropdown Item List',
            'List Box Item List': 'List Box Item List',
            'General': 'GENERAL',
            'Appearance': 'APPEARANCE',
            'Options': 'OPTIONS',
            'Delete Item': 'Delete',
            'Up': 'Up',
            'Down': 'Down',
            'Multiline': 'Multiline',
            'Revised': 'Revised',
            'Reviewed': 'Reviewed',
            'Received': 'Received',
            'Confidential': 'Confidential',
            'Approved': 'Approved',
            'Not Approved': 'Not Approved',
            'Witness': 'Witness',
            'Initial Here': 'Initial Here',
            'Draft': 'Draft',
            'Final': 'Final',
            'For Public Release': 'For Public Release',
            'Not For Public Release': 'Not For Public Release',
            'For Comment': 'For Comment',
            'Void': 'Void',
            'Preliminary Results': 'Preliminary Results',
            'Information Only': 'Information Only',
            'in': 'in',
            'm': 'm',
            'ft_in': 'ft_in',
            'ft': 'ft',
            'p': 'p',
            'cm': 'cm',
            'mm': 'mm',
            'pt': 'pt',
            'cu': 'cu',
            'sq': 'sq',
            'Initial': 'Initial'
        };
        this.viewerBase = new PdfViewerBase(this);
        this.drawing = new Drawing(this);
        this.pdfRendererModule = new PdfRenderer(this, this.viewerBase);
    }
    /**
     * Returns the current zoom percentage of the PdfViewer control.
     *
     * @asptype int
     * @blazorType int
     * @returns {number} - number
     */
    get zoomPercentage() {
        return this.magnificationModule.zoomFactor * 100;
    }
    /**
     * Gets the bookmark view object of the pdf viewer.
     *
     * @asptype BookmarkView
     * @blazorType BookmarkView
     * @returns { BookmarkView } - Bookmark view module
     */
    get bookmark() {
        return this.bookmarkViewModule;
    }
    /**
     * Gets the print object of the pdf viewer.
     *
     * @asptype Print
     * @blazorType Print
     * @returns { Print } - Print module
     */
    get print() {
        return this.printModule;
    }
    /**
     * Gets the magnification object of the pdf viewer.
     *
     * @asptype Magnification
     * @blazorType Magnification
     * @returns { Magnification } - Magnification module
     */
    get magnification() {
        return this.magnificationModule;
    }
    /**
     * Gets the navigation object of the pdf viewer.
     *
     * @asptype Navigation
     * @blazorType Navigation
     * @returns { Navigation } - Navigation module
     */
    get navigation() {
        return this.navigationModule;
    }
    /**
     * Gets the text search object of the pdf viewer.
     *
     * @asptype TextSearch
     * @blazorType TextSearch
     * @returns { TextSearch } - Text search module
     */
    get textSearch() {
        return this.textSearchModule;
    }
    /**
     * Gets the toolbar object of the pdf viewer.
     *
     * @asptype Toolbar
     * @blazorType Toolbar
     * @returns { Toolbar } - Toolbar module
     */
    get toolbar() {
        return this.toolbarModule;
    }
    /**
     * Gets the thumbnail-view object of the pdf viewer.
     *
     * @asptype ThumbnailView
     * @blazorType ThumbnailView
     * @returns { ThumbnailView } - Thumbnail view module
     */
    get thumbnailView() {
        return this.thumbnailViewModule;
    }
    /**
     * Gets the annotation object of the pdf viewer.
     *
     * @asptype Annotation
     * @blazorType Annotation
     * @returns { Annotation } - Annotation module
     */
    get annotation() {
        return this.annotationModule;
    }
    /**
     * Gets the FormDesigner object of the pdf viewer.
     *
     * @asptype FormDesigner
     * @blazorType FormDesigner
     * @returns { FormDesigner } - Form designer module
     */
    get formDesigner() {
        return this.formDesignerModule;
    }
    /**
     * Gets the TextSelection object of the pdf viewer.
     *
     * @asptype TextSelection
     * @blazorType TextSelection
     * @returns { TextSelection } - Text selection module
     */
    get textSelection() {
        return this.textSelectionModule;
    }
    /**
     * Gets the Accessibility Tags object of the pdf viewer.
     *
     * @asptype AccessibilityTags
     * @blazorType AccessibilityTags
     * @returns { AccessibilityTags } - Accessibility tags module
     */
    get accessibilityTags() {
        return this.accessibilityTagsModule;
    }
    /**
     * Gets the Pdf renderer object of the pdf renderer.
     *
     * @asptype PdfRenderer
     * @blazorType PdfRenderer
     * @returns { PdfRenderer } - Pdf renderer module
     * @private
     */
    get pdfRenderer() {
        return this.pdfRendererModule;
    }
    /**
     * Gets the page organizer object of the PDF Viewer.
     *
     * @asptype PageOrganizer
     * @blazorType PageOrganizer
     * @returns { PageOrganizer } - Page organizer module
     */
    get pageOrganizer() {
        return this.pageOrganizerModule;
    }
    preRender() {
        this.localeObj = new L10n(this.getModuleName(), this.defaultLocale, this.locale);
        if (!isNullOrUndefined(this.element) && this.element.id === '') {
            //Set unique id, if id is empty
            this.element.id = this.getUniqueElementId();
        }
        if (Browser.isDevice) {
            //EJ2-63562 - Reduced the touchPadding of mobile devices to 16 to improve selection of fields without affecting resizing ability.
            this.touchPadding = 16;
        }
        this.updateLocalStorage(this.enableLocalStorage);
    }
    getUniqueElementId() {
        return 'pdfViewer_' + Date.now().toString(36) + Math.random().toString(36).substring(2);
    }
    initializePdfiumModule(fontCollection) {
        this.viewerBase.pdfViewerRunner.addTask({
            url: this.getScriptPathForPlatform(),
            fonts: fontCollection,
            message: 'initialLoading'
        }, TaskPriorityLevel.High);
    }
    render() {
        if (this.enableHtmlSanitizer && this.serviceUrl) {
            this.serviceUrl = SanitizeHtmlHelper.sanitize(this.serviceUrl);
        }
        if (isNullOrUndefined(this.serviceUrl) || this.serviceUrl === '') {
            this.viewerBase.clientSideRendering = true;
        }
        if (this.viewerBase.clientSideRendering) {
            // eslint-disable-next-line
            const proxy = this;
            const workerBob = new Blob([PdfiumRunner.toString().replace(/^[^{]*{([\s\S]*)}$/m, '$1')], { type: 'text/javascript' });
            const workerBlobUrl = URL.createObjectURL(workerBob);
            window['pdfViewerRunner_' + this.element.id] = this.viewerBase.pdfViewerRunner = new PdfiumTaskScheduler(workerBlobUrl, proxy);
            if (this.customFonts && this.customFonts.length > 0) {
                PdfViewerUtils.fetchCustomFonts(this.customFonts, this.getScriptPathForPlatform()).then((fontCollection) => {
                    this.pdfRenderer.FallbackFontCollection = fontCollection;
                    this.initializePdfiumModule(fontCollection);
                });
            }
            else {
                this.initializePdfiumModule({});
            }
            this.viewerBase.pdfViewerRunner.onMessage('loaded', function (event) {
                if (event.data.message === 'loaded') {
                    proxy.renderComponent();
                    proxy.fireResourcesLoaded();
                }
            });
        }
        else {
            this.renderComponent();
        }
    }
    /**
     * Exports the specified page as a Base64-encoded image string.
     *
     * @param {number} pageIndex -  The index of the page to export.
     * @param {Size} [size] - Specifies the width and height of the image. If not specified, the default size will be used.
     * @param {number} [size.width] - The width of the image.
     * @param {number} [size.height] - The height of the image.
     * @returns { Promise<string> } - Returns the Base64-encoded string representing the image of the specified page.
     * @private
     */
    exportAsImage(pageIndex, size) {
        let image;
        if (!isNullOrUndefined(size)) {
            image = this.pdfRendererModule.exportAsImagewithSize(pageIndex, size);
        }
        else {
            image = this.pdfRendererModule.exportAsImage(pageIndex);
        }
        return image;
    }
    /**
     * Exports the range of pages as Base64-encoded image strings.
     *
     * @param {number} startIndex - The index of the first page to export.
     * @param {number} endIndex - The index of the last page to export.
     * @param {Size} [size] - Specifies the width and height of the image. If not specified, the default size will be used.
     * @param {number} [size.width] - The width of the image.
     * @param {number} [size.height] - The height of the image.
     * @returns { Promise<string[]> } - An array of Base64-encoded strings, each representing the image of a page within the specified range.
     * @private
     */
    exportAsImages(startIndex, endIndex, size) {
        let imageDeatils;
        if (!isNullOrUndefined(size)) {
            imageDeatils = this.pdfRendererModule.exportAsImagesWithSize(startIndex, endIndex, size);
        }
        else {
            imageDeatils = this.pdfRendererModule.exportAsImages(startIndex, endIndex);
        }
        return imageDeatils;
    }
    /**
     * Extracts text from one or multiple pages in the PDF document based on the specified criteria.
     *
     * This method retrieves the text data and associated information from the specified page(s) in the PDF document.
     * Based on the input parameters, it can extract text data from either a single page or a range of pages.
     *
     * @param {number} startIndex - The starting page index for text extraction.
     * @param {number | ExtractTextOption} endIndexOrIsoptions - Either the ending page index for the text extraction
     *        or the options for text extraction if extracting text from a single page.
     * @param {ExtractTextOption} [options] - Optional parameter that specifies additional options for text extraction
     *        when extracting from multiple pages.
     *
     * @returns {Promise<{textData: TextDataSettingsModel[], pageText: string}>} - A promise that resolves with an object containing two properties:
     * - textData: An array of TextDataSettingsModel objects, each representing details and structure of extracted text.
     * - pageText: A concatenated string of the text extracted from the page(s).
     *          {Promise<{pageText: string}>} - A promise resolving with an object containing the extracted plain text
     *                                           from a single page.
     *          {Promise<{textData: TextDataSettingsModel[]}>} - A promise resolving with an array of extracted text
     *                                                            data models from a single page.
     */
    extractText(startIndex, endIndexOrIsoptions, options) {
        const isLayout = true;
        let endIndex;
        let option;
        // Determines the correct values for endIndex and isLayout
        if (typeof endIndexOrIsoptions === 'number') {
            endIndex = endIndexOrIsoptions;
            option = options;
        }
        else {
            option = endIndexOrIsoptions;
        }
        if (isNullOrUndefined(endIndex)) {
            endIndex = startIndex;
        }
        return this.pdfRendererModule.extractsText(startIndex, endIndex, option, isLayout);
    }
    getScriptPathForPlatform() {
        if (this.enableHtmlSanitizer && this.resourceUrl) {
            this.resourceUrl = SanitizeHtmlHelper.sanitize(this.resourceUrl);
        }
        if (!isNullOrUndefined(this.resourceUrl) && this.resourceUrl !== '') {
            return this.resourceUrl;
        }
        const { protocol, host, pathname } = document.location;
        // Remove trailing slashes from the pathname using a regular expression
        const trimmedPathname = pathname.replace(/\/+$/, '');
        const baseUrl = `${protocol}//${host}${trimmedPathname}`;
        if (this.isAngular || (this.parent && this.parent.isAngular)) {
            return baseUrl + '/assets/ej2-pdfviewer-lib';
        }
        else if (this.isReact || (this.parent && this.parent.isReact)) {
            return baseUrl + '/ej2-pdfviewer-lib';
        }
        else if ((this.isVue || (this.parent && this.parent.isVue)) ||
            (this.isVue3 || (this.parent && this.parent.isVue3))) {
            return baseUrl + '/public/js/ej2-pdfviewer-lib';
        }
        else {
            window.getRunningScript = () => {
                return () => {
                    const stackTrace = new Error().stack;
                    // eslint-disable-next-line
                    const match = stackTrace && stackTrace.match(/(?:http[s]?:\/\/(?:[^\/\s]+\/))(.*\.js)/);
                    return match ? match[0] : 'src/pdfviewer/pdfviewer.js';
                };
            };
            const scriptLinkURL = window.getRunningScript()();
            const splitURL = scriptLinkURL.split('/');
            const path = scriptLinkURL.replace('/' + splitURL[splitURL.length - 1], '');
            return path + '/ej2-pdfviewer-lib';
        }
    }
    renderComponent() {
        this.viewerBase.initializeComponent();
        if (!this.enableFormFields) {
            this.formFieldsModule = new FormFields(this, this.viewerBase);
            this.formFieldsModule.formFieldsReadOnly(this.enableFormFields);
        }
        if (this.enableTextSelection && this.textSelectionModule) {
            this.textSelectionModule.enableTextSelectionMode();
        }
        else {
            this.viewerBase.disableTextSelectionMode();
        }
        this.drawing.renderLabels(this);
        this.renderComplete();
    }
    getModuleName() {
        return 'PdfViewer';
    }
    /**
     * @private
     * @returns {Object} - Object
     */
    getLocaleConstants() {
        return this.defaultLocale;
    }
    /**
     * To modify the Json Data in ajax request.
     *
     * @param jsonData
     * @returns void
     */
    setJsonData(jsonData) {
        this.viewerBase.ajaxData = jsonData;
    }
    updateLocalStorage(enable) {
        if (enable && !PdfViewerBase.sessionStorageManager.enableLocalStorage) {
            PdfViewerBase.sessionStorageManager.migrateToLocalStorage(true);
        }
        else {
            this.enableLocalStorage = PdfViewerBase.sessionStorageManager.enableLocalStorage;
        }
    }
    onPropertyChanged(newProp, oldProp) {
        let requireRefresh = false;
        if (this.isDestroyed) {
            return;
        }
        const properties = Object.keys(newProp);
        for (const prop of properties) {
            switch (prop) {
                case 'locale':
                    if (this.viewerBase.loadedData) {
                        let data = null;
                        if (this.formFieldsModule) {
                            data = this.viewerBase.getItemFromSessionStorage('_formfields');
                        }
                        if (data) {
                            this.viewerBase.formfieldvalue = JSON.parse(data);
                            const annotCollection = this.annotationCollection;
                            const filename = this.viewerBase.jsonDocumentId;
                            super.refresh();
                            this.load(this.viewerBase.loadedData, null);
                            this.addAnnotation(annotCollection);
                            this.viewerBase.loadedData = null;
                            this.downloadFileName = filename;
                            this.fileName = filename;
                        }
                    }
                    break;
                case 'toolbarSettings':
                    if (!Browser.isDevice || this.enableDesktopMode) {
                        this.toolbar.applyToolbarSettings();
                        if (!isNullOrUndefined(this.toolbar.annotationToolbarModule)) {
                            this.toolbar.annotationToolbarModule.applyAnnotationToolbarSettings();
                        }
                        if (!isNullOrUndefined(this.toolbar.formDesignerToolbarModule)) {
                            this.toolbar.formDesignerToolbarModule.applyFormDesignerToolbarSettings();
                        }
                    }
                    else {
                        this.toolbar.applyToolbarSettingsForMobile();
                        this.toolbar.annotationToolbarModule.applyMobileAnnotationToolbarSettings();
                    }
                    break;
                case 'enableToolbar':
                    this.notify('', { module: 'toolbar', enable: this.enableToolbar });
                    requireRefresh = true;
                    break;
                case 'enableLocalStorage':
                    this.updateLocalStorage(this.enableLocalStorage);
                    break;
                case 'enableTextSelection':
                    if (!Browser.isDevice || this.enableDesktopMode) {
                        this.interactionMode = 'TextSelection';
                        if (!isNullOrUndefined(this.toolbarModule) && !isNullOrUndefined(this.formDesignerModule) &&
                            this.toolbarModule.formDesignerToolbarModule && this.isFormDesignerToolbarVisible) {
                            this.viewerBase.disableTextSelectionMode();
                        }
                        else {
                            this.toolbarModule.updateInteractionItems();
                        }
                    }
                    else {
                        if (this.enableTextSelection && this.textSelectionModule) {
                            this.textSelectionModule.enableTextSelectionMode();
                        }
                        else {
                            this.viewerBase.disableTextSelectionMode();
                        }
                    }
                    break;
                case 'enableCommentPanel':
                    this.notify('', { module: 'annotation', enable: this.enableCommentPanel });
                    requireRefresh = true;
                    if (this.toolbarModule && this.toolbarModule.annotationToolbarModule) {
                        this.toolbarModule.annotationToolbarModule.enableCommentPanelTool(this.enableCommentPanel);
                    }
                    if (!this.enableCommentPanel) {
                        if (this.viewerBase.navigationPane) {
                            this.viewerBase.navigationPane.closeCommentPanelContainer();
                        }
                    }
                    break;
                case 'documentPath':
                    if (!this.viewerBase.isSkipDocumentPath) {
                        if (!isBlazor()) {
                            this.load(newProp.documentPath, null);
                        }
                        else {
                            this._dotnetInstance.invokeMethodAsync('LoadDocumentFromClient', newProp.documentPath);
                        }
                    }
                    else {
                        this.viewerBase.isSkipDocumentPath = false;
                    }
                    break;
                case 'interactionMode':
                    this.interactionMode = newProp.interactionMode;
                    if (newProp.interactionMode === 'Pan') {
                        this.viewerBase.initiatePanning();
                        if (this.toolbar) {
                            this.toolbar.updateInteractionTools(false);
                        }
                    }
                    else if (newProp.interactionMode === 'TextSelection') {
                        this.viewerBase.initiateTextSelectMode();
                        if (this.toolbar) {
                            this.toolbar.updateInteractionTools(true);
                        }
                    }
                    break;
                case 'height':
                    this.height = newProp.height;
                    this.viewerBase.updateHeight();
                    this.viewerBase.onWindowResize();
                    if (this.toolbar && this.toolbar.annotationToolbarModule) {
                        if (this.toolbar.annotationToolbarModule.isToolbarHidden) {
                            this.toolbar.annotationToolbarModule.adjustViewer(false);
                        }
                        else {
                            this.toolbar.annotationToolbarModule.adjustViewer(true);
                        }
                    }
                    break;
                case 'width':
                    this.width = newProp.width;
                    this.viewerBase.updateWidth();
                    this.viewerBase.onWindowResize();
                    break;
                case 'customStamp':
                    this.renderCustomerStamp(this.customStamp[0]);
                    break;
                case 'customStampSettings':
                    if (newProp.customStampSettings.customStamps) {
                        for (let i = 0; i < newProp.customStampSettings.customStamps.length; i++) {
                            this.viewerBase.customStampCollection.push({ customStampName: this.customStampSettings.
                                    customStamps[parseInt(i.toString(), 10)].customStampName, customStampImageSource: this.customStampSettings.customStamps[parseInt(i.toString(), 10)].customStampImageSource });
                        }
                    }
                    break;
                case 'enableFormFields':
                    if (this.enableFormFields && this.formFieldsModule) {
                        for (let m = 0; m < this.pageCount; m++) {
                            this.formFieldsModule.renderFormFields(m, false);
                        }
                    }
                    else {
                        this.formFieldsModule = new FormFields(this, this.viewerBase);
                        this.formFieldsModule.formFieldsReadOnly(this.enableFormFields);
                    }
                    break;
                case 'designerMode':
                    if (this.designerMode) {
                        this.formDesignerModule.setMode('designer');
                    }
                    else {
                        this.formDesignerModule.setMode('edit');
                    }
                    break;
                case 'highlightSettings':
                case 'underlineSettings':
                case 'strikethroughSettings':
                case 'squigglySettings':
                    if (this.annotationModule && this.annotationModule.textMarkupAnnotationModule) {
                        this.annotationModule.textMarkupAnnotationModule.updateTextMarkupSettings(prop);
                    }
                    break;
                case 'signatureFieldSettings':
                case 'initialFieldSettings':
                    if (this.formDesignerModule) {
                        const isInitialField = (prop === 'initialFieldSettings');
                        // eslint-disable-next-line security/detect-object-injection
                        this.formDesignerModule.updateSignatureSettings(newProp[prop], isInitialField);
                    }
                    break;
                case 'textFieldSettings':
                    if (this.formDesignerModule) {
                        // eslint-disable-next-line security/detect-object-injection
                        this.formDesignerModule.updateTextFieldSettings(newProp[prop]);
                    }
                    break;
                case 'passwordFieldSettings':
                    if (this.formDesignerModule) {
                        // eslint-disable-next-line security/detect-object-injection
                        this.formDesignerModule.updatePasswordFieldSettings(newProp[prop]);
                    }
                    break;
                case 'checkBoxFieldSettings':
                    if (this.formDesignerModule) {
                        // eslint-disable-next-line security/detect-object-injection
                        this.formDesignerModule.updateCheckBoxFieldSettings(newProp[prop]);
                    }
                    break;
                case 'radioButtonFieldSettings':
                    if (this.formDesignerModule) {
                        // eslint-disable-next-line security/detect-object-injection
                        this.formDesignerModule.updateRadioButtonFieldSettings(newProp[prop]);
                    }
                    break;
                case 'DropdownFieldSettings':
                    if (this.formDesignerModule) {
                        // eslint-disable-next-line security/detect-object-injection
                        this.formDesignerModule.updateDropDownFieldSettings(newProp[prop]);
                    }
                    break;
                case 'listBoxFieldSettings':
                    if (this.formDesignerModule) {
                        // eslint-disable-next-line security/detect-object-injection
                        this.formDesignerModule.updateListBoxFieldSettings(newProp[prop]);
                    }
                    break;
                case 'isFormDesignerToolbarVisible':
                    if (!Browser.isDevice || this.enableDesktopMode) {
                        if (this.toolbarModule && this.formDesignerModule && !oldProp.isFormDesignerToolbarVisible &&
                            newProp.isFormDesignerToolbarVisible) {
                            if (this.toolbarModule.annotationToolbarModule && this.isAnnotationToolbarVisible) {
                                this.isAnnotationToolbarVisible = false;
                                this.toolbarModule.annotationToolbarModule.showAnnotationToolbar();
                            }
                            this.toolbarModule.formDesignerToolbarModule.resetFormDesignerToolbar();
                        }
                        else {
                            if (!isNullOrUndefined(this.toolbarModule) && !isNullOrUndefined(this.formDesignerModule) &&
                                this.toolbarModule.formDesignerToolbarModule && !this.isFormDesignerToolbarVisible) {
                                this.isFormDesignerToolbarVisible = false;
                                this.formDesignerModule.setMode('edit');
                                this.toolbarModule.formDesignerToolbarModule.resetFormDesignerToolbar();
                            }
                        }
                    }
                    break;
                case 'isAnnotationToolbarVisible':
                    if (!Browser.isDevice || this.enableDesktopMode) {
                        if (this.toolbarModule && this.annotationModule && !oldProp.isAnnotationToolbarVisible &&
                            newProp.isAnnotationToolbarVisible) {
                            if (this.toolbarModule.formDesignerToolbarModule && this.isFormDesignerToolbarVisible) {
                                this.isFormDesignerToolbarVisible = false;
                                this.toolbarModule.formDesignerToolbarModule.showFormDesignerToolbar();
                            }
                            this.toolbarModule.annotationToolbarModule.resetToolbar();
                        }
                    }
                    else {
                        if (this.toolbarModule) {
                            this.toolbar.showAnnotationToolbar(newProp.isAnnotationToolbarVisible);
                        }
                    }
                    break;
                case 'serviceUrl':
                    if (isNullOrUndefined(newProp.serviceUrl) || newProp.serviceUrl === '') {
                        this.viewerBase.clientSideRendering = true;
                    }
                    else {
                        this.viewerBase.clientSideRendering = false;
                    }
                    break;
                case 'pageOrganizerSettings':
                    if (!isNullOrUndefined(newProp.pageOrganizerSettings)) {
                        if (!isNullOrUndefined(this.pageOrganizer)) {
                            this.pageOrganizer.setPageOrganizerSettings(newProp.pageOrganizerSettings);
                            if (oldProp.pageOrganizerSettings.imageZoomMax !== newProp.pageOrganizerSettings.imageZoomMax
                                || oldProp.pageOrganizerSettings.imageZoomMin !== newProp.pageOrganizerSettings.imageZoomMin) {
                                this.pageOrganizer.handleImageSizeBoundsChange(newProp.pageOrganizerSettings);
                            }
                            if (oldProp.pageOrganizerSettings.showImageZoomingSlider !== newProp.pageOrganizerSettings.showImageZoomingSlider) {
                                this.pageOrganizer.handleImageResizerVisibility(newProp.pageOrganizerSettings.showImageZoomingSlider);
                            }
                            if (oldProp.pageOrganizerSettings.imageZoom !== newProp.pageOrganizerSettings.imageZoom) {
                                this.pageOrganizer.handlePageZoomChange(newProp.pageOrganizerSettings.imageZoom, oldProp.pageOrganizerSettings.imageZoom);
                            }
                        }
                    }
                    break;
            }
        }
    }
    renderCustomerStamp(customStamp) {
        this.annotation.stampAnnotationModule.isStampAddMode = true;
        this.annotationModule.stampAnnotationModule.isStampAnnotSelected = true;
        this.viewerBase.stampAdded = true;
        this.viewerBase.isAlreadyAdded = false;
        this.annotation.stampAnnotationModule.createCustomStampAnnotation(customStamp.customStampImageSource, customStamp.customStampName);
    }
    getPersistData() {
        return 'PdfViewer';
    }
    requiredModules() {
        const modules = [];
        if (this.enableMagnification) {
            modules.push({
                member: 'Magnification', args: [this, this.viewerBase]
            });
        }
        if (this.enableNavigation) {
            modules.push({
                member: 'Navigation', args: [this, this.viewerBase]
            });
        }
        if (this.enableToolbar || this.enableNavigationToolbar || this.enableAnnotationToolbar || this.enableFormDesignerToolbar) {
            modules.push({
                member: 'Toolbar', args: [this, this.viewerBase]
            });
        }
        if (this.enableHyperlink) {
            modules.push({
                member: 'LinkAnnotation', args: [this, this.viewerBase]
            });
        }
        if (this.enableThumbnail) {
            modules.push({
                member: 'ThumbnailView', args: [this, this.viewerBase]
            });
        }
        if (this.enableBookmark) {
            modules.push({
                member: 'BookmarkView', args: [this, this.viewerBase]
            });
        }
        if (this.enableTextSelection) {
            modules.push({
                member: 'TextSelection', args: [this, this.viewerBase]
            });
        }
        if (this.enableTextSearch) {
            modules.push({
                member: 'TextSearch', args: [this, this.viewerBase]
            });
        }
        if (this.enablePrint) {
            modules.push({
                member: 'Print', args: [this, this.viewerBase]
            });
        }
        if (this.enableAnnotation) {
            modules.push({
                member: 'Annotation', args: [this, this.viewerBase]
            });
        }
        if (this.enableFormFields) {
            modules.push({
                member: 'FormFields', args: [this, this.viewerBase]
            });
        }
        if (this.enableFormDesigner && !isBlazor()) {
            modules.push({
                member: 'FormDesigner', args: [this, this.viewerBase]
            });
        }
        if (this.enableAccessibilityTags) {
            modules.push({
                member: 'AccessibilityTags', args: [this, this.viewerBase]
            });
        }
        if (isNullOrUndefined(this.serviceUrl) || this.serviceUrl === '') {
            modules.push({
                member: 'PdfRenderer', args: [this, this.viewerBase]
            });
        }
        if (this.enablePageOrganizer) {
            modules.push({
                member: 'PageOrganizer', args: [this, this.viewerBase]
            });
        }
        return modules;
    }
    /**
     * Loads the given PDF document in the PDF viewer control
     *
     * @param  {string} document - Specifies the document name for load
     * @param  {string} password - Specifies the Given document password
     * @returns {void}
     */
    load(document, password) {
        this.loadDocInternally(document, password);
    }
    /**
     * Loads the given PDF document internally in the PDF viewer control
     *
     * @param  {string} document - Specifies the document name for load
     * @param  {string} password - Specifies the Given document password
     * @param  {boolean} isSkipDocumentId - It indicates whether we need to skip removing the jsonDocumentId
     * @private
     * @returns {void}
     */
    loadDocInternally(document, password, isSkipDocumentId = true) {
        if (this.pageCount !== 0) {
            this.viewerBase.clear(true);
        }
        else {
            this.viewerBase.clear(false);
        }
        this.pageCount = 0;
        this.currentPageNumber = 0;
        if (!isBlazor()) {
            if (this.toolbarModule) {
                this.toolbarModule.resetToolbar();
            }
        }
        else {
            this.viewerBase.blazorUIAdaptor.resetToolbar();
        }
        this.isFormFieldsLoaded = true;
        if (this.viewerBase.documentPathByteArray instanceof Uint8Array && !this.viewerBase.clientSideRendering) {
            const base64String = this.convertByteArrayToBase64(this.viewerBase.documentPathByteArray);
            this.viewerBase.initiatePageRender(base64String, password);
        }
        else {
            this.viewerBase.initiatePageRender(document, password, isSkipDocumentId);
        }
    }
    convertByteArrayToBase64(byteArray) {
        let binaryString = '';
        const byteArrayLength = byteArray.byteLength;
        for (let i = 0; i < byteArrayLength; i++) {
            binaryString += String.fromCharCode(byteArray[parseInt(i.toString(), 10)]);
        }
        return btoa(binaryString);
    }
    /**
     * Loads the given PDF document in the PDF viewer control
     *
     * @param {string} documentId - It describes about the document Id value
     * @param {boolean} isFileName - It ensures whether the file name is true or not
     * @param {string} fileName - It describes about the file name
     * @private
     * @returns {void}
     */
    loadDocument(documentId, isFileName, fileName) {
        if (this.pageCount !== 0) {
            this.viewerBase.clear(true);
        }
        else {
            this.viewerBase.clear(false);
        }
        this.viewerBase.clear(!isNullOrUndefined(this.customContextMenuItems));
        this.pageCount = 0;
        this.currentPageNumber = 0;
        this.viewerBase.blazorUIAdaptor.resetToolbar();
        this.fileName = fileName;
        this.viewerBase.initiateLoadDocument(documentId, isFileName, fileName);
    }
    /**
     * Loads the PDF document with the document details in the PDF viewer control
     *
     * @param {any} documentDetails - It describes about the document details
     * @param {string} password - It describes about the password value
     * @private
     * @returns {void}
     */
    loadSuccess(documentDetails, password) {
        this.viewerBase.loadSuccess(documentDetails, password);
    }
    /**
     * Set the focus of the given element
     *
     * @param {string} elementId - It describes about the element id value
     * @private
     * @returns {void}
     */
    focusElement(elementId) {
        const element = document.getElementById(elementId);
        if (element != null) {
            element.focus();
        }
    }
    /**
     * Downloads the PDF document being loaded in the ejPdfViewer control.
     *
     * @returns {void}
     */
    download() {
        if (this.enableDownload) {
            this.viewerBase.download();
        }
    }
    /**
     * Saves the PDF document being loaded in the PDF Viewer control as blob.
     *
     * @returns {Promise<Blob>} - Promise
     */
    saveAsBlob() {
        if (this.enableDownload) {
            return new Promise((resolve, reject) => {
                resolve(this.viewerBase.saveAsBlob());
            });
        }
        else {
            return null;
        }
    }
    /**
     * updates the PDF Viewer container width and height from externally.
     *
     * @returns {void}
     */
    updateViewerContainer() {
        this.viewerBase.updateViewerContainer();
    }
    /**
     * Retrieves the information of a specified page in the viewer.
     * @param {number} pageIndex - The page index to get the details. The first page is 0.
     * @returns {PageInfo} - An instance of the PageInfo model containing the page information.
     */
    getPageInfo(pageIndex) {
        const pageDetails = {};
        if (!isNullOrUndefined(pageIndex) && typeof pageIndex == 'number' && !isNullOrUndefined(this.viewerBase.pageSize)
            && this.viewerBase.pageSize.length !== 0 && pageIndex >= 0 && pageIndex < this.viewerBase.pageSize.length) {
            const pageIndexValue = parseInt(pageIndex.toString(), 10);
            const pageSizeDetails = this.viewerBase.pageSize[pageIndexValue];
            pageDetails.pageIndex = pageIndexValue;
            pageDetails.height = this.viewerBase.ConvertPixelToPoint(pageSizeDetails.height);
            pageDetails.width = this.viewerBase.ConvertPixelToPoint(pageSizeDetails.width);
            pageDetails.rotation = this.viewerBase.getAngle(pageSizeDetails.rotation);
        }
        return pageDetails;
    }
    /**
     * Specifies the message to be displayed  in the popup.
     *
     * @param {string} errorString - It describes about the error string value
     * @returns {void}
     */
    showNotificationPopup(errorString) {
        this.viewerBase.showNotificationPopup(errorString);
    }
    /**
     * Focus a form field in a document by its field name or the field object.
     *
     * @param {any} field - It describes about the field value
     * @returns {void}
     */
    focusFormField(field) {
        if (typeof (field) === 'string') {
            const fieldCollections = this.retrieveFormFields();
            for (let i = 0; i < fieldCollections.length; i++) {
                if (fieldCollections[parseInt(i.toString(), 10)].name === field) {
                    field = fieldCollections[parseInt(i.toString(), 10)];
                }
            }
        }
        if (field) {
            this.viewerBase.isFocusField = true;
            this.viewerBase.focusField = field;
            if (this.formDesignerModule) {
                this.navigationModule.goToPage(field.pageIndex + 1);
            }
            else {
                const pageIndex = parseFloat(field.id.split('_')[1]);
                this.navigationModule.goToPage(pageIndex + 1);
            }
            setTimeout(() => {
                let currentField = document.getElementById(field.id);
                if (this.formDesignerModule && field.type === 'Checkbox') {
                    currentField = document.getElementById(field.id + '_input');
                }
                if (currentField) {
                    if (this.formDesignerModule && (field.type === 'SignatureField' || field.type === 'InitialField')) {
                        currentField.parentElement.focus();
                    }
                    else {
                        currentField.focus();
                        this.viewerBase.isFocusField = false;
                        this.viewerBase.focusField = [];
                    }
                }
            }, 100);
        }
    }
    /**
     * Update the form field values from externally.
     *
     * @param {any} fieldValue - It describes about the field value
     * @returns {void}
     */
    updateFormFieldsValue(fieldValue) {
        let target = document.getElementById(fieldValue.id);
        let isformDesignerModuleListBox = false;
        if (target) {
            target = target ? target : document.getElementById(fieldValue.id + '_content_html_element').children[0].children[0];
            if (target && fieldValue.type === 'Textbox' || fieldValue.type === 'Password' || fieldValue.type === 'PasswordField') {
                target.value = fieldValue.value;
                target.multiline = fieldValue.isMultiline;
            }
            else if (fieldValue.type === 'Checkbox' || fieldValue.type === 'RadioButton' || fieldValue.type === 'CheckBox') {
                if (fieldValue.type === 'CheckBox') {
                    target.style.appearance = 'auto';
                }
                if (this.formDesignerModule) {
                    if (fieldValue.type === 'RadioButton') {
                        const radioButtonOption = { isSelected: fieldValue.isSelected };
                        this.formDesignerModule.updateFormField(fieldValue, radioButtonOption);
                    }
                    else {
                        const checkBoxOption = { isChecked: fieldValue.isChecked };
                        this.formDesignerModule.updateFormField(fieldValue, checkBoxOption);
                    }
                }
                else {
                    if (fieldValue.type === 'RadioButton') {
                        target.selected = fieldValue.isSelected;
                    }
                    else {
                        target.checked = fieldValue.isChecked;
                    }
                }
                // eslint-disable-next-line
                if (target.value != fieldValue.value) {
                    target.value = fieldValue.value;
                }
            }
            else if (fieldValue.type === 'DropDown' || fieldValue.type === 'ListBox' || fieldValue.type === 'DropdownList') {
                if (this.formDesignerModule) {
                    isformDesignerModuleListBox = true;
                    const dropDownListOption = { options: fieldValue.value };
                    this.formDesignerModule.updateFormField(fieldValue, dropDownListOption);
                }
                else {
                    target.value = fieldValue.value;
                    target.selectedIndex = fieldValue.selectedIndex;
                }
            }
            if (fieldValue.type === 'SignatureField' || fieldValue.type === 'InitialField') {
                if (fieldValue.signatureType || fieldValue.initialType) {
                    if (typeof fieldValue.signatureType !== 'string') {
                        fieldValue.signatureType = fieldValue.initialType;
                    }
                }
                fieldValue.fontName = fieldValue.fontName ? fieldValue.fontName : fieldValue.fontFamily;
                const currentValue = fieldValue.value;
                const signatureField = this.getFormFieldByID(fieldValue.id);
                const isSameValue = this.formDesignerModule ? signatureField.value === fieldValue.value :
                    signatureField.Value === fieldValue.value;
                if (target.classList.contains('e-pdfviewer-signatureformfields-signature') && !isSameValue) {
                    if (this.formDesignerModule) {
                        this.annotation.deleteAnnotationById(fieldValue.id.split('_')[0] + '_content');
                    }
                    else {
                        this.annotation.deleteAnnotationById(fieldValue.id);
                    }
                }
                if (!fieldValue.signatureType || !fieldValue.value) {
                    fieldValue.value = currentValue;
                    if (this.viewerBase.isSignaturePathData(fieldValue.value)) {
                        fieldValue.signatureType = 'Path';
                        target.signatureType = 'Path';
                    }
                    else if (this.viewerBase.isSignatureImageData(fieldValue.value)) {
                        fieldValue.signatureType = 'Image';
                        target.signatureType = 'Image';
                    }
                    else {
                        fieldValue.signatureType = 'Type';
                        target.signatureType = 'Type';
                    }
                }
                if (fieldValue.tooltip) {
                    target.tooltip = fieldValue.tooltip;
                }
                target.Required = fieldValue.isRequired ? fieldValue.isRequired : false;
                if (!isSameValue) {
                    this.formFieldsModule.drawSignature(fieldValue.signatureType, fieldValue.value, target, fieldValue.fontName, fieldValue.signatureBounds);
                }
            }
            else {
                if (!isformDesignerModuleListBox) {
                    this.formFieldsModule.updateDataInSession(target);
                }
            }
        }
        else {
            const designerData = this.viewerBase.getItemFromSessionStorage('_formDesigner');
            if (!isNullOrUndefined(designerData)) {
                const FormFieldsData = JSON.parse(designerData);
                const filteredCollection = this.viewerBase.formFieldCollection.filter((field) => {
                    return field.FormField.id.split('_')[0] === fieldValue.id;
                });
                let csData;
                if (fieldValue.signatureType === 'Path') {
                    const collectionData = processPathData(fieldValue.value);
                    csData = splitArrayCollection(collectionData);
                }
                filteredCollection.forEach((field) => {
                    field.FormField.signatureType = fieldValue.signatureType;
                    if (fieldValue.signatureType === 'Path') {
                        field.FormField.value = JSON.stringify(csData);
                    }
                    else {
                        field.FormField.value = fieldValue.value;
                    }
                });
                for (let m = 0; m < FormFieldsData.length; m++) {
                    // eslint-disable-next-line
                    const field = FormFieldsData[parseInt(m.toString(), 10)].FormField;
                    if (field.id.split('_')[0] === fieldValue.id) {
                        if (fieldValue.signatureType === 'Path') {
                            FormFieldsData[parseInt(m.toString(), 10)].FormField.value = JSON.stringify(csData);
                        }
                        else {
                            FormFieldsData[parseInt(m.toString(), 10)].FormField.value = fieldValue.value;
                        }
                        FormFieldsData[parseInt(m.toString(), 10)].FormField.signatureType = fieldValue.signatureType;
                        this.viewerBase.setItemInSessionStorage(FormFieldsData, '_formDesigner');
                        break;
                    }
                }
            }
            const fieldsData = this.viewerBase.getItemFromSessionStorage('_formfields');
            if (!isNullOrUndefined(fieldsData)) {
                const FormFieldsData = JSON.parse(fieldsData);
                for (let m = 0; m < FormFieldsData.length; m++) {
                    let currentData = FormFieldsData[parseInt(m.toString(), 10)];
                    let fieldName;
                    if (fieldValue.type === 'Checkbox' || fieldValue.type === 'RadioButton' || fieldValue.type === 'CheckBox') {
                        fieldName = currentData.FieldName;
                    }
                    else if (fieldValue.type === 'DropDown' || fieldValue.type === 'ListBox' || fieldValue.type === 'DropdownList') {
                        fieldName = currentData.Text;
                    }
                    else {
                        fieldName = currentData.FieldName;
                    }
                    //map the signature field and its data object to find the signature field name.
                    const fieldData = FormFieldsData.filter(function (item) {
                        return item.FieldName ===
                            currentData.FieldName.split('_')[0];
                    });
                    if (!isNullOrUndefined(fieldData) && !isNullOrUndefined(fieldData[0])) {
                        if (fieldData[0].Name === 'SignatureField' || fieldData[0].Name === 'InitialField') {
                            fieldName = currentData.FieldName.split('_')[0];
                            currentData.LineBounds = FormFieldsData.filter(function (item) {
                                return item.FieldName ===
                                    fieldName;
                            })[0].LineBounds;
                        }
                    }
                    if (fieldName === fieldValue.name) {
                        if (fieldValue.type === 'Textbox' || fieldValue.type === 'Password' || fieldValue.type === 'PasswordField') {
                            if (fieldValue.value) {
                                currentData.Text = fieldValue.value;
                                currentData.Value = fieldValue.value;
                            }
                        }
                        else if (fieldValue.type === 'Checkbox' || fieldValue.type === 'RadioButton' || fieldValue.type === 'CheckBox') {
                            if (fieldValue.isSelected || fieldValue.isChecked) {
                                currentData.Selected = true;
                            }
                            else {
                                currentData.Selected = false;
                            }
                            currentData.Value = fieldValue.value;
                        }
                        else if (fieldValue.type === 'DropDown' || fieldValue.type === 'ListBox' || fieldValue.type === 'DropdownList') {
                            currentData.SelectedValue = fieldValue.value;
                            const index = currentData.TextList ? currentData.TextList.indexOf(fieldValue.value) : 0;
                            currentData.selectedIndex = index > -1 ? index : 0;
                            if (fieldValue.type === 'ListBox') {
                                currentData.SelectedListed = [currentData.selectedIndex];
                            }
                            else {
                                currentData.SelectedListed = [];
                            }
                        }
                        else if (fieldValue.type === 'SignatureField' || fieldValue.type === 'InitialField') {
                            if (fieldValue.value) {
                                currentData.Value = fieldValue.value;
                                const input = currentData.Value;
                                if (typeof input === 'string' && input.trim().startsWith('[') && fieldValue.signatureType === 'Path') {
                                    currentData.Value = this.viewerBase.signatureValue(input);
                                }
                                currentData = this.updateSignatureValue(currentData, fieldValue, fieldValue.signatureBounds);
                            }
                        }
                        this.formFieldsModule.updateFormFieldsCollection(currentData);
                        this.viewerBase.updateDocumentEditedProperty(true);
                    }
                }
                PdfViewerBase.sessionStorageManager.removeItem(this.viewerBase.documentId + '_formfields');
                this.viewerBase.setItemInSessionStorage(FormFieldsData, '_formfields');
            }
        }
    }
    getFormFieldByID(id) {
        if (this.formDesignerModule) {
            return this.nameTable[id.split('_')[0]];
        }
        const data = PdfViewerBase.sessionStorageManager.getItem(this.viewerBase.documentId + '_formfields');
        const formFieldsData = JSON.parse(data);
        return formFieldsData[formFieldsData.findIndex((el) => el.uniqueID === id)];
    }
    /**
     * @param {any} number - Gets the number value
     * @returns {number} - number
     */
    ConvertPointToPixel(number) {
        return (number * (96 / 72));
    }
    /**
     * @param {any} currentData - Current form field data.
     * @param {any} fieldValue - Form Field.
     * @param {any} signBounds - It contains a signatureBounds.
     * @returns {any} - Returns the updated the current Data.
     */
    updateSignatureValue(currentData, fieldValue, signBounds) {
        if (!fieldValue.signatureType) {
            fieldValue.signatureType = this.viewerBase.isSignatureImageData(fieldValue.value) ? 'Image' : (this.viewerBase.isSignaturePathData(fieldValue.value) ? 'Path' : 'Type');
        }
        const bound = currentData.LineBounds;
        const left = this.ConvertPointToPixel(bound.X);
        const top = this.ConvertPointToPixel(bound.Y);
        const width = this.ConvertPointToPixel(bound.Width);
        const height = this.ConvertPointToPixel(bound.Height);
        let bounds;
        if (fieldValue.signatureType === 'Type') {
            if (!currentData.FontFamily) {
                currentData.FontFamily = 'Helvetica';
            }
            bounds = this.formFieldsModule.getSignBounds(currentData.pageIndex, currentData.RotationAngle, currentData.pageIndex, this.viewerBase.getZoomFactor(), left, top, width, height);
            if (this.signatureFitMode === 'Default') {
                bounds = this.formFieldsModule.getDefaultBoundsforSign(bounds);
            }
            currentData.Bounds = bounds;
            const fontSize = bounds.height / 1.35;
            const textWidth = this.viewerBase.getTextWidth(currentData.value, fontSize, currentData.FontFamily);
            let widthRatio = 1;
            if (textWidth > bounds.width) {
                widthRatio = bounds.width / textWidth;
            }
            currentData.FontSize = this.viewerBase.getFontSize(Math.floor((fontSize * widthRatio)));
        }
        else if (fieldValue.signatureType === 'Image') {
            bounds = this.formFieldsModule.getSignBounds(currentData.pageIndex, currentData.RotationAngle, currentData.pageIndex, this.viewerBase.getZoomFactor(), left, top, width, height);
            const image = new Image();
            image.src = currentData.Value;
            // eslint-disable-next-line
            const proxy = this;
            image.onload = function () {
                proxy.imageOnLoad(bounds, image, currentData);
            };
        }
        else {
            if ((currentData.Value.indexOf('base64')) !== -1) {
                bounds = this.formFieldsModule.getSignBounds(currentData.pageIndex, currentData.RotationAngle, currentData.pageIndex, this.viewerBase.getZoomFactor(), left, top, width, height);
                if (this.signatureFitMode === 'Default') {
                    bounds = this.formFieldsModule.getDefaultBoundsforSign(bounds);
                }
            }
            else {
                if (!isNullOrUndefined(signBounds)) {
                    bounds = signBounds;
                }
                else if (this.signatureFitMode === 'Default') {
                    const signBounds = this.viewerBase.signatureModule.updateSignatureAspectRatio(currentData.Value, false, null, currentData);
                    bounds = this.formFieldsModule.getSignBounds(currentData.pageIndex, currentData.RotationAngle, currentData.pageIndex, this.viewerBase.getZoomFactor(), left, top, signBounds.width, signBounds.height, true);
                    bounds.x = bounds.x + signBounds.left;
                    bounds.y = bounds.y + signBounds.top;
                }
                else {
                    bounds = this.formFieldsModule.getSignBounds(currentData.pageIndex, currentData.RotationAngle, currentData.pageIndex, this.viewerBase.getZoomFactor(), left, top, width, height);
                }
            }
            currentData.Bounds = bounds;
        }
        return currentData;
    }
    imageOnLoad(bounds, image, currentData) {
        if (this.signatureFitMode === 'Default') {
            const padding = Math.min(bounds.height / this.paddingDifferenceValue, bounds.width / this.paddingDifferenceValue);
            const maxHeight = bounds.height - padding;
            const maxWidth = bounds.width - padding;
            const imageWidth = image.width;
            const imageHeight = image.height;
            const beforeWidth = bounds.width;
            const beforeHeight = bounds.height;
            const ratio = Math.min(maxWidth / imageWidth, maxHeight / imageHeight);
            bounds.width = imageWidth * ratio;
            bounds.height = imageHeight * ratio;
            bounds.x = bounds.x + (beforeWidth - bounds.width) / 2;
            bounds.y = bounds.y + (beforeHeight - bounds.height) / 2;
            const data = this.viewerBase.getItemFromSessionStorage('_formfields');
            if (data) {
                const FormFieldsData = JSON.parse(data);
                for (let i = 0; i < FormFieldsData.length; i++) {
                    if (FormFieldsData[parseInt(i.toString(), 10)].FieldName === currentData.FieldName) {
                        FormFieldsData[parseInt(i.toString(), 10)].Bounds = bounds;
                        this.formFieldsModule.updateFormFieldsCollection(FormFieldsData[parseInt(i.toString(), 10)]);
                    }
                }
                PdfViewerBase.sessionStorageManager.removeItem(this.viewerBase.documentId + '_formfields');
                this.viewerBase.setItemInSessionStorage(FormFieldsData, '_formfields');
            }
        }
    }
    /**
     * Perform undo action for the edited annotations
     *
     * @returns {void}
     */
    undo() {
        if (this.annotationModule) {
            this.annotationModule.undo();
        }
        else {
            this.viewerBase.getModuleWarningMessage('Annotation');
        }
    }
    /**
     * Perform redo action for the edited annotations
     *
     * @returns {void}
     */
    redo() {
        if (this.annotationModule) {
            this.annotationModule.redo();
        }
        else {
            this.viewerBase.getModuleWarningMessage('Annotation');
        }
    }
    /**
     * Unloads the PDF document being displayed in the PDF viewer.
     *
     * @returns {void}
     */
    unload() {
        if (!isNullOrUndefined(this.viewerBase.pdfViewerRunner) && !this.viewerBase.isPasswordProtected
            && this.toolbar && !this.viewerBase.skipOnReload) {
            this.viewerBase.pdfViewerRunner.addTask({ message: 'unloadFPDF' }, TaskPriorityLevel.High);
        }
        this.viewerBase.clear(true);
        this.pageCount = 0;
        if (Browser.isDevice && this.pageCount === 0 && !this.enableDesktopMode) {
            this.viewerBase.mobileSpanContainer.innerHTML = this.pageCount.toString();
            this.viewerBase.mobilecurrentPageContainer.innerHTML = this.pageCount.toString();
        }
        if (!isBlazor()) {
            if (this.toolbarModule) {
                this.viewerBase.pageCount = 0;
                this.toolbarModule.resetToolbar();
            }
        }
        else {
            this.viewerBase.blazorUIAdaptor.resetToolbar();
        }
        if (this.fileByteArray) {
            this.fileByteArray = null;
        }
        if (this.magnificationModule) {
            this.magnificationModule.zoomTo(100);
        }
        if (this.viewerBase.hyperlinkAndLinkAnnotation) {
            this.viewerBase.hyperlinkAndLinkAnnotation = {};
        }
        if (this.viewerBase.pageTextDetails) {
            this.viewerBase.pageTextDetails = {};
        }
        if (this.textSearchModule) {
            this.textSearchModule.showSearchBox(false);
        }
    }
    /**
     * Destroys all managed resources used by this object.
     *
     * @returns {void}
     */
    destroy() {
        super.destroy();
        if (!isNullOrUndefined(this.element)) {
            if (!this.refreshing) {
                this.element.classList.remove('e-pdfviewer');
            }
            this.element.innerHTML = '';
        }
        if (this.viewerBase.navigationPane) {
            this.viewerBase.navigationPane.restrictUpdateZoomValue = false;
        }
        this.viewerBase.destroy();
        if (this.viewerBase.navigationPane) {
            this.viewerBase.navigationPane.restrictUpdateZoomValue = true;
        }
    }
    /**
     * Perform imports annotations action in the PDF Viewer
     *
     * @param {any} importData - Specifies the data for annotation imports
     * @param {AnnotationDataFormat} annotationDataFormat - Specifies the annotation data format
     * @returns {void}
     */
    importAnnotation(importData, annotationDataFormat) {
        if (this.annotationModule) {
            if (typeof (importData) === 'string') {
                const isXfdfFile = ((importData.indexOf('.xfdf') > -1) || (annotationDataFormat.indexOf('Xfdf') > -1)) ? true : false;
                if (annotationDataFormat) {
                    if (importData.indexOf('</xfdf>') > -1) {
                        this.viewerBase.importAnnotations(importData, annotationDataFormat, false);
                    }
                    else {
                        // eslint-disable-next-line
                        if (annotationDataFormat == 'Json') {
                            if (importData.includes('pdfAnnotation')) {
                                this.importAnnotationsAsJson(importData);
                            }
                            else if (importData.split('.')[1] === 'json') {
                                this.viewerBase.isPDFViewerJson = true;
                                this.viewerBase.importAnnotations(importData, annotationDataFormat, isXfdfFile);
                            }
                            else {
                                this.importAnnotationsAsJson(importData);
                            }
                        }
                        else {
                            this.viewerBase.importAnnotations(importData, annotationDataFormat, isXfdfFile);
                        }
                    }
                }
                else {
                    if (importData.split('.')[1] === 'json') {
                        if (importData.includes('pdfAnnotation')) {
                            this.importAnnotationsAsJson(importData);
                        }
                        else {
                            const newImportData = importData.split(',')[1] ? importData.split(',')[1] : importData.split(',')[0];
                            importData = decodeURIComponent(escape(atob(newImportData)));
                            this.importAnnotationsAsJson(importData);
                        }
                    }
                    else {
                        this.viewerBase.importAnnotations(importData, AnnotationDataFormat.Xfdf, isXfdfFile);
                    }
                }
            }
            else {
                const imporedAnnotation = importData.pdfAnnotation;
                if (typeof (importData) === 'object' && !isNullOrUndefined(imporedAnnotation) && !isNullOrUndefined(Object.keys(imporedAnnotation)) && !isNullOrUndefined(Object.keys(imporedAnnotation)[0]) && Object.keys(imporedAnnotation[Object.keys(imporedAnnotation)[0]]).length > 1) {
                    this.viewerBase.importAnnotations(importData);
                }
                else {
                    this.updateShortHexValues(importData);
                    importData = JSON.stringify(importData);
                    this.viewerBase.isPDFViewerJson = false;
                    if (this.viewerBase.clientSideRendering) {
                        const encoder = new TextEncoder();
                        const utf8Bytes = encoder.encode(importData);
                        let binaryString = '';
                        const chunkSize = 65536; // Process in chunks of 64 KB
                        for (let i = 0; i < utf8Bytes.length; i += chunkSize) {
                            binaryString += String.fromCharCode(...Array.from(utf8Bytes.subarray(i, i + chunkSize)));
                        }
                        this.viewerBase.importAnnotations(btoa(binaryString), AnnotationDataFormat.Json);
                    }
                    else {
                        this.viewerBase.importAnnotations(btoa(importData), AnnotationDataFormat.Json);
                    }
                }
            }
        }
        else {
            this.viewerBase.getModuleWarningMessage('Annotation');
        }
    }
    updateShortHexValues(importData) {
        const keys = Object.keys(importData.pdfAnnotation);
        for (let x = 0; x < keys.length; x++) {
            const annots = importData.pdfAnnotation[keys[parseInt(x.toString(), 10)]].shapeAnnotation;
            for (let y = 0; y < annots.length; y++) {
                if ((!isNullOrUndefined(annots[parseInt(y.toString(), 10)].color) &&
                    annots[parseInt(y.toString(), 10)].color.replace('#', '').length === 3)) {
                    const hex = annots[parseInt(y.toString(), 10)].color.replace('#', '');
                    const r = hex[0] + hex[0];
                    const g = hex[1] + hex[1];
                    const b = hex[2] + hex[2];
                    annots[parseInt(y.toString(), 10)].color = `#${r}${g}${b}`;
                }
                if (!isNullOrUndefined(annots[parseInt(y.toString(), 10)]['interior-color']) &&
                    annots[parseInt(y.toString(), 10)]['interior-color'].replace('#', '').length === 3) {
                    const fillHex = annots[parseInt(y.toString(), 10)]['interior-color'].replace('#', '');
                    const r = fillHex[0] + fillHex[0];
                    const g = fillHex[1] + fillHex[1];
                    const b = fillHex[2] + fillHex[2];
                    annots[parseInt(y.toString(), 10)]['interior-color'] = `#${r}${g}${b}`;
                }
            }
        }
    }
    importAnnotationsAsJson(importData) {
        const jsonData = JSON.parse(importData);
        const firstAnnotation = jsonData.pdfAnnotation[Object.keys(jsonData.pdfAnnotation)[0]];
        if ((Object.keys(jsonData.pdfAnnotation).length >= 1) && (firstAnnotation.textMarkupAnnotation ||
            firstAnnotation.measureShapeAnnotation || firstAnnotation.freeTextAnnotation ||
            firstAnnotation.stampAnnotations || firstAnnotation.signatureInkAnnotation ||
            (firstAnnotation.shapeAnnotation && firstAnnotation.shapeAnnotation[0].Bounds))) {
            this.viewerBase.isPDFViewerJson = true;
            this.viewerBase.importAnnotations(jsonData, AnnotationDataFormat.Json);
        }
        else {
            this.viewerBase.isPDFViewerJson = false;
            this.viewerBase.importAnnotations(btoa(importData), AnnotationDataFormat.Json);
        }
    }
    /**
     * Perform export annotations action in the PDF Viewer
     *
     * @param {AnnotationDataFormat} annotationDataFormat -Gets the annotation data format
     * @returns {void}
     */
    exportAnnotation(annotationDataFormat) {
        if (this.annotationModule) {
            if (annotationDataFormat && annotationDataFormat === 'Xfdf') {
                this.viewerBase.exportAnnotations(AnnotationDataFormat.Xfdf);
            }
            else {
                this.viewerBase.exportAnnotations(AnnotationDataFormat.Json);
            }
        }
        else {
            this.viewerBase.getModuleWarningMessage('Annotation');
        }
    }
    /**
     * Perform export annotations action in the PDF Viewer
     *
     *@param {AnnotationDataFormat} annotationDataFormat - Export the annotation based on the format.
     * @returns {Promise<object>} - promise
     */
    exportAnnotationsAsObject(annotationDataFormat = AnnotationDataFormat.Json) {
        const isAnnotations = this.viewerBase.updateExportItem();
        if (this.annotationModule && isAnnotations) {
            return new Promise((resolve, reject) => {
                this.viewerBase.exportAnnotationsAsObject(annotationDataFormat).then((value) => {
                    resolve(value);
                });
            });
        }
        else {
            // eslint-disable-next-line
            return new Promise((resolve) => { resolve(null); });
        }
    }
    /**
     * Export annotations and returns a base64 string for both Json and XFDF formats
     *
     * @param {AnnotationDataFormat} annotationDataFormat - Gets the annotation data format
     * @returns {Promise<string>} - promise
     */
    exportAnnotationsAsBase64String(annotationDataFormat) {
        if (this.annotationModule) {
            return new Promise((resolve, reject) => {
                this.viewerBase.createRequestForExportAnnotations(false, annotationDataFormat, true).then((value) => {
                    resolve(value);
                });
            });
        }
        else {
            return null;
        }
    }
    /**
     * Perform to add annotations in the PDF Viewer
     *
     * @param {any} annotation - Specifies the annotation
     * @returns {void}
     */
    addAnnotation(annotation) {
        if (this.viewerBase) {
            this.viewerBase.addAnnotation(annotation);
        }
    }
    /**
     * Imports the form fields data into the current PDF document.
     *
     * @param {string} data - The path for importing the fields.
     * @param {FormFieldDataFormat} formFieldDataFormat - Gets the form field data format
     * @returns {void}
     */
    importFormFields(data, formFieldDataFormat) {
        if (this.formFieldsModule) {
            if (isNullOrUndefined(formFieldDataFormat)) {
                formFieldDataFormat = FormFieldDataFormat.Json;
            }
            this.viewerBase.importFormFields(data, formFieldDataFormat);
        }
        else {
            this.viewerBase.getModuleWarningMessage('FormFields');
        }
    }
    /**
     * Exports the form field data in the specified data format.
     *
     * @param {string} data - The path for exporting the fields.
     * @param {FormFieldDataFormat} formFieldDataFormat - Form field data format
     * @returns {void}
     */
    exportFormFields(data, formFieldDataFormat) {
        if (this.formFieldsModule) {
            this.viewerBase.exportFormFields(data, formFieldDataFormat);
        }
        else {
            this.viewerBase.getModuleWarningMessage('FormFields');
        }
    }
    /**
     * Returns an object which represents the form field data in the specified data format.
     *
     * @param {FormFieldDataFormat} formFieldDataFormat - Form field data format
     * @returns {Promise<object>} - promise
     */
    exportFormFieldsAsObject(formFieldDataFormat = FormFieldDataFormat.Json) {
        if (this.formFieldsModule) {
            return new Promise((resolve, reject) => {
                this.viewerBase.exportFormFieldsAsObject(formFieldDataFormat).then((value) => {
                    resolve(value);
                });
            });
        }
        else {
            return null;
        }
    }
    /**
     * reset all form fields data
     *
     * @returns {void}
     */
    resetFormFields() {
        this.formFieldsModule.resetFormFields();
    }
    /**
     * Clears data from the form fields.
     * Parameter - Specifies the form field object.
     *
     * @param {any} formField - It describes about the form field
     * @returns {void}
     */
    clearFormFields(formField) {
        this.formFieldsModule.clearFormFields(formField);
    }
    /**
     * To delete the annotation Collections in the PDF Document.
     *
     * @returns {void}
     */
    deleteAnnotations() {
        if (this.annotationModule) {
            this.viewerBase.deleteAnnotations();
        }
        else {
            this.viewerBase.getModuleWarningMessage('Annotation');
        }
    }
    /**
     * To retrieve the form fields in the PDF Document.
     *
     * @returns {void}
     */
    retrieveFormFields() {
        return this.formFieldCollections;
    }
    /**
     * To update the form fields within a PDF document, but only when the FormDesigner module is either not injected or has been disabled.
     *
     * @param {any} formFields - It describes about the form field
     * @returns {void}
     */
    updateFormFields(formFields) {
        this.updateFormFieldsValue(formFields);
        this.formFieldsModule.updateFormFieldValues(formFields);
    }
    /**
     * @returns {void}
     */
    fireResourcesLoaded() {
        this.trigger('resourcesLoaded');
    }
    /**
     * @param {any} JsonData - It gives the json data values
     * @private
     * @returns {void}
     */
    fireAjaxRequestInitiate(JsonData) {
        const eventArgs = { name: 'ajaxRequestInitiate', JsonData: JsonData };
        this.trigger('ajaxRequestInitiate', eventArgs);
    }
    /**
     * @param {any} jsonData - It gives the json data values
     * @private
     * @returns {void}
     */
    firePageRenderInitiate(jsonData) {
        const eventArgs = { name: 'pageRenderInitiate', jsonData: jsonData };
        this.trigger('pageRenderInitiate', eventArgs);
    }
    /**
     * @param {string} value - The button field value
     * @param {string} fieldName - It describes about the button field name
     * @param {string} id - It describes about the id value
     * @private
     * @returns {void}
     */
    fireButtonFieldClickEvent(value, fieldName, id) {
        const eventArgs = { name: 'buttonFieldClicked', buttonFieldValue: value, buttonFieldName: fieldName, id: id };
        this.trigger('buttonFieldClick', eventArgs);
    }
    /**
     * @param {string} name - Form field name
     * @param {FormFieldModel} field - It describes about the form field model
     * @param {boolean} cancel - checks whether the cancel is true or not
     * @param {boolean} isLeftClick - becomes true on signature panel left click.
     * @private
     * @returns {Promise<void>} - returns promise
     */
    fireFormFieldClickEvent(name, field, cancel, isLeftClick) {
        return __awaiter(this, void 0, void 0, function* () {
            let eventArgs = { name: name, field: field, cancel: cancel };
            if (isBlazor()) {
                eventArgs = (yield this.triggerEvent('formFieldClick', eventArgs)) || eventArgs;
                eventArgs.field.type = field.type;
            }
            else {
                this.triggerEvent('formFieldClick', eventArgs);
            }
            if (field.type === 'SignatureField' || field.type === 'InitialField') {
                if (field.type === 'InitialField') {
                    this.viewerBase.isInitialField = true;
                }
                else {
                    this.viewerBase.isInitialField = false;
                }
                let target = document.getElementById(field.id);
                if (target.style.visibility === 'hidden') {
                    target.disabled = true;
                }
                target = target ? target : (document.getElementById(field.id + '_content_html_element') ? document.getElementById(field.id + '_content_html_element').children[0].children[0] : null);
                const formFieldCollectionsValue = this.formFieldCollections.filter(function (item) { return item.id === field.id; });
                if (formFieldCollectionsValue) {
                    const readOnly = formFieldCollectionsValue[0].isReadOnly;
                    if ((!readOnly) && !eventArgs.cancel && target && !target.disabled && target.classList.contains('e-pdfviewer-signatureformfields') && (isLeftClick || isNullOrUndefined(isLeftClick))) {
                        this.viewerBase.signatureModule.showSignatureDialog(true);
                    }
                    else if (readOnly) {
                        target.disabled = true;
                    }
                }
            }
        });
    }
    /**
     * @param { srting} name - Get the name of the event.
     * @param {IFormField} field - Event arguments for the form field add event.
     * @param {number} pageIndex - Get the page number.
     * @private
     * @returns {void}
     */
    fireFormFieldAddEvent(name, field, pageIndex) {
        const eventArgs = { name: name, field: field, pageIndex: pageIndex };
        this.viewerBase.isFormFieldSelect = false;
        this.trigger('formFieldAdd', eventArgs);
        if (!this.annotation) {
            this.viewerBase.updateDocumentEditedProperty(true);
        }
    }
    /**
     * @param {string} name - Get the name of the event.
     * @param {IFormField} field - Event arguments for the form field remove event.
     * @param {number} pageIndex - Get the page number.
     * @private
     * @returns {void}
     */
    fireFormFieldRemoveEvent(name, field, pageIndex) {
        const eventArgs = { name: name, field: field, pageIndex: pageIndex };
        this.trigger('formFieldRemove', eventArgs);
        if (!this.annotation) {
            this.viewerBase.updateDocumentEditedProperty(true);
        }
    }
    /**
     * @param {FormFieldDoubleClickArgs} eventArgs - Returns the event args
     * @private
     * @returns {FormFieldDoubleClickArgs} - FormFieldDoubleClickArgs
     */
    fireFormFieldDoubleClickEvent(eventArgs) {
        this.trigger('formFieldDoubleClick', eventArgs);
        return eventArgs;
    }
    /**
     * @param {string} name - Get the name of the event.
     * @param {IFormField} field - Event arguments for the form field properties change event.
     * @param {number} pageIndex - Get the page number.
     * @param {boolean} isValueChanged - Specifies whether the form field value is changed or not.
     * @param {boolean} isFontFamilyChanged - Specifies whether the font family of the form field is changed or not.
     * @param {boolean} isFontSizeChanged - Specifies whether the font size of the form field is changed or not.
     * @param {boolean} isFontStyleChanged - Specifies whether the font style of the form field is changed or not.
     * @param {boolean} isColorChanged - Specifies whether the font color of the form field is changed or not.
     * @param {boolean} isBackgroundColorChanged - Specifies whether the background color of the form field is changed or not.
     * @param {boolean} isBorderColorChanged - Specifies whether the border color of the form field is changed or not.
     * @param {boolean} isBorderWidthChanged - Specifies whether the border width of the form field is changed or not.
     * @param {boolean} isAlignmentChanged - Specifies whether the text alignment of the form field is changed or not.
     * @param {boolean} isReadOnlyChanged - Specifies the Read Only of Form field is changed or not.
     * @param {boolean} isVisibilityChanged - Specifies whether the form field visibility is changed or not.
     * @param {boolean} isMaxLengthChanged - Specifies whether the max length of the form field is changed or not.
     * @param {boolean} isRequiredChanged - Specifies whether the is required option of the form field is changed or not.
     * @param {boolean} isPrintChanged - Specifies whether the print option of the form field is changed or not.
     * @param {boolean} isToolTipChanged - Specifies whether the tool tip property is changed or not.
     * @param {boolean} isCustomDataChanged - Specifies isCustomDataChanged
     * @param {any} oldValue - Specifies the old value of the form field.
     * @param {any} newValue - Specifies the new value of the form field.
     * @param {any} isNamechanged - Specifies whether the name changed property is changed or not.
     * @private
     * @returns {void}
     */
    fireFormFieldPropertiesChangeEvent(name, field, pageIndex, isValueChanged, isFontFamilyChanged, isFontSizeChanged, isFontStyleChanged, isColorChanged, isBackgroundColorChanged, isBorderColorChanged, isBorderWidthChanged, isAlignmentChanged, isReadOnlyChanged, isVisibilityChanged, isMaxLengthChanged, isRequiredChanged, isPrintChanged, isToolTipChanged, isCustomDataChanged, oldValue, newValue, isNamechanged) {
        const eventArgs = {
            name: name, field: field, pageIndex: pageIndex, isValueChanged: isValueChanged,
            isFontFamilyChanged: isFontFamilyChanged, isFontSizeChanged: isFontSizeChanged,
            isFontStyleChanged: isFontStyleChanged, isColorChanged: isColorChanged,
            isBackgroundColorChanged: isBackgroundColorChanged, isBorderColorChanged: isBorderColorChanged,
            isBorderWidthChanged: isBorderWidthChanged, isAlignmentChanged: isAlignmentChanged,
            isReadOnlyChanged: isReadOnlyChanged, isVisibilityChanged: isVisibilityChanged,
            isMaxLengthChanged: isMaxLengthChanged, isRequiredChanged: isRequiredChanged, isPrintChanged: isPrintChanged,
            isToolTipChanged: isToolTipChanged, oldValue: oldValue, newValue: newValue,
            isNameChanged: !isNullOrUndefined(isNamechanged) ? isNamechanged : false, isCustomDataChanged: isCustomDataChanged
        };
        this.trigger('formFieldPropertiesChange', eventArgs);
        if (!this.annotation) {
            this.viewerBase.updateDocumentEditedProperty(true);
        }
    }
    /**
     * @param {string} name - Get the name of the event.
     * @param {IFormField} field - Event arguments for the form field mouse leave event.
     * @param {number} pageIndex - Get the page number.
     * @private
     * @returns {void}
     */
    fireFormFieldMouseLeaveEvent(name, field, pageIndex) {
        const eventArgs = { name: name, field: field, pageIndex: pageIndex };
        this.trigger('formFieldMouseLeave', eventArgs);
    }
    /**
     * @param {string} name - Get the name of the event.
     * @param {IFormField} field - Event arguments for the form field mouse over event.
     * @param {number} pageIndex - Get the page number.
     * @param {number} pageX - Get the mouse over x position with respect to the page container.
     * @param {number} pageY - Get the mouse over y position with respect to the page container.
     * @param {number} X - Specifies the mouse over x position with respect to the viewer container.
     * @param {number} Y - Specifies the mouse over y position with respect to the viewer container.
     * @private
     * @returns {void}
     */
    fireFormFieldMouseoverEvent(name, field, pageIndex, pageX, pageY, X, Y) {
        const eventArgs = { name: name, field: field, pageIndex: pageIndex, pageX: pageX,
            pageY: pageY, X: X, Y: Y };
        this.trigger('formFieldMouseover', eventArgs);
    }
    /**
     * @param {string} name - Get the name of the event.
     * @param {IFormField} field - Event arguments for the form field move event.
     * @param {number} pageIndex - Get the page number.
     * @param {IFormFieldBound} previousPosition - Get the previous position of the form field in the page.
     * @param {IFormFieldBound} currentPosition - Current position of form field in the page.
     * @private
     * @returns {void}
     */
    fireFormFieldMoveEvent(name, field, pageIndex, previousPosition, currentPosition) {
        const eventArgs = { name: name, field: field, pageIndex: pageIndex,
            previousPosition: previousPosition, currentPosition: currentPosition };
        this.trigger('formFieldMove', eventArgs);
        if (!this.annotation) {
            this.viewerBase.updateDocumentEditedProperty(true);
        }
    }
    /**
     * @param {string} name - Get the name of the event.
     * @param {IFormField} field - Event arguments for the form field move event.
     * @param {number} pageIndex - Get the page number.
     * @param {IFormFieldBound} previousPosition - Get the previous position of the form field in the page.
     * @param {IFormFieldBound} currentPosition - Current position of form field in the page.
     * @private
     * @returns {void}
     */
    fireFormFieldResizeEvent(name, field, pageIndex, previousPosition, currentPosition) {
        const eventArgs = { name: name, field: field, pageIndex: pageIndex,
            previousPosition: previousPosition, currentPosition: currentPosition };
        this.trigger('formFieldResize', eventArgs);
        if (!this.annotation) {
            this.viewerBase.updateDocumentEditedProperty(true);
        }
    }
    /**
     * @param {string} name - Get the name of the event.
     * @param {IFormField} field - Event arguments for the form field select event.
     * @param {number} pageIndex - Get the page number.
     * @param {boolean} isProgrammaticSelection - Specifies whether the the form field is selected programmatically or by UI.
     * @private
     * @returns {void}
     */
    fireFormFieldSelectEvent(name, field, pageIndex, isProgrammaticSelection) {
        const eventArgs = { name: name, field: field, pageIndex: pageIndex,
            isProgrammaticSelection: isProgrammaticSelection };
        this.trigger('formFieldSelect', eventArgs);
    }
    /**
     * @param {string} name - Get the name of the event.
     * @param {IFormField} field - Event arguments for the form field unselect event.
     * @param {number} pageIndex - Get the page number.
     * @private
     * @returns {void}
     */
    fireFormFieldUnselectEvent(name, field, pageIndex) {
        const eventArgs = { name: name, field: field, pageIndex: pageIndex };
        this.trigger('formFieldUnselect', eventArgs);
    }
    /**
     * @param {any} pageData - It contains the page data
     * @private
     * @returns {void}
     */
    fireDocumentLoad(pageData) {
        const eventArgs = { name: 'documentLoad', documentName: this.fileName, pageData: pageData };
        this.trigger('documentLoad', eventArgs);
        if (isBlazor()) {
            this._dotnetInstance.invokeMethodAsync('LoadDocument', null);
            this.viewerBase.blazorUIAdaptor.loadDocument();
        }
    }
    /**
     * @param {string} fileName - Get the file name
     * @private
     * @returns {void}
     */
    fireDocumentUnload(fileName) {
        const eventArgs = { name: 'documentUnload', documentName: fileName };
        this.trigger('documentUnload', eventArgs);
    }
    /**
     * @param {boolean} isPasswordRequired - Checks whether the password required is true or not
     * @param {string} password - Get the password value
     * @private
     * @returns {void}
     */
    fireDocumentLoadFailed(isPasswordRequired, password) {
        const eventArgs = { name: 'documentLoadFailed', documentName: this.fileName,
            isPasswordRequired: isPasswordRequired, password: password };
        this.trigger('documentLoadFailed', eventArgs);
    }
    /**
     * @param {number} errorStatusCode - It Gets the error status code
     * @param {string} errorMessage - It Gets the error message
     * @param {string} action - It describes the action
     * @param {boolean} retryCount - checks whether retry count ie true or not
     * @private
     * @returns {void}
     */
    fireAjaxRequestFailed(errorStatusCode, errorMessage, action, retryCount) {
        const eventArgs = { name: 'ajaxRequestFailed', documentName: this.fileName,
            errorStatusCode: errorStatusCode, errorMessage: errorMessage, action: action };
        if (retryCount) {
            eventArgs.retryCount = true;
        }
        this.trigger('ajaxRequestFailed', eventArgs);
    }
    /**
     * @param {string} action - It describes the action
     * @param {any} data - It describes the data
     * @private
     * @returns {boolean} - boolean
     */
    fireAjaxRequestSuccess(action, data) {
        const eventArgs = { name: 'ajaxRequestSuccess', documentName: this.fileName,
            action: action, data: data, cancel: false };
        this.trigger('ajaxRequestSuccess', eventArgs);
        if (eventArgs.cancel) {
            return true;
        }
        else {
            return false;
        }
    }
    /**
     * @param {any} data - It describes the data
     * @private
     * @returns {any} - any
     */
    firePageRenderComplete(data) {
        const eventArgs = { name: 'pageRenderComplete', documentName: this.fileName, data: data };
        this.trigger('pageRenderComplete', eventArgs);
    }
    /**
     * @param {string} action - It describes the action
     * @private
     * @returns {void}
     */
    fireValidatedFailed(action) {
        if (!isBlazor()) {
            const eventArgs = { formField: this.formFieldCollections,
                documentName: this.fileName, nonFillableFields: this.viewerBase.nonFillableFields };
            this.trigger('validateFormFields', eventArgs);
        }
        else {
            const eventArgs = {};
            eventArgs.documentName = this.fileName;
            eventArgs.formFields = this.formFieldCollections;
            eventArgs.nonFillableFields = this.viewerBase.nonFillableFields;
            this.trigger('validateFormFields', eventArgs);
        }
    }
    /**
     * @param {number} x - It Gets the x value
     * @param {number} y - It Gets the y value
     * @param {number} pageNumber - It Gets the page number value
     * @private
     * @returns {void}
     */
    firePageClick(x, y, pageNumber) {
        const eventArgs = { name: 'pageClick', documentName: this.fileName, x: x, y: y, pageNumber: pageNumber };
        this.trigger('pageClick', eventArgs);
    }
    /**
     * @param {number} previousPageNumber - It Gets the previous page number
     * @private
     * @returns {void}
     */
    firePageChange(previousPageNumber) {
        const eventArgs = { name: 'pageChange', documentName: this.fileName,
            currentPageNumber: this.currentPageNumber, previousPageNumber: previousPageNumber };
        this.trigger('pageChange', eventArgs);
        if (isBlazor()) {
            //this._dotnetInstance.invokeMethodAsync('OnPageChanged', this.currentPageNumber);
            this.viewerBase.blazorUIAdaptor.pageChanged(this.currentPageNumber);
        }
    }
    /**
     * @private
     * @returns {void}
     */
    fireZoomChange() {
        const eventArgs = { name: 'zoomChange', zoomValue: this.magnificationModule.zoomFactor * 100,
            previousZoomValue: this.magnificationModule.previousZoomFactor * 100 };
        this.trigger('zoomChange', eventArgs);
    }
    /**
     * @param {string} hyperlink - Get the hyper link
     * @param  {HTMLAnchorElement} hyperlinkElement - Get the hyper link element
     * @private
     * @returns {Promise<boolean>} - Promise<boolean>
     */
    fireHyperlinkClick(hyperlink, hyperlinkElement) {
        return __awaiter(this, void 0, void 0, function* () {
            let eventArgs = { name: 'hyperlinkClick', hyperlink: hyperlink,
                hyperlinkElement: hyperlinkElement, cancel: false };
            if (isBlazor()) {
                eventArgs = (yield this.triggerEvent('hyperlinkClick', eventArgs)) || eventArgs;
            }
            else {
                this.triggerEvent('hyperlinkClick', eventArgs);
            }
            if (eventArgs.hyperlinkElement.href !== eventArgs.hyperlink) {
                hyperlinkElement.href = eventArgs.hyperlink;
            }
            if (eventArgs.cancel) {
                return false;
            }
            else {
                return true;
            }
        });
    }
    /**
     * @param {HTMLAnchorElement} hyperlinkElement - Gets the hyper link element
     * @private
     * @returns {void}
     */
    fireHyperlinkHover(hyperlinkElement) {
        const eventArgs = { name: 'hyperlinkMouseOver', hyperlinkElement: hyperlinkElement };
        this.trigger('hyperlinkMouseOver', eventArgs);
    }
    /**
     * @param {string} fieldName - Gets the field name
     * @param {string} value - Gets the field value
     * @private
     * @returns {void}
     */
    fireFocusOutFormField(fieldName, value) {
        const eventArgs = { name: 'formFieldFocusOut', fieldName: fieldName, value: value };
        this.trigger('formFieldFocusOut', eventArgs);
    }
    /**
     * @param {number} pageNumber -- It Gets the page number value
     * @param {string} index - It Gets the index value
     * @param {AnnotationType} type - It Gets the annotation type
     * @param {any} bounds - Gets the annotation bounds
     * @param {any} settings - Gets the annotation settings
     * @param {string} textMarkupContent - Gets the text markup content
     * @param {number} tmStartIndex - Gets the text markup start index
     * @param {number} tmEndIndex - Gets the text markup end index
     * @param {ShapeLabelSettingsModel} labelSettings - Gets the label settings
     * @param {any} multiPageCollection - Gets the multi page collection
     * @param {string} customStampName - Gets the name of the custom stamp
     * @private
     * @returns {void}
     */
    fireAnnotationAdd(pageNumber, index, type, bounds, settings, textMarkupContent, tmStartIndex, tmEndIndex, labelSettings, multiPageCollection, customStampName) {
        const eventArgs = { name: 'annotationAdd', pageIndex: pageNumber,
            annotationId: index, annotationType: type, annotationBound: bounds, annotationSettings: settings };
        if (textMarkupContent) {
            if (isBlazor()) {
                eventArgs.annotationSettings.textMarkupContent = textMarkupContent;
                eventArgs.annotationSettings.textMarkupStartIndex = tmStartIndex;
                eventArgs.annotationSettings.textMarkupEndIndex = tmEndIndex;
            }
            else {
                eventArgs.textMarkupContent = textMarkupContent;
                eventArgs.textMarkupStartIndex = tmStartIndex;
                eventArgs.textMarkupEndIndex = tmEndIndex;
            }
        }
        if (labelSettings) {
            eventArgs.labelSettings = labelSettings;
        }
        if (multiPageCollection) {
            eventArgs.multiplePageCollection = multiPageCollection;
        }
        if (type === 'Image') {
            eventArgs.customStampName = customStampName;
        }
        this.viewerBase.isAnnotationSelect = false;
        this.trigger('annotationAdd', eventArgs);
        if (isBlazor()) {
            // this._dotnetInstance.invokeMethodAsync('AnnotationAdd', null);
            this.viewerBase.blazorUIAdaptor.annotationAdd();
        }
    }
    /**
     * @param {number} pageNumber -- It Gets the page number value
     * @param {string} index - It Gets the index value
     * @param {AnnotationType} type - It Gets the annotation type
     * @param {any} bounds - Gets the annotation bounds
     * @param {string} textMarkupContent - Gets the text markup content
     * @param {number} tmStartIndex - Gets the text markup start index
     * @param {number} tmEndIndex - Gets the text markup end index
     * @param {any} multiPageCollection - Gets the multi page collection
     * @private
     * @returns {void}
     */
    fireAnnotationRemove(pageNumber, index, type, bounds, textMarkupContent, tmStartIndex, tmEndIndex, multiPageCollection) {
        const eventArgs = { name: 'annotationRemove', pageIndex: pageNumber, annotationId: index,
            annotationType: type, annotationBounds: bounds };
        if (textMarkupContent) {
            eventArgs.textMarkupContent = textMarkupContent;
            eventArgs.textMarkupStartIndex = tmStartIndex;
            eventArgs.textMarkupEndIndex = tmEndIndex;
        }
        if (multiPageCollection) {
            eventArgs.multiplePageCollection = multiPageCollection;
        }
        this.trigger('annotationRemove', eventArgs);
    }
    /**
     * @param {string} value - Gets the value of the free text annotation
     * @private
     * @returns {void}
     */
    fireBeforeAddFreeTextAnnotation(value) {
        const eventArgs = { name: 'beforeAddFreeText', value: value };
        this.trigger('beforeAddFreeText', eventArgs);
    }
    /**
     * @param {string} id - Gets the id of the comment
     * @param {string} text - Gets the text value
     * @param {any} annotation - Gets the annotation value
     * @private
     * @returns {void}
     */
    fireCommentAdd(id, text, annotation) {
        const eventArgs = { name: 'CommentAdd', id: id, text: text, annotation: annotation };
        this.trigger('commentAdd', eventArgs);
    }
    /**
     * @param {string} id - Gets the id of the comment
     * @param {string} text - Gets the text value
     * @param {any} annotation - Gets the annotation value
     * @private
     * @returns {void}
     */
    fireCommentEdit(id, text, annotation) {
        const eventArgs = { name: 'CommentEdit', id: id, text: text, annotation: annotation };
        this.trigger('commentEdit', eventArgs);
    }
    /**
     * @param {string} id - Gets the id of the comment
     * @param {string} text - Gets the text value
     * @param {any} annotation - Gets the annotation value
     * @private
     * @returns {void}
     */
    fireCommentDelete(id, text, annotation) {
        const eventArgs = { name: 'CommentDelete', id: id, text: text, annotation: annotation };
        this.trigger('commentDelete', eventArgs);
    }
    /**
     * @param {string} id - Gets the id of the comment
     * @param {string} text - Gets the text value
     * @param {any} annotation - Gets the annotation value
     * @private
     * @returns {void}
     */
    fireCommentSelect(id, text, annotation) {
        const eventArgs = { name: 'CommentSelect', id: id, text: text, annotation: annotation };
        this.trigger('commentSelect', eventArgs);
    }
    /**
     * @param {string} id - Gets the id of the comment
     * @param {string} text - Gets the text value
     * @param {any} annotation - Gets the annotation value
     * @param {CommentStatus} statusChange - Get the value of status change
     * @private
     * @returns {void}
     */
    fireCommentStatusChanged(id, text, annotation, statusChange) {
        const eventArgs = { name: 'CommentStatusChanged', id: id, text: text, annotation: annotation, status: statusChange };
        this.trigger('commentStatusChanged', eventArgs);
    }
    /**
     * @param {number} pageNumber -- It Gets the page number value
     * @param {string} index - It Gets the index value
     * @param {AnnotationType} type - It Gets the annotation type
     * @param {boolean} isColorChanged - check whetehr the color changed is true or not
     * @param {boolean} isOpacityChanged - check's whether the opacity changed is true or not
     * @param {boolean} isTextChanged - check's whether the text changed is true or not
     * @param {boolean} isCommentsChanged - check's whether the comments changed is true or not
     * @param {string} textMarkupContent - Gets the text markup content
     * @param {number} tmStartIndex - Gets the text markup start index
     * @param {number} tmEndIndex - Gets the text markup end index
     * @param {any} multiPageCollection - Gets the multi page collection
     * @private
     * @returns {void}
     */
    fireAnnotationPropertiesChange(pageNumber, index, type, isColorChanged, isOpacityChanged, isTextChanged, isCommentsChanged, textMarkupContent, tmStartIndex, tmEndIndex, multiPageCollection) {
        const eventArgs = { name: 'annotationPropertiesChange', pageIndex: pageNumber,
            annotationId: index, annotationType: type, isColorChanged: isColorChanged,
            isOpacityChanged: isOpacityChanged, isTextChanged: isTextChanged, isCommentsChanged: isCommentsChanged };
        if (textMarkupContent) {
            eventArgs.textMarkupContent = textMarkupContent;
            eventArgs.textMarkupStartIndex = tmStartIndex;
            eventArgs.textMarkupEndIndex = tmEndIndex;
        }
        if (multiPageCollection) {
            eventArgs.multiplePageCollection = multiPageCollection;
        }
        this.trigger('annotationPropertiesChange', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @param {string} index - Gets the index value
     * @param {any} type - Gets the type of the signature
     * @param {any} bounds - Gets the annotation bounda
     * @param {number} opacity - Gets the opacity value
     * @param {string} strokeColor - Gets the stroke color value
     * @param {number} thickness - Gets the thickness value
     * @param {string} data - Gets the data of the annotation
     * @private
     * @returns {void}
     */
    fireSignatureAdd(pageNumber, index, type, bounds, opacity, strokeColor, thickness, data) {
        const eventArgs = { pageIndex: pageNumber, id: index, type: type, bounds: bounds, opacity: opacity };
        if (thickness) {
            eventArgs.thickness = thickness;
        }
        if (strokeColor) {
            eventArgs.strokeColor = strokeColor;
        }
        if (data) {
            eventArgs.data = data;
        }
        this.trigger('addSignature', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @param {string} index - Gets the index value of the signature
     * @param {AnnotationType} type - Gets the annotation type
     * @param {any} bounds - Gets the bounds value of the annotation
     * @private
     * @returns {void}
     */
    fireSignatureRemove(pageNumber, index, type, bounds) {
        const eventArgs = { pageIndex: pageNumber, id: index, type: type, bounds: bounds };
        this.trigger('removeSignature', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @param {string} id - Gets the id value of the signature
     * @param {AnnotationType} type - Gets the annotation type
     * @param {number} opacity - Gets the opacity value of the annotation
     * @param {string} strokeColor - Gets the stroke color value
     * @param {number} thickness - Gets the thickness value
     * @param {object} previousPosition - Gets the previous position values
     * @param {object} currentPosition - Gets the current position values
     * @private
     * @returns {void}
     */
    fireSignatureMove(pageNumber, id, type, opacity, strokeColor, thickness, previousPosition, currentPosition) {
        const eventArgs = { pageIndex: pageNumber, id: id, type: type, opacity: opacity,
            strokeColor: strokeColor, thickness: thickness, previousPosition: previousPosition, currentPosition: currentPosition };
        this.trigger('moveSignature', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the current page number value
     * @param {string} index - Gets the index value
     * @param {AnnotationType} type - Gets the annotation type
     * @param {boolean} isStrokeColorChanged - Check's whether the stroke color changes is true or not
     * @param {boolean} isOpacityChanged - Check's whether the opacity change is true or not
     * @param {boolean} isThicknessChanged - Check's whether the thickness change is true or not
     * @param {any} oldProp - Gets the old prop value
     * @param {any} newProp - Gets the new prop value
     * @private
     * @returns {void}
     */
    fireSignaturePropertiesChange(pageNumber, index, type, isStrokeColorChanged, isOpacityChanged, isThicknessChanged, oldProp, newProp) {
        const eventArgs = { pageIndex: pageNumber, id: index, type: type,
            isStrokeColorChanged: isStrokeColorChanged, isOpacityChanged: isOpacityChanged,
            isThicknessChanged: isThicknessChanged, oldValue: oldProp, newValue: newProp };
        this.trigger('signaturePropertiesChange', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @param {string} index - Gets the index value
     * @param {AnnotationType} type - Gets the annotation type
     * @param {number} opacity - Gets the opacity value
     * @param {string} strokeColor - Gets the stroke color value
     * @param {number} thickness - Gets the thickness value
     * @param {any} currentPosition - Gets the current position of the signature
     * @param {any} previousPosition - Gets the previous position of the signature
     * @private
     * @returns {void}
     */
    fireSignatureResize(pageNumber, index, type, opacity, strokeColor, thickness, currentPosition, previousPosition) {
        const eventArgs = { pageIndex: pageNumber, id: index, type: type, opacity: opacity,
            strokeColor: strokeColor, thickness: thickness, currentPosition: currentPosition,
            previousPosition: previousPosition };
        this.trigger('resizeSignature', eventArgs);
    }
    /**
     * @param {string} id - Gets the id value
     * @param {number} pageNumber - Gets the page number value
     * @param {object} signature - Gets the signature object
     * @private
     * @returns {void}
     */
    fireSignatureSelect(id, pageNumber, signature) {
        const eventArgs = { id: id, pageIndex: pageNumber, signature: signature };
        this.trigger('signatureSelect', eventArgs);
    }
    /**
     * @param {string} id - Gets the id value
     * @param {number} pageNumber - Gets the page number value
     * @param {object} signature - Gets the signature object
     * @private
     * @returns {void}
     */
    fireSignatureUnselect(id, pageNumber, signature) {
        const eventArgs = { id: id, pageIndex: pageNumber, signature: signature };
        this.trigger('signatureUnselect', eventArgs);
    }
    /**
     * @param {string} id - Gets the annotation id value
     * @param {number} pageNumber - Gets the page number value
     * @param {any} annotation - Gets the annotation
     * @param {any} annotationCollection - Gets the annotation collection
     * @param {any} multiPageCollection - Gets the multi page collection
     * @param {boolean} isSelected - checks whether the selected is true or not
     * @param {string} annotationAddMode - Gets the annotation add mode value
     * @private
     * @returns {void}
     */
    fireAnnotationSelect(id, pageNumber, annotation, annotationCollection, multiPageCollection, isSelected, annotationAddMode) {
        let eventArgs = { name: 'annotationSelect', annotationId: id, pageIndex: pageNumber,
            annotation: annotation };
        if (annotationCollection) {
            eventArgs = { name: 'annotationSelect', annotationId: id, pageIndex: pageNumber, annotation: annotation,
                annotationCollection: annotationCollection };
        }
        if (multiPageCollection) {
            eventArgs.multiplePageCollection = multiPageCollection;
        }
        if (isSelected) {
            eventArgs.isProgrammaticSelection = isSelected;
        }
        if (annotationAddMode) {
            eventArgs.annotationAddMode = annotationAddMode;
        }
        if (isBlazor()) {
            if (annotation.type === 'FreeText') {
                const fontStyle = { isBold: false, isItalic: false, isStrikeout: false, isUnderline: false };
                if (annotation.fontStyle === 1) {
                    fontStyle.isBold = true;
                }
                else if (annotation.fontStyle === 2) {
                    fontStyle.isItalic = true;
                }
                else if (annotation.fontStyle === 3) {
                    fontStyle.isStrikeout = true;
                }
                else if (annotation.fontStyle === 4) {
                    fontStyle.isUnderline = true;
                }
                annotation.fontStyle = fontStyle;
            }
            //this._dotnetInstance.invokeMethodAsync('AnnotationSelect', annotation.type);
            this.viewerBase.blazorUIAdaptor.annotationSelect(annotation.type);
        }
        this.trigger('annotationSelect', eventArgs);
    }
    /**
     * @param {string} id - Gets the annottion id value
     * @param {number} pageNumber - Gets the page number value
     * @param {any} annotation - Gets the annotation details
     * @private
     * @returns {void}
     */
    fireAnnotationUnSelect(id, pageNumber, annotation) {
        if (isBlazor()) {
            this.viewerBase.blazorUIAdaptor.annotationUnSelect();
        }
        const eventArgs = { name: 'annotationUnSelect', annotationId: id,
            pageIndex: pageNumber, annotation: annotation };
        this.trigger('annotationUnSelect', eventArgs);
    }
    /**
     * @param {string} id - Gets the annottion id value
     * @param {number} pageNumber - Gets the page number value
     * @param {any} annotation - Gets the annotation details
     * @private
     * @returns {void}
     */
    fireAnnotationDoubleClick(id, pageNumber, annotation) {
        const eventArgs = { name: 'annotationDblClick', annotationId: id,
            pageIndex: pageNumber, annotation: annotation };
        this.trigger('annotationDoubleClick', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @private
     * @returns {void}
     */
    fireTextSelectionStart(pageNumber) {
        this.isTextSelectionStarted = true;
        const eventArgs = { pageIndex: pageNumber };
        this.trigger('textSelectionStart', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @param {string} text - Gets the selected text value
     * @param {any[]} bound - Gets the annotation bounds
     * @private
     * @returns {void}
     */
    fireTextSelectionEnd(pageNumber, text, bound) {
        if (this.isTextSelectionStarted) {
            const eventArgs = { pageIndex: pageNumber, textContent: text, textBounds: bound };
            this.trigger('textSelectionEnd', eventArgs);
            this.isTextSelectionStarted = false;
        }
    }
    /**
     * @param {HTMLCanvasElement} canvas - Gets the canvas element
     * @param {number} index - Gets the index value
     * @private
     * @returns {void}
     */
    renderDrawing(canvas, index) {
        if (isNullOrUndefined(index) && (!isNullOrUndefined(this.viewerBase.activeElements.activePageID) &&
            this.viewerBase.activeElements.activePageID >= 0) && !this.viewerBase.isPrint) {
            index = this.viewerBase.activeElements.activePageID;
        }
        if (this.annotation) {
            this.annotation.renderAnnotations(index, null, null, null, canvas);
        }
        else if (this.formDesignerModule) {
            this.formDesignerModule.updateCanvas(index, canvas);
        }
    }
    /**
     * @param {number} pageNumber -- It Gets the page number value
     * @param {string} index - It Gets the index value
     * @param {AnnotationType} type - It Gets the annotation type
     * @param {any} bounds - Gets the annotation bounds
     * @param {any} settings - Gets the settings of the annotation
     * @param {string} textMarkupContent - Gets the text markup content
     * @param {number} tmStartIndex - Gets the text markup start index
     * @param {number} tmEndIndex - Gets the text markup end index
     * @param {ShapeLabelSettingsModel} labelSettings - Gets the label settings
     * @param {any} multiPageCollection - Gets the multi page collection
     * @private
     * @returns {void}
     */
    fireAnnotationResize(pageNumber, index, type, bounds, settings, textMarkupContent, tmStartIndex, tmEndIndex, labelSettings, multiPageCollection) {
        const eventArgs = { name: 'annotationResize', pageIndex: pageNumber,
            annotationId: index, annotationType: type, annotationBound: bounds, annotationSettings: settings };
        if (textMarkupContent) {
            eventArgs.textMarkupContent = textMarkupContent;
            eventArgs.textMarkupStartIndex = tmStartIndex;
            eventArgs.textMarkupEndIndex = tmEndIndex;
        }
        if (labelSettings) {
            eventArgs.labelSettings = labelSettings;
        }
        if (multiPageCollection) {
            eventArgs.multiplePageCollection = multiPageCollection;
        }
        this.trigger('annotationResize', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @param {string} id - Gets the id value of the annotation
     * @param {AnnotationType} type - Gets the annotation type
     * @param {any} annotationSettings - Gets the annotation settings
     * @param {object} previousPosition - Gets the previous position of the annotation
     * @param {object} currentPosition - Gets the current position of the annotation
     * @private
     * @returns {void}
     */
    fireAnnotationMoving(pageNumber, id, type, annotationSettings, previousPosition, currentPosition) {
        const eventArgs = { name: 'annotationMoving', pageIndex: pageNumber,
            annotationId: id, annotationType: type, annotationSettings: annotationSettings,
            previousPosition: previousPosition, currentPosition: currentPosition };
        this.trigger('annotationMoving', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @param {string} id - Gets the id value of the annotation
     * @param {AnnotationType} type - Gets the annotation type
     * @param {any} annotationSettings - Gets the annotation settings
     * @param {object} previousPosition - Gets the previous position of the annotation
     * @param {object} currentPosition - Gets the current position of the annotation
     * @private
     * @returns {void}
     */
    fireAnnotationMove(pageNumber, id, type, annotationSettings, previousPosition, currentPosition) {
        const eventArgs = { name: 'annotationMove', pageIndex: pageNumber,
            annotationId: id, annotationType: type, annotationSettings: annotationSettings,
            previousPosition: previousPosition, currentPosition: currentPosition };
        this.trigger('annotationMove', eventArgs);
    }
    /**
     * @param {string} id -Gets the id value of the annotation
     * @param {number} pageNumber - Gets the page number value
     * @param {AnnotationType} annotationType - Gets the annotation type
     * @param {any} bounds - Gets the bounds values of the annotation
     * @param {any} annotation - Gets the annotation
     * @param {any} currentPosition - Gets the current position of the annotation
     * @param {any} mousePosition - Gets the mouse position of the annotation
     * @private
     * @returns {void}
     */
    fireAnnotationMouseover(id, pageNumber, annotationType, bounds, annotation, currentPosition, mousePosition) {
        const eventArgs = { name: 'annotationMouseover', annotationId: id,
            pageIndex: pageNumber, annotationType: annotationType, annotationBounds: bounds,
            annotation: annotation, pageX: currentPosition.left, pageY: currentPosition.top,
            X: mousePosition.left, Y: mousePosition.top };
        if (isBlazor()) {
            if (annotation.subject === 'Perimeter calculation') {
                eventArgs.annotationType = 'Perimeter';
            }
            else if (annotation.subject === 'Area calculation') {
                eventArgs.annotationType = 'Area';
            }
            else if (annotation.subject === 'Volume calculation') {
                eventArgs.annotationType = 'Volume';
            }
            else if (annotation.subject === 'Arrow') {
                eventArgs.annotationType = 'Arrow';
            }
            else if (annotation.subject === 'Circle') {
                eventArgs.annotationType = 'Circle';
            }
        }
        this.trigger('annotationMouseover', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @private
     * @returns {void}
     */
    fireAnnotationMouseLeave(pageNumber) {
        const eventArgs = { name: 'annotationMouseLeave', pageIndex: pageNumber };
        this.trigger('annotationMouseLeave', eventArgs);
    }
    /**
     * @param {number} pageX - Gets the page X value
     * @param {number} pageY - Gets the page Y value
     * @private
     * @returns {void}
     */
    firePageMouseover(pageX, pageY) {
        const eventArgs = { pageX: pageX, pageY: pageY };
        this.trigger('pageMouseover', eventArgs);
    }
    /**
     * @param {string} fileName - Gets the file name
     * @private
     * @returns {void}
     */
    fireDownloadStart(fileName) {
        const eventArgs = { fileName: fileName, cancel: false };
        this.trigger('downloadStart', eventArgs);
        if (eventArgs.cancel) {
            return false;
        }
        else {
            return true;
        }
    }
    /**
     * @param {string} fileName - Gets the file name value
     * @param {string} downloadData - Gets the download data
     * @private
     * @returns {void}
     */
    fireDownloadEnd(fileName, downloadData) {
        const eventArgs = { fileName: fileName, downloadDocument: downloadData };
        this.trigger('downloadEnd', eventArgs);
    }
    /**
     * @private
     * @returns {Promise<void>} - Promise<void>
     */
    firePrintStart() {
        return __awaiter(this, void 0, void 0, function* () {
            let eventArgs = { fileName: this.downloadFileName, cancel: false };
            if (isBlazor) {
                eventArgs = (yield this.triggerEvent('printStart', eventArgs)) || eventArgs;
            }
            else {
                this.triggerEvent('printStart', eventArgs);
            }
            if (!eventArgs.cancel) {
                this.printModule.print();
            }
        });
    }
    /**
     * @param {string} eventName - Gets the name of the event
     * @param {object} args - Gets the args object value
     * @private
     * @returns {Promise<void | object>} - Returns a promise
     */
    triggerEvent(eventName, args) {
        return __awaiter(this, void 0, void 0, function* () {
            let eventArgs = yield this.trigger(eventName, args);
            if (isBlazor && typeof eventArgs === 'string') {
                eventArgs = JSON.parse(eventArgs);
            }
            return eventArgs;
        });
    }
    /**
     * @param {string} fileName - Gets the name of the file
     * @private
     * @returns {void}
     */
    firePrintEnd(fileName) {
        const eventArgs = { fileName: fileName };
        this.trigger('printEnd', eventArgs);
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @private
     * @returns {void}
     */
    fireThumbnailClick(pageNumber) {
        const eventArgs = { name: 'thumbnailClick', pageNumber: pageNumber };
        this.trigger('thumbnailClick', eventArgs);
    }
    /**
     * Custom toolbar click event.
     *
     * @param {ClickEventArgs} target - Gets the click event args value
     * @private
     * @returns {void}
     */
    fireCustomToolbarClickEvent(target) {
        return __awaiter(this, void 0, void 0, function* () {
            this.trigger('toolbarClick', target);
        });
    }
    /**
     * @param {number} pageNumber - Gets the page number value
     * @param {number} position - Gets the position of the book mark
     * @param {string} text - Gets the text value
     * @param {string} fileName - Gets the name of the file
     * @private
     * @returns {void}
     */
    fireBookmarkClick(pageNumber, position, text, fileName) {
        const eventArgs = { name: 'bookmarkClick', pageNumber: pageNumber,
            position: position, text: text, fileName: fileName };
        this.trigger('bookmarkClick', eventArgs);
    }
    /**
     * @param {any} importData - Gets the imported data
     * @private
     * @returns {void}
     */
    fireImportStart(importData) {
        const eventArgs = { name: 'importAnnotationsStart', importData: importData, formFieldData: null };
        this.trigger('importStart', eventArgs);
    }
    /**
     * @param {any} exportData - Gets the exported data
     * @private
     * @returns {boolean} - Returns boolean value
     */
    fireExportStart(exportData) {
        const eventArgs = { name: 'exportAnnotationsStart', exportData: exportData, formFieldData: null, cancel: false };
        this.trigger('exportStart', eventArgs);
        if (eventArgs.cancel) {
            return false;
        }
        else {
            return true;
        }
    }
    /**
     * @param {any} importData - Gets the imported data
     * @private
     * @returns {void}
     */
    fireImportSuccess(importData) {
        const eventArgs = { name: 'importAnnotationsSuccess', importData: importData, formFieldData: null };
        this.trigger('importSuccess', eventArgs);
    }
    /**
     * @param {any} exportData - Gets the exported data
     * @param {string} fileName - Gets the name of the file
     * @private
     * @returns {void}
     */
    fireExportSuccess(exportData, fileName) {
        const eventArgs = { name: 'exportAnnotationsSuccess', exportData: exportData, fileName: fileName, formFieldData: null };
        this.trigger('exportSuccess', eventArgs);
    }
    /**
     * @param {any} data - Gets the imported data
     * @param {string} errorDetails - Gets the error details
     * @private
     * @returns {void}
     */
    fireImportFailed(data, errorDetails) {
        const eventArgs = { name: 'importAnnotationsFailed', importData: data, errorDetails: errorDetails, formFieldData: null };
        this.trigger('importFailed', eventArgs);
    }
    /**
     * @param {any} data - Gets the exported data
     * @param {string} errorDetails - Gets the error details
     * @private
     * @returns {void}
     */
    fireExportFailed(data, errorDetails) {
        const eventArgs = { name: 'exportAnnotationsFailed', exportData: data, errorDetails: errorDetails, formFieldData: null };
        this.trigger('exportFailed', eventArgs);
    }
    /**
     * @param {any} data - Gets the imported data
     * @private
     * @returns {void}
     */
    fireFormImportStarted(data) {
        const eventArgs = { name: 'importFormFieldsStart', importData: null, formFieldData: data };
        this.trigger('importStart', eventArgs);
    }
    /**
     * @param {any} data - Gets the exported data
     * @private
     * @returns {boolean} - boolean
     */
    fireFormExportStarted(data) {
        const eventArgs = { name: 'exportFormFieldsStart', exportData: null, formFieldData: data, cancel: false };
        this.trigger('exportStart', eventArgs);
        if (eventArgs.cancel) {
            return false;
        }
        else {
            return true;
        }
    }
    /**
     * @param {any} data - Gets the imported data
     * @private
     * @returns {void}
     */
    fireFormImportSuccess(data) {
        const eventArgs = { name: 'importFormFieldsSuccess', importData: data, formFieldData: data };
        this.trigger('importSuccess', eventArgs);
    }
    /**
     * @param {any} data - Gets the exported data
     * @param {string} fileName - Gets the name of the file
     * @private
     * @returns {void}
     */
    fireFormExportSuccess(data, fileName) {
        const eventArgs = { name: 'exportFormFieldsSuccess', exportData: data, fileName: fileName, formFieldData: data };
        this.trigger('exportSuccess', eventArgs);
    }
    /**
     * @param {any} data - Gets the imported data
     * @param {string} errorDetails - Gets the error details
     * @private
     * @returns {void}
     */
    fireFormImportFailed(data, errorDetails) {
        const eventArgs = { name: 'importFormFieldsfailed', importData: data, errorDetails: errorDetails, formFieldData: data };
        this.trigger('importFailed', eventArgs);
    }
    /**
     * @param {any} data - Gets the exported data
     * @param {string} errorDetails - Gets the error details
     * @private
     * @returns {void}
     */
    fireFormExportFailed(data, errorDetails) {
        const eventArgs = { name: 'exportFormFieldsFailed', exportData: data, errorDetails: errorDetails, formFieldData: data };
        this.trigger('exportFailed', eventArgs);
    }
    /**
     * @param {DocumentTextCollectionSettingsModel} documentCollection - Gets the document collection values
     * @private
     * @returns {void}
     */
    fireTextExtractionCompleted(documentCollection) {
        let emptyObj = [];
        if (this.extractTextOption === ExtractTextOption.TextAndBounds) {
            emptyObj = documentCollection;
        }
        else if (this.extractTextOption === ExtractTextOption.None) {
            emptyObj = [];
        }
        else {
            for (let i = 0; i < documentCollection.length; i++) {
                const document = documentCollection[parseInt(i.toString(), 10)][parseInt(i.toString(), 10)];
                if (!emptyObj[parseInt(i.toString(), 10)]) {
                    emptyObj[parseInt(i.toString(), 10)] = {};
                }
                switch (this.extractTextOption) {
                    case ExtractTextOption.TextOnly:
                        emptyObj[parseInt(i.toString(), 10)][parseInt(i.toString(), 10)] =
                            { PageSize: document.PageSize, PageText: document.PageText };
                        break;
                    case ExtractTextOption.BoundsOnly:
                        emptyObj[parseInt(i.toString(), 10)][parseInt(i.toString(), 10)] =
                            { PageSize: document.PageSize, TextData: document.TextData };
                        break;
                }
            }
        }
        const eventArgs = { documentTextCollection: emptyObj };
        this.trigger('extractTextCompleted', eventArgs);
    }
    /**
     * @param {string} searchText - Gets the search text values
     * @param {boolean} isMatchcase - Gets whether the match case is true or not
     * @private
     * @returns {void}
     */
    fireTextSearchStart(searchText, isMatchcase) {
        const eventArgs = { name: 'textSearchStart', searchText: searchText, matchCase: isMatchcase };
        this.trigger('textSearchStart', eventArgs);
    }
    /**
     * @param {string} searchText - Gets the search text values
     * @param {boolean} isMatchcase - Gets whether the match case is true or not
     * @private
     * @returns {void}
     */
    fireTextSearchComplete(searchText, isMatchcase) {
        const eventArgs = { name: 'textSearchComplete', searchText: searchText, matchCase: isMatchcase };
        this.trigger('textSearchComplete', eventArgs);
    }
    /**
     * @param {string} searchText - Gets the search text values
     * @param {boolean} isMatchcase - Gets whether the match case is true or not
     * @param {RectangleBoundsModel} bounds - Gets the bounds values
     * @param {number} pageNumber - Gets the page number value
     * @private
     * @returns {void}
     */
    fireTextSearchHighlight(searchText, isMatchcase, bounds, pageNumber) {
        const eventArgs = { name: 'textSearchHighlight', searchText: searchText, matchCase: isMatchcase, bounds: bounds, pageNumber: pageNumber };
        this.trigger('textSearchHighlight', eventArgs);
    }
    /**
     * @param  {string} id - Gets the id value
     * @private
     * @returns {void}
     */
    firecustomContextMenuSelect(id) {
        const eventArgs = { id: id };
        this.trigger('customContextMenuSelect', eventArgs);
    }
    /**
     * @param  {string[]} ids - Gets the id value
     * @private
     * @returns {void}
     */
    firecustomContextMenuBeforeOpen(ids) {
        const eventArgs = { ids: ids };
        this.trigger('customContextMenuBeforeOpen', eventArgs);
    }
    /**
     * @param {KeyboardCommandModel} gesture - Gets the keyboard command value
     * @private
     * @returns {void}
     */
    fireKeyboardCustomCommands(gesture) {
        const eventArgs = { keyboardCommand: gesture };
        this.trigger('keyboardCustomCommands', eventArgs);
    }
    /**
     * @param {string} fileName - Gets the name of the file
     * @param {string} downloadData - Gets the downloaded data values
     * @private
     * @returns {boolean} - boolean
     */
    firePageOrganizerSaveAsEventArgs(fileName, downloadData) {
        const eventArgs = { fileName: fileName, downloadDocument: downloadData, cancel: false };
        this.trigger('pageOrganizerSaveAs', eventArgs);
        if (eventArgs.cancel) {
            return false;
        }
        else {
            return true;
        }
    }
    /**
     * @param {number} previousZoom Gets the previous image zoom value
     * @param {number} currentZoom Gets the current image zoom value
     * @private
     * @returns {void}
     */
    firePageOrganizerZoomChanged(previousZoom, currentZoom) {
        const eventArgs = { previousZoom: previousZoom, currentZoom: currentZoom };
        this.trigger('pageOrganizerZoomChanged', eventArgs);
    }
    /**
     * @param {ClientRect} bounds - Gets the bounds values
     * @param {string} commonStyle - Gets the common style value
     * @param {HTMLElement} cavas - Gets the canvas value
     * @param {number} index - Gets the index values
     * @private
     * @returns {void}
     */
    renderAdornerLayer(bounds, commonStyle, cavas, index) {
        renderAdornerLayer(bounds, commonStyle, cavas, index, this);
    }
    /**
     * @param {number} index - Gets the index value
     * @param {AnnotationSelectorSettingsModel} currentSelector - Gets the current selector
     * @private
     * @returns {void}
     */
    renderSelector(index, currentSelector) {
        this.drawing.renderSelector(index, currentSelector);
    }
    /**
     * @param {string[]} objArray - Gets the object array values
     * @param {AnnotationSelectorSettingsModel} currentSelector - Gets the current selector
     * @param {boolean} multipleSelection - Checks whether the multiple selection is true or not
     * @param {boolean} preventUpdate - Checks whether the prevent update is true or not
     * @private
     * @returns {void}
     */
    select(objArray, currentSelector, multipleSelection, preventUpdate) {
        const allowServerDataBind = this.allowServerDataBinding;
        this.enableServerDataBinding(false);
        if (this.annotationModule) {
            const module = this.annotationModule.textMarkupAnnotationModule;
            const annotationSelect = module && module.selectTextMarkupCurrentPage;
            const annotation = this.selectedItems.annotations[0];
            if (annotationSelect) {
                const currentAnnot = this.annotationModule.textMarkupAnnotationModule.currentTextMarkupAnnotation;
                this.annotationModule.textMarkupAnnotationModule.clearCurrentAnnotationSelection(annotationSelect, true);
                this.fireAnnotationUnSelect(currentAnnot.annotName, currentAnnot.pageNumber, currentAnnot);
            }
            if (!multipleSelection) {
                if (this.viewerBase.activeElements && this.viewerBase.activeElements.activePageID >= 0) {
                    if (!this.viewerBase.isNewStamp && annotation && annotation.shapeAnnotationType !== 'HandWrittenSignature' && annotation.shapeAnnotationType !== 'SignatureText' && annotation.shapeAnnotationType !== 'SignatureImage') {
                        this.fireAnnotationUnSelect(annotation.annotName, annotation.pageIndex, annotation);
                    }
                }
            }
        }
        if (this.viewerBase.signatureModule) {
            const annotation = this.selectedItems.annotations[0];
            if (!multipleSelection) {
                const selectorModel = this.selectedItems;
                if (selectorModel.annotations.length) {
                    for (let j = 0; j < selectorModel.annotations.length; j++) {
                        const node = selectorModel.annotations[parseInt(j.toString(), 10)];
                        if (this.viewerBase.activeElements && this.viewerBase.activeElements.activePageID >= 0) {
                            if (!this.viewerBase.isNewSignatureAdded && (annotation.shapeAnnotationType === 'HandWrittenSignature' || annotation.shapeAnnotationType === 'SignatureText' || annotation.shapeAnnotationType === 'SignatureImage')) {
                                this.annotationModule.unselectSignature(node.signatureName, node.pageIndex, node);
                            }
                        }
                    }
                }
            }
        }
        if (this.formDesignerModule) {
            const formField = this.selectedItems.formFields[0];
            if (formField) {
                if (this.formDesignerModule && formField && formField.formFieldAnnotationType) {
                    const field = {
                        name: formField.name, id: formField.id, value: formField.value,
                        fontFamily: formField.fontFamily, fontSize: formField.fontSize, fontStyle: formField.fontStyle,
                        color: formField.color,
                        backgroundColor: formField.backgroundColor,
                        borderColor: formField.borderColor,
                        thickness: formField.thickness,
                        alignment: formField.alignment, isReadonly: formField.isReadonly,
                        visibility: formField.visibility,
                        maxLength: formField.maxLength, isRequired: formField.isRequired,
                        isPrint: formField.isPrint, rotation: formField.rotateAngle, tooltip: formField.tooltip,
                        options: formField.options,
                        isChecked: formField.isChecked, isSelected: formField.isSelected
                    };
                    this.fireFormFieldUnselectEvent('formFieldUnselect', field, formField.pageIndex);
                }
            }
        }
        // eslint-disable-next-line
        const proxy = this;
        this.viewerBase.renderedPagesList.forEach(function (item) {
            proxy.clearSelection(item);
        });
        this.drawing.select(objArray, currentSelector, multipleSelection, preventUpdate);
        this.enableServerDataBinding(allowServerDataBind, true);
    }
    /**
     * @param {number} pageId - Gets the page id value
     * @private
     * @returns {ZOrderPageTable} - return
     */
    getPageTable(pageId) {
        return this.drawing.getPageTable(pageId);
    }
    /**
     * @param {number} diffX - Gets the diffX value
     * @param {number} diffY - Gets the diffY value
     * @param {number} pageIndex - Gets the page index value
     * @param {AnnotationSelectorSettingsModel} currentSelector - Gets the current selector
     * @param {PdfAnnotationBaseModel} helper - Gets the helper value
     * @private
     * @returns {boolean} - returns boolean
     */
    dragSelectedObjects(diffX, diffY, pageIndex, currentSelector, helper) {
        return this.drawing.dragSelectedObjects(diffX, diffY, pageIndex, currentSelector, helper);
    }
    /**
     * @param {number} sx - Gets the sx value
     * @param {number} sy - Gets the sy value
     * @param {PointModel} pivot - Gets the pivot value
     * @private
     * @returns {boolean} - return boolean
     */
    scaleSelectedItems(sx, sy, pivot) {
        return this.drawing.scaleSelectedItems(sx, sy, pivot);
    }
    /**
     * @param {string} endPoint - Gets the end point value
     * @param {IElement} obj - Gets the object value
     * @param {PointModel} point - Gets the point value
     * @param {PointModel} segment - Gets the segment
     * @param {IElement} target - Gets the target value
     * @param {string} targetPortId - Gets the target port id value
     * @param {AnnotationSelectorSettingsModel} currentSelector - Gets the current selector
     * @private
     * @returns {boolean} -returns boolean value
     */
    dragConnectorEnds(endPoint, obj, point, segment, target, targetPortId, currentSelector) {
        return this.drawing.dragConnectorEnds(endPoint, obj, point, segment, target, null, currentSelector);
    }
    /**
     * @param {number} pageId - Gets the page id value
     * @private
     * @returns {void}
     */
    clearSelection(pageId) {
        const allowServerDataBind = this.allowServerDataBinding;
        this.enableServerDataBinding(false);
        const selectormodel = this.selectedItems;
        const node = selectormodel.annotations.length > 0 ?
            this.selectedItems.annotations[0] : this.selectedItems.formFields[0];
        if (selectormodel.annotations.length > 0) {
            selectormodel.offsetX = 0;
            selectormodel.offsetY = 0;
            selectormodel.width = 0;
            selectormodel.height = 0;
            selectormodel.rotateAngle = 0;
            selectormodel.annotations = [];
            selectormodel.wrapper = null;
        }
        else if (selectormodel.formFields.length > 0) {
            selectormodel.offsetX = 0;
            selectormodel.offsetY = 0;
            selectormodel.width = 0;
            selectormodel.height = 0;
            selectormodel.rotateAngle = 0;
            selectormodel.formFields = [];
            selectormodel.wrapper = null;
        }
        this.drawing.clearSelectorLayer(pageId);
        this.viewerBase.isAnnotationSelect = false;
        this.viewerBase.isFormFieldSelect = false;
        if (this.annotationModule) {
            const module = this.annotationModule.textMarkupAnnotationModule;
            if (module) {
                const annotationSelect = module.selectTextMarkupCurrentPage;
                this.annotationModule.textMarkupAnnotationModule.clearCurrentSelectedAnnotation();
                this.annotationModule.textMarkupAnnotationModule.clearCurrentAnnotationSelection(annotationSelect);
            }
        }
        this.enableServerDataBinding(allowServerDataBind, true);
    }
    /**
     * Get page number from the user coordinates x and y.
     *
     * @param {Point} clientPoint - The user will provide a x, y coordinates.
     * @returns {number} - number
     */
    getPageNumberFromClientPoint(clientPoint) {
        const pageNumber = this.viewerBase.getPageNumberFromClientPoint(clientPoint);
        return pageNumber;
    }
    /**
     * Convert user coordinates to the PDF page coordinates.
     *
     * @param {Point} clientPoint - The user should provide a x, y coordinates.
     * @param {number} pageNumber - We need to pass pageNumber.
     * @returns {Point} - point
     */
    convertClientPointToPagePoint(clientPoint, pageNumber) {
        const pagePoint = this.viewerBase.convertClientPointToPagePoint(clientPoint, pageNumber);
        return pagePoint;
    }
    /**
     * Convert page coordinates to the user coordinates.
     *
     * @param {Point} pagePoint - The user should provide a page x, y coordinates.
     * @param {number} pageNumber - We need to pass pageNumber.
     * @returns {Point} - point
     */
    convertPagePointToClientPoint(pagePoint, pageNumber) {
        const clientPoint = this.viewerBase.convertPagePointToClientPoint(pagePoint, pageNumber);
        return clientPoint;
    }
    /**
     * Convert page coordinates to the scrolling coordinates.
     *
     * @param {Point} pagePoint - The user should provide a page x, y coordinates.
     * @param {number} pageNumber - We need to pass pageNumber.
     * @returns {Point} - point
     */
    convertPagePointToScrollingPoint(pagePoint, pageNumber) {
        const scrollingPoint = this.viewerBase.convertPagePointToScrollingPoint(pagePoint, pageNumber);
        return scrollingPoint;
    }
    /**
     * Brings the given rectangular region to view and zooms in the document to fit the region in client area (view port).
     *
     * @param {Rect} rectangle - Specifies the region in client coordinates that is to be brought to view.
     * @returns {void}
     */
    zoomToRect(rectangle) {
        this.magnificationModule.zoomToRect(rectangle);
    }
    /**
     * @param {PdfAnnotationBase} obj - It describes about the object
     * @private
     * @returns {PdfAnnotationBaseModel} - Pdf annotation base model
     */
    add(obj) {
        return this.drawing.add(obj);
    }
    /**
     * @param {PdfAnnotationBaseModel} obj - It describes about the object
     * @private
     * @returns {void}
     */
    remove(obj) {
        return this.drawing.remove(obj);
    }
    /**
     * @private
     * @returns {Object} - returns object
     */
    copy() {
        if (this.annotation) {
            this.annotation.isShapeCopied = true;
        }
        else if (this.formDesigner && this.designerMode) {
            this.formDesigner.isShapeCopied = true;
        }
        return this.drawing.copy();
    }
    /**
     * @param {number} angle - It describes about the angle value
     * @param {AnnotationSelectorSettingsModel} currentSelector  - It describes about the current selector
     * @private
     * @returns {boolean} - returns boolean value
     */
    rotate(angle, currentSelector) {
        return this.drawing.rotate(this.selectedItems, angle, null, currentSelector);
    }
    /**
     * @param {PdfAnnotationBaseModel[]} obj - It describes about the object
     * @private
     * @returns {void}
     */
    paste(obj) {
        let index;
        if (this.viewerBase.activeElements.activePageID) {
            index = this.viewerBase.activeElements.activePageID;
        }
        return this.drawing.paste(obj, index || 0);
    }
    /**
     * @private
     * @returns {void}
     */
    refresh() {
        for (let i = 0; i < this.annotations.length; i++) {
            if (this.zIndexTable.length !== undefined) {
                const notFound = true;
                for (let i = 0; i < this.zIndexTable.length; i++) {
                    const objects = this.zIndexTable[parseInt(i.toString(), 10)].objects;
                    for (let j = 0; j < objects.length; j++) {
                        objects.splice(j, 1);
                    }
                    delete this.zIndexTable[parseInt(i.toString(), 10)];
                }
                if (this.annotations[parseInt(i.toString(), 10)]) {
                    delete this.annotations[parseInt(i.toString(), 10)];
                }
                if (this.selectedItems.annotations && this.selectedItems.annotations[parseInt(i.toString(), 10)]) {
                    delete this.selectedItems.annotations[parseInt(i.toString(), 10)];
                }
                this.zIndexTable = [];
                this.renderDrawing();
            }
            if (this.annotations && this.annotations.length !== 0) {
                this.annotations.length = 0;
                this.selectedItems.annotations.length = 0;
            }
        }
    }
    /**
     * @private
     * @returns {void}
     */
    cut() {
        let index;
        if (this.viewerBase.activeElements.activePageID) {
            index = this.viewerBase.activeElements.activePageID;
        }
        if (this.annotation) {
            this.annotation.isShapeCopied = true;
        }
        else if (this.formDesigner && this.designerMode) {
            this.formDesigner.isShapeCopied = true;
        }
        return this.drawing.cut(index || 0);
    }
    /**
     * @param {PdfAnnotationBaseModel} actualObject - It describes about the actual object value
     * @param {PdfAnnotationBaseModel} node - It describes about the node value
     * @param {boolean} isNeedToRender - It describes about the isNeedToRender value
     * @private
     * @returns {void}
     */
    nodePropertyChange(actualObject, node, isNeedToRender) {
        this.drawing.nodePropertyChange(actualObject, node, isNeedToRender);
    }
    /**
     * enableServerDataBinding method
     *
     * @returns { void }  enableServerDataBinding method.
     * @param {boolean} enable - provide the node value.
     * @param {boolean} clearBulkChanges - checks whether the clear bulk changes true or not
     * @private
     */
    enableServerDataBinding(enable, clearBulkChanges = false) {
        if (isBlazor()) {
            this.allowServerDataBinding = enable;
            if (clearBulkChanges) {
                this.bulkChanges = {};
            }
        }
    }
    /**
     * @param {number} tx - It describes about the tx value
     * @param {number} ty - It describes about the ty value
     * @param {number} pageIndex - It describes about the page index value
     * @param {Rect} nodeBounds - It describes about the node bounds value
     * @param {boolean} isStamp - It describes about the isStamp value
     * @param {boolean} isSkip - It describes about the isSkip value
     * @private
     * @returns {boolean} - boolean
     */
    checkBoundaryConstraints(tx, ty, pageIndex, nodeBounds, isStamp, isSkip) {
        return this.drawing.checkBoundaryConstraints(tx, ty, pageIndex, nodeBounds, isStamp, isSkip);
    }
    /**
     * Adds a custom menu item to the existing menu, with optional configurations.
     *
     * @param {MenuItemModel[]} menuItems - The custom menu item to be added.
     * @param {boolean} disableDefaultItems - Optional. When set to true, this parameter disables the inclusion of default items in the menu. Defaults to false, meaning default items will be included.
     * @param {boolean} appendToEnd - Optional. When set to true, the custom menu item will be added at the bottom of the existing menu list. If false or not provided, the item will be added at the default position.
     * @returns {void}
     */
    addCustomMenu(menuItems, disableDefaultItems, appendToEnd) {
        if (!isNullOrUndefined(menuItems)) {
            this.customContextMenuItems.push(...menuItems);
            this.showCustomContextMenuBottom = appendToEnd;
        }
        this.disableDefaultContextMenu = disableDefaultItems;
    }
};
__decorate([
    Property()
], PdfViewer.prototype, "serviceUrl", void 0);
__decorate([
    Property(0)
], PdfViewer.prototype, "pageCount", void 0);
__decorate([
    Property(1)
], PdfViewer.prototype, "printScaleFactor", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isDocumentEdited", void 0);
__decorate([
    Property(0)
], PdfViewer.prototype, "currentPageNumber", void 0);
__decorate([
    Property()
], PdfViewer.prototype, "documentPath", void 0);
__decorate([
    Property(null)
], PdfViewer.prototype, "exportAnnotationFileName", void 0);
__decorate([
    Property()
], PdfViewer.prototype, "downloadFileName", void 0);
__decorate([
    Property('auto')
], PdfViewer.prototype, "height", void 0);
__decorate([
    Property('auto')
], PdfViewer.prototype, "width", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableToolbar", void 0);
__decorate([
    Property(1)
], PdfViewer.prototype, "retryCount", void 0);
__decorate([
    Property([500])
], PdfViewer.prototype, "retryStatusCodes", void 0);
__decorate([
    Property(0)
], PdfViewer.prototype, "retryTimeout", void 0);
__decorate([
    Property(2)
], PdfViewer.prototype, "initialRenderPages", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "showNotificationDialog", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableNavigationToolbar", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableCommentPanel", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isCommandPanelOpen", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "enableTextMarkupResizer", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "enableMultiLineOverlap", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isValidFreeText", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isAnnotationToolbarOpen", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isAnnotationToolbarVisible", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isFormDesignerToolbarVisible", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "enableMultiPageAnnotation", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableDownload", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enablePrint", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enablePrintRotation", void 0);
__decorate([
    Property([])
], PdfViewer.prototype, "customFonts", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableThumbnail", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enablePageOrganizer", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isPageOrganizerOpen", void 0);
__decorate([
    Property({ canDelete: true, canInsert: true, canRotate: true, canCopy: true, canRearrange: true, canImport: true, showImageZoomingSlider: false, imageZoomMin: 1, imageZoomMax: 5, imageZoom: 1 })
], PdfViewer.prototype, "pageOrganizerSettings", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isThumbnailViewOpen", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isSignatureEditable", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableBookmark", void 0);
__decorate([
    Property('TextAndBounds')
], PdfViewer.prototype, "extractTextOption", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "enableLocalStorage", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "enableBookmarkStyles", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableHyperlink", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableHandwrittenSignature", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableInkAnnotation", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "restrictZoomRequest", void 0);
__decorate([
    Property('CurrentTab')
], PdfViewer.prototype, "hyperlinkOpenState", void 0);
__decorate([
    Property('RightClick')
], PdfViewer.prototype, "contextMenuOption", void 0);
__decorate([
    Property([])
], PdfViewer.prototype, "disableContextMenuItems", void 0);
__decorate([
    Property({ name: '', id: '', type: '', isReadOnly: false, isSelected: false, isChecked: false, value: '', signatureType: [''], fontName: '', fontFamily: 'Helvetica', fontSize: 10, fontStyle: 'None', color: 'black', backgroundColor: 'white', alignment: 'Left', visibility: 'visible', maxLength: 0, isRequired: false, isPrint: false, tooltip: '', pageIndex: -1, options: [], signatureIndicatorSettings: { opacity: 1, backgroundColor: 'orange', width: 19, height: 10, fontSize: 10, text: null, color: 'black', customData: '' } })
], PdfViewer.prototype, "formFieldCollections", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableNavigation", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableAutoComplete", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableMagnification", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "enableShapeLabel", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableImportAnnotationMeasurement", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enablePinchZoom", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableTextSelection", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableTextSearch", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableAnnotation", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableFormFields", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableFormDesigner", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "designerMode", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "enableFormFieldsValidation", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isFormFieldDocument", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "enableDesktopMode", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "hideSaveSignature", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableFreeText", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableTextMarkupAnnotation", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableShapeAnnotation", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableMeasureAnnotation", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableStampAnnotations", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableStickyNotesAnnotation", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableAnnotationToolbar", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableFormDesignerToolbar", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isBookmarkPanelOpen", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isInitialFieldToolbarSelection", void 0);
__decorate([
    Property('TextSelection')
], PdfViewer.prototype, "interactionMode", void 0);
__decorate([
    Property('Default')
], PdfViewer.prototype, "zoomMode", void 0);
__decorate([
    Property('Default')
], PdfViewer.prototype, "signatureFitMode", void 0);
__decorate([
    Property('Default')
], PdfViewer.prototype, "printMode", void 0);
__decorate([
    Property(0)
], PdfViewer.prototype, "zoomValue", void 0);
__decorate([
    Property(10)
], PdfViewer.prototype, "minZoom", void 0);
__decorate([
    Property(400)
], PdfViewer.prototype, "maxZoom", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableZoomOptimization", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isExtractText", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "isMaintainSelection", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "hideEmptyDigitalSignatureFields", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "showDigitalSignatureAppearance", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableAccessibilityTags", void 0);
__decorate([
    Property(true)
], PdfViewer.prototype, "enableHtmlSanitizer", void 0);
__decorate([
    Property('M/d/yyyy h:mm:ss a')
], PdfViewer.prototype, "dateTimeFormat", void 0);
__decorate([
    Property('')
], PdfViewer.prototype, "resourceUrl", void 0);
__decorate([
    Property({ showTooltip: true, toolbarItems: ['OpenOption', 'UndoRedoTool', 'PageNavigationTool', 'MagnificationTool', 'PanTool', 'SelectionTool', 'CommentTool', 'SubmitForm', 'AnnotationEditTool', 'FormDesignerEditTool', 'FreeTextAnnotationOption', 'InkAnnotationOption', 'ShapeAnnotationOption', 'StampAnnotation', 'SignatureOption', 'SearchOption', 'PrintOption', 'DownloadOption'], annotationToolbarItems: ['HighlightTool', 'UnderlineTool', 'StrikethroughTool', 'SquigglyTool', 'ColorEditTool', 'OpacityEditTool', 'AnnotationDeleteTool', 'StampAnnotationTool', 'HandWrittenSignatureTool', 'InkAnnotationTool', 'ShapeTool', 'CalibrateTool', 'StrokeColorEditTool', 'ThicknessEditTool', 'FreeTextAnnotationTool', 'FontFamilyAnnotationTool', 'FontSizeAnnotationTool', 'FontStylesAnnotationTool', 'FontAlignAnnotationTool', 'FontColorAnnotationTool', 'CommentPanelTool'], formDesignerToolbarItems: ['TextboxTool', 'PasswordTool', 'CheckBoxTool', 'RadioButtonTool', 'DropdownTool', 'ListboxTool', 'DrawSignatureTool', 'DeleteTool'] })
], PdfViewer.prototype, "toolbarSettings", void 0);
__decorate([
    Property({ ajaxHeaders: [], withCredentials: false })
], PdfViewer.prototype, "ajaxRequestSettings", void 0);
__decorate([
    Property({ customStampName: '', customStampImageSource: '' })
], PdfViewer.prototype, "customStamp", void 0);
__decorate([
    Property({ load: 'Load', renderPages: 'RenderPdfPages', unload: 'Unload', download: 'Download', renderThumbnail: 'RenderThumbnailImages', print: 'PrintImages', renderComments: 'RenderAnnotationComments', importAnnotations: 'ImportAnnotations', exportAnnotations: 'ExportAnnotations', importFormFields: 'ImportFormFields', exportFormFields: 'ExportFormFields', renderTexts: 'RenderPdfTexts', validatePassword: 'ValidatePassword' })
], PdfViewer.prototype, "serverActionSettings", void 0);
__decorate([
    Property({ name: '', isReadOnly: false, visibility: 'visible', isRequired: false, isPrint: true, tooltip: '', thickness: 1, signatureIndicatorSettings: { opacity: 1, backgroundColor: 'orange', width: 19, height: 10, fontSize: 10, text: null, color: 'black' }, signatureDialogSettings: { displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload, hideSaveSignature: false } })
], PdfViewer.prototype, "signatureFieldSettings", void 0);
__decorate([
    Property({ name: '', isReadOnly: false, visibility: 'visible', isRequired: false, isPrint: true, tooltip: '', thickness: 1, initialIndicatorSettings: { opacity: 1, backgroundColor: 'orange', width: 19, height: 10, fontSize: 10, text: null, color: 'black' }, initialDialogSettings: { displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload, hideSaveSignature: false } })
], PdfViewer.prototype, "initialFieldSettings", void 0);
__decorate([
    Property({ opacity: 1, color: '#FFDF56', author: 'Guest', annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges }, isLock: false, enableMultiPageAnnotation: false, enableTextMarkupResizer: false, allowedInteractions: ['None'], isPrint: true, subject: 'Highlight' })
], PdfViewer.prototype, "highlightSettings", void 0);
__decorate([
    Property({ opacity: 1, color: '#ff0000', author: 'Guest', annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges }, isLock: false, enableMultiPageAnnotation: false, enableTextMarkupResizer: false, allowedInteractions: ['None'], isPrint: true, subject: 'Strikethrough' })
], PdfViewer.prototype, "strikethroughSettings", void 0);
__decorate([
    Property({ opacity: 1, color: '#ff0000', author: 'Guest', annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges }, isLock: false, enableMultiPageAnnotation: false, enableTextMarkupResizer: false, allowedInteractions: ['None'], isPrint: true, subject: 'Squiggly' })
], PdfViewer.prototype, "squigglySettings", void 0);
__decorate([
    Property({ opacity: 1, color: '#00ff00', author: 'Guest', annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges }, isLock: false, enableMultiPageAnnotation: false, enableTextMarkupResizer: false, allowedInteractions: ['None'], isPrint: true, subject: 'Underline' })
], PdfViewer.prototype, "underlineSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', strokeColor: '#ff0000', author: 'Guest', thickness: 1, borderDashArray: 0, lineHeadStartStyle: 'None', lineHeadEndStyle: 'None', annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, allowedInteractions: ['None'], isPrint: true, subject: 'Line' })
], PdfViewer.prototype, "lineSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', strokeColor: '#ff0000', author: 'Guest', thickness: 1, borderDashArray: 0, lineHeadStartStyle: 'Closed', lineHeadEndStyle: 'Closed', annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, allowedInteractions: ['None'], isPrint: true, subject: 'Arrow' })
], PdfViewer.prototype, "arrowSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', strokeColor: '#ff0000', author: 'Guest', thickness: 1, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, allowedInteractions: ['None'], isPrint: true, subject: 'Rectangle' })
], PdfViewer.prototype, "rectangleSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', borderColor: '#ff0000', fontColor: '#000', fontSize: 16, labelHeight: 24.6, labelMaxWidth: 151, labelContent: 'Label' })
], PdfViewer.prototype, "shapeLabelSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', strokeColor: '#ff0000', author: 'Guest', thickness: 1, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, allowedInteractions: ['None'], isPrint: true, subject: 'Circle' })
], PdfViewer.prototype, "circleSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', strokeColor: '#ff0000', author: 'Guest', thickness: 1, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, allowedInteractions: ['None'], isPrint: true, subject: 'Polygon' })
], PdfViewer.prototype, "polygonSettings", void 0);
__decorate([
    Property({ opacity: 1, author: 'Guest', annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, dynamicStamps: [DynamicStampItem.Revised, DynamicStampItem.Reviewed, DynamicStampItem.Received, DynamicStampItem.Confidential, DynamicStampItem.Approved, DynamicStampItem.NotApproved], signStamps: [SignStampItem.Witness, SignStampItem.InitialHere, SignStampItem.SignHere, SignStampItem.Accepted, SignStampItem.Rejected], standardBusinessStamps: [StandardBusinessStampItem.Approved, StandardBusinessStampItem.NotApproved, StandardBusinessStampItem.Draft, StandardBusinessStampItem.Final, StandardBusinessStampItem.Completed, StandardBusinessStampItem.Confidential, StandardBusinessStampItem.ForPublicRelease, StandardBusinessStampItem.NotForPublicRelease, StandardBusinessStampItem.ForComment, StandardBusinessStampItem.Void, StandardBusinessStampItem.PreliminaryResults, StandardBusinessStampItem.InformationOnly], allowedInteractions: ['None'], isPrint: true, subject: '' })
], PdfViewer.prototype, "stampSettings", void 0);
__decorate([
    Property({ opacity: 1, author: 'Guest', width: 0, height: 0, left: 0, top: 0, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, enableCustomStamp: true, allowedInteractions: ['None'], isPrint: true, subject: '' })
], PdfViewer.prototype, "customStampSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', strokeColor: '#ff0000', author: 'Guest', thickness: 1, borderDashArray: 0, lineHeadStartStyle: 'Closed', lineHeadEndStyle: 'Closed', annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, leaderLength: 40, resizeCursorType: CursorType.move, allowedInteractions: ['None'], isPrint: true, subject: 'Distance calculation' })
], PdfViewer.prototype, "distanceSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', strokeColor: '#ff0000', author: 'Guest', thickness: 1, borderDashArray: 0, lineHeadStartStyle: 'Open', lineHeadEndStyle: 'Open', minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, allowedInteractions: ['None'], isPrint: true, subject: 'Perimeter calculation' })
], PdfViewer.prototype, "perimeterSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', strokeColor: '#ff0000', author: 'Guest', thickness: 1, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, allowedInteractions: ['None'], isPrint: true, subject: 'Area calculation' })
], PdfViewer.prototype, "areaSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', strokeColor: '#ff0000', author: 'Guest', thickness: 1, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, allowedInteractions: ['None'], isPrint: true, subject: 'Radius calculation' })
], PdfViewer.prototype, "radiusSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', strokeColor: '#ff0000', author: 'Guest', thickness: 1, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, allowedInteractions: ['None'], isPrint: true, subject: 'Volume calculation' })
], PdfViewer.prototype, "volumeSettings", void 0);
__decorate([
    Property({ author: 'Guest', opacity: 1, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, isLock: false, allowedInteractions: ['None'], isPrint: true, subject: 'Sticky Note' })
], PdfViewer.prototype, "stickyNotesSettings", void 0);
__decorate([
    Property({ opacity: 1, fillColor: '#ffffff00', borderColor: '#ffffff00', author: 'Guest', borderWidth: 1, width: 151, fontSize: 16, height: 24.6, fontColor: '#000', fontFamily: 'Helvetica', defaultText: 'Type Here', textAlignment: 'Left', fontStyle: FontStyle.None, allowTextOnly: false, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, allowedInteractions: ['None'], isPrint: true, isReadonly: false, enableAutoFit: false, subject: 'Text Box' })
], PdfViewer.prototype, "freeTextSettings", void 0);
__decorate([
    Property({ conversionUnit: 'in', displayUnit: 'in', scaleRatio: 1, depth: 96 })
], PdfViewer.prototype, "measurementSettings", void 0);
__decorate([
    Property({ selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null })
], PdfViewer.prototype, "annotationSelectorSettings", void 0);
__decorate([
    Property({ searchHighlightColor: '#fdd835', searchColor: '#8b4c12' })
], PdfViewer.prototype, "textSearchColorSettings", void 0);
__decorate([
    Property({ displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload, hideSaveSignature: false })
], PdfViewer.prototype, "signatureDialogSettings", void 0);
__decorate([
    Property({ displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload, hideSaveSignature: false })
], PdfViewer.prototype, "initialDialogSettings", void 0);
__decorate([
    Property({ signatureItem: ['Signature', 'Initial'], saveSignatureLimit: 1, saveInitialLimit: 1, opacity: 1, strokeColor: '#000000', width: 150, height: 100, thickness: 1, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, allowedInteractions: ['None'], signatureDialogSettings: { displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload, hideSaveSignature: false }, initialDialogSettings: { displayMode: DisplayMode.Draw | DisplayMode.Text | DisplayMode.Upload, hideSaveSignature: false } })
], PdfViewer.prototype, "handWrittenSignatureSettings", void 0);
__decorate([
    Property({ author: 'Guest', opacity: 1, strokeColor: '#ff0000', thickness: 1, annotationSelectorSettings: { selectionBorderColor: '', resizerBorderColor: 'black', resizerFillColor: '#FF4081', resizerSize: 8, selectionBorderThickness: 1, resizerShape: 'Square', selectorLineDashArray: [], resizerLocation: AnnotationResizerLocation.Corners | AnnotationResizerLocation.Edges, resizerCursorType: null }, isLock: false, allowedInteractions: ['None'], isPrint: true, subject: 'Ink' })
], PdfViewer.prototype, "inkAnnotationSettings", void 0);
__decorate([
    Property({ author: 'Guest', minHeight: 0, minWidth: 0, maxWidth: 0, maxHeight: 0, isLock: false, skipPrint: false, skipDownload: false, allowedInteractions: ['None'], subject: '' })
], PdfViewer.prototype, "annotationSettings", void 0);
__decorate([
    Property({ enableTileRendering: true, x: 0, y: 0 })
], PdfViewer.prototype, "tileRenderingSettings", void 0);
__decorate([
    Property({ delayPageRequestTimeOnScroll: 100 })
], PdfViewer.prototype, "scrollSettings", void 0);
__decorate([
    Property({ name: '', value: '', fontFamily: 'Helvetica', fontSize: 10, fontStyle: 'None', color: 'black', borderColor: 'black', backgroundColor: 'white', alignment: 'Left', isReadOnly: false, visibility: 'visible', maxLength: 0, isRequired: false, isPrint: true, tooltip: '', thickness: 1, isMultiline: false, customData: '' })
], PdfViewer.prototype, "textFieldSettings", void 0);
__decorate([
    Property({ name: '', value: '', fontFamily: 'Helvetica', fontSize: 10, fontStyle: 'None', color: 'black', borderColor: 'black', backgroundColor: 'white', alignment: 'Left', isReadOnly: false, visibility: 'visible', maxLength: 0, isRequired: false, isPrint: true, tooltip: '', thickness: 1, customData: '' })
], PdfViewer.prototype, "passwordFieldSettings", void 0);
__decorate([
    Property({ name: '', value: '', isChecked: false, backgroundColor: 'white', isReadOnly: false, visibility: 'visible', isPrint: true, tooltip: '', isRequired: false, thickness: 1, borderColor: 'black', customData: '' })
], PdfViewer.prototype, "checkBoxFieldSettings", void 0);
__decorate([
    Property({ name: '', value: '', isSelected: false, backgroundColor: 'white', isReadOnly: false, visibility: 'visible', isPrint: true, tooltip: '', isRequired: false, thickness: 1, borderColor: 'black', customData: '' })
], PdfViewer.prototype, "radioButtonFieldSettings", void 0);
__decorate([
    Property({ name: '', fontFamily: 'Helvetica', fontSize: 10, fontStyle: 'None', color: 'black', backgroundColor: 'white', alignment: 'Left', isReadOnly: false, visibility: 'visible', isRequired: false, isPrint: true, tooltip: '', options: [], thickness: 1, borderColor: 'black', customData: '' })
], PdfViewer.prototype, "DropdownFieldSettings", void 0);
__decorate([
    Property({ name: '', fontFamily: 'Helvetica', fontSize: 10, fontStyle: 'None', color: 'black', backgroundColor: 'white', alignment: 'Left', isReadOnly: false, visibility: 'visible', isRequired: false, isPrint: false, tooltip: '', options: [], thickness: 1, borderColor: 'black', customData: '' })
], PdfViewer.prototype, "listBoxFieldSettings", void 0);
__decorate([
    Property({ contextMenuAction: 'RightClick', contextMenuItems: [ContextMenuItem.Comment, ContextMenuItem.Copy, ContextMenuItem.Cut, ContextMenuItem.Delete, ContextMenuItem.Highlight, ContextMenuItem.Paste, ContextMenuItem.Properties, ContextMenuItem.ScaleRatio, ContextMenuItem.Strikethrough, ContextMenuItem.Underline, ContextMenuItem.Squiggly] })
], PdfViewer.prototype, "contextMenuSettings", void 0);
__decorate([
    Property([])
], PdfViewer.prototype, "customContextMenuItems", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "disableDefaultContextMenu", void 0);
__decorate([
    Property(false)
], PdfViewer.prototype, "showCustomContextMenuBottom", void 0);
__decorate([
    Complex({}, CommandManager)
], PdfViewer.prototype, "commandManager", void 0);
__decorate([
    Complex({}, Selector)
], PdfViewer.prototype, "selectedItems", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "created", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "resourcesLoaded", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "documentLoad", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "documentUnload", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "documentLoadFailed", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "ajaxRequestFailed", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "ajaxRequestSuccess", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "pageRenderComplete", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "validateFormFields", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "pageClick", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "pageChange", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "hyperlinkClick", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "hyperlinkMouseOver", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "zoomChange", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationAdd", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationRemove", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationPropertiesChange", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationResize", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "addSignature", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "removeSignature", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "moveSignature", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "signaturePropertiesChange", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "resizeSignature", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "signatureSelect", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "signatureUnselect", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationSelect", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationUnSelect", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationDoubleClick", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationMove", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationMoving", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationMouseover", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "annotationMouseLeave", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "pageMouseover", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "importStart", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "exportStart", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "importSuccess", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "exportSuccess", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "importFailed", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "exportFailed", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "extractTextCompleted", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "thumbnailClick", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "bookmarkClick", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "toolbarClick", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "textSelectionStart", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "textSelectionEnd", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "downloadStart", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "buttonFieldClick", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldClick", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "downloadEnd", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "printStart", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "printEnd", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "textSearchStart", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "textSearchComplete", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "textSearchHighlight", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "ajaxRequestInitiate", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "pageRenderInitiate", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "commentAdd", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "commentEdit", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "commentDelete", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "commentSelect", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "commentStatusChanged", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "beforeAddFreeText", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldFocusOut", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldAdd", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldRemove", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldPropertiesChange", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldMouseLeave", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldMouseover", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldMove", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldResize", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldSelect", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldUnselect", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "formFieldDoubleClick", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "customContextMenuSelect", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "customContextMenuBeforeOpen", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "keyboardCustomCommands", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "pageOrganizerSaveAs", void 0);
__decorate([
    Event()
], PdfViewer.prototype, "pageOrganizerZoomChanged", void 0);
__decorate([
    Collection([], PdfAnnotationBase)
], PdfViewer.prototype, "annotations", void 0);
__decorate([
    Collection([], PdfFormFieldBase)
], PdfViewer.prototype, "formFields", void 0);
__decorate([
    Property()
], PdfViewer.prototype, "drawingObject", void 0);
PdfViewer = __decorate([
    NotifyPropertyChanges
], PdfViewer);
export { PdfViewer };
